import "../styles/chart-engine.css";
import "../styles/workspace-layout.css";
import "../styles/option-chain-header.css";
import "../styles/custom-interval.css";
import "../styles/indicator-manager-layout.css";
import "../styles/template-ui.css";
import "../styles/realtime-market-data.css";
import "../styles/account-menu.css";
import { initializeAccountMenu } from "../components/account-menu.js";
import {
  createChartReadyPayload,
  drawingCreationError,
  hasPersistableChartWindows,
  indicatorCreationError,
  loadChartBootstrap,
  permittedIndicatorInstances,
  productMarketDataEndpoints,
  resolveChartCapabilityPolicy
} from "../bootstrap/chart-bootstrap.js";
import {
  applyBranding,
  applyCustomizationPresentation,
  applyEffectiveTheme,
  customizationIndicatorInstances,
  featureIsVisible,
  initialChartSeeds,
  paletteChartSettings,
  resolveEffectiveThemeMode
} from "../customization/effective-customization.js";
import { enhanceChartColorPickers, syncChartColorPicker } from "../components/chart-color-picker.js";
import { enhanceChartDropdowns, syncChartDropdown } from "../components/chart-dropdown.js";
import { createChartIcon } from "../components/chart-icon.js";
import { DrawingContextMenu } from "../components/drawing-context-menu.js";
import { FundamentalEventTooltip } from "../components/fundamental-event-tooltip.js";
import { ClockPreferenceStore, ToolbarClock } from "../components/toolbar-clock.js";
import { AlertWorkspace } from "../components/alert-workspace.js";
import { GoToDateDialog } from "../components/go-to-date-dialog.js";
import { ObjectTreeWindow } from "../components/object-tree-window.js";
import { UpcomingEarningsWindow } from "../components/upcoming-earnings-window.js";
import { UpcomingSplitsWindow } from "../components/upcoming-splits-window.js";
import { hydrateOptionalFeatures, initializeOptionalFeature } from "./optional-feature.js";
import {
  normalizeInstrument,
  SymbolSearch,
  SymbolSearchRepository,
  SymbolSessionStore
} from "../components/symbol-search.js";
import { ChartWorkspace } from "../core/chart-workspace.js";
import {
  addDateOnlyDays,
  compareDateOnly,
  dateOnlyDistance,
  dateRangeForTimestamps,
  formatDateOnly,
  formatDateOnlyLabel,
  initialDateForViewport,
  isDateInRanges,
  isGoToDateShortcut,
  parseDateOnly,
  resolveBarForDate,
  timestampToDateOnly,
  todayDateOnly
} from "../core/date-navigation.js";
import { normalizeChartTimeframe } from "../core/chart-timeframes.js";
import { CustomIntervalMenu } from "../components/custom-interval-menu.js";
import { DataWorkerClient } from "../data/data-worker-client.js";
import { cloneChartData } from "../data/chart-data.js";
import {
  DRAWING_TOOLS,
  createDrawingToolIcon,
  getDrawingTool
} from "../drawings/drawing-tools.js";
import {
  DrawingCommandHistory,
  captureDrawingTargets
} from "../drawings/drawing-command-history.js";
import { setDrawingThemePalette } from "../drawings/drawing-model.js";
import {
  FundamentalEventDataSource,
  fundamentalEventDateRange
} from "../data/fundamental-event-data-source.js";
import { HistoryDataSource } from "../data/history-data-source.js";
import { HistoryRequestCache } from "../data/history-request-cache.js";
import { HistoryWindowController, mergeHistoryWindow } from "../data/history-window-controller.js";
import { initialHistoryPlan, loadInitialHistory } from "../data/initial-history-plan.js";
import { calculateIndicator } from "../indicators/indicator-calculations.js";
import { getIndicatorDefinition } from "../indicators/indicator-definitions.js";
import { IndicatorManager } from "../indicators/indicator-manager.js";
import {
  cloneIndicatorInstances,
  createIndicatorInstance,
  createDefaultIndicatorInstances,
  inputSummary,
  paneLabel,
  serializeIndicatorInstancesForWorkspace
} from "../indicators/indicator-model.js";
import {
  createChartTemplatePayload,
  createIndicatorTemplatePayload,
  materiallyEqual,
  prepareChartTemplate,
  prepareIndicatorTemplate
} from "../templates/template-contracts.js";
import { TemplateMenuController } from "../templates/template-menu.js";
import { TemplateRepository } from "../templates/template-repository.js";
import { createIframeMessenger } from "../messaging/embed-messaging.js";
import { WebGlUnavailableError } from "../renderers/webgl2/webgl-chart-renderer.js";
import {
  FloatingWindowController,
  setSurfaceWindowSnapping,
  surfaceWindowSnappingEnabled
} from "../workspace/floating-window-controller.js";
import { WatchlistManager } from "../workspace/watchlist-manager.js";
import { WorkspaceObjectRegistry } from "../workspace/object-tree-model.js";
import {
  reserveChartWindowId,
  restorableChartWindows
} from "../workspace/chart-window-identity.js";
import { ConnectionGrantClient } from "../realtime/connection-grant-client.js";
import { BrowserStreamLease } from "../realtime/browser-stream-lease.js";
import {
  applyPriceDataToCurrentBar,
  CurrentBarUpdateResult
} from "../realtime/current-bar-overlay.js";
import {
  availabilityFromProviderEntitlement,
  RealtimeConnectionState
} from "../realtime/market-data-contracts.js";
import { createMockAdapterHarness } from "../realtime/mock-provider-adapter.js";
import { createQuoteMediaAdapter } from "../realtime/quotemedia-provider-adapter.js";
import { RealtimeConnectionManager } from "../realtime/realtime-connection-manager.js";

const DEFAULT_MAX_CHART_WINDOWS = 6;
const WINDOW_CASCADE_OFFSET = 28;
const WORKSPACE_PREFERENCES_STORAGE_PREFIX = "flexcharting.workspace.preferences.v1";
const WORKSPACE_SCHEMA_VERSION = 1;
const WORKSPACE_SAVE_DEBOUNCE_MS = 800;

const root = document.querySelector("[data-chart-app]");
if (!(root instanceof HTMLElement)) throw new Error("The chart application root was not found.");
initializeAccountMenu(document);

const workspaceSurface = requiredElement("[data-workspace-surface]");
const initialChartWindow = requiredElement("[data-chart-window]");
const newChartButton = requiredElement("[data-new-chart]");
const newWatchlistButton = requiredElement("[data-new-watchlist]");
const alertManagerButton = requiredElement("[data-open-alert-manager]");
const objectTreeButton = requiredElement("[data-open-object-tree]");
const upcomingEarningsButton = requiredElement("[data-open-upcoming-earnings]");
const upcomingSplitsButton = requiredElement("[data-open-upcoming-splits]");
const watchlistWindowTemplate = requiredElement("[data-watchlist-window-template]");
const windowResetButton = requiredElement("[data-window-reset]");
const symbolSelect = requiredElement("[data-symbol]");
const symbolSearchTrigger = requiredElement("[data-symbol-search-trigger]");
const symbolToolbarCode = requiredElement("[data-symbol-toolbar-code]", symbolSearchTrigger);
const symbolToolbarExchange = requiredElement("[data-symbol-toolbar-exchange]", symbolSearchTrigger);
const symbolSearchElement = requiredElement("[data-symbol-search]");
const timeframeSelect = requiredElement("[data-timeframe]");
const themeToggle = requiredElement("[data-theme-toggle]");
const fullscreenToggle = requiredElement("[data-fullscreen-toggle]");
const resetButton = requiredElement("[data-reset-view]");
const updated = requiredElement("[data-last-updated]");
const renderStatus = requiredElement("[data-render-status]");
const toolbarClockElement = root.querySelector("[data-toolbar-clock]");
const dependencyStatusElements = new Map(
  [...root.querySelectorAll("[data-dependency-service]")]
    .map((element) => [element.dataset.dependencyService, element])
);
const settingsWindow = requiredElement("[data-settings-window]");
const settingsDragHandle = requiredElement("[data-settings-drag-handle]", settingsWindow);
const settingsCloseButton = requiredElement("[data-settings-close]", settingsWindow);
const settingsSymbol = requiredElement("[data-settings-symbol]", settingsWindow);
const settingsTabs = [...settingsWindow.querySelectorAll("[data-settings-tab]")];
const settingsPanels = [...settingsWindow.querySelectorAll("[data-settings-panel]")];
const settingsControls = [...settingsWindow.querySelectorAll("[data-chart-setting]")];
const colorSettingsControls = settingsControls.filter(
  (control) => control instanceof HTMLInputElement && control.type === "color"
);
const chartStyleSections = [...settingsWindow.querySelectorAll("[data-chart-style-section]")];
const backgroundStyleControl = requiredElement(
  '[data-chart-setting="backgroundStyle"]',
  settingsWindow
);
const backgroundGradientControl = requiredElement(
  "[data-background-gradient-control]",
  settingsWindow
);
const watermarkOpacityControl = requiredElement(
  '[data-chart-setting="watermarkOpacity"]',
  settingsWindow
);
const watermarkOpacityValue = requiredElement(
  "[data-watermark-opacity-value]",
  settingsWindow
);
const marketHoursControl = requiredElement(
  '[data-chart-setting="sessionType"]',
  settingsWindow
);
const marketSessionColors = requiredElement("[data-market-session-colors]", settingsWindow);
const marketSessionColorRows = [
  ...marketSessionColors.querySelectorAll("[data-market-session-color]")
];
const watermarkContentControls = [
  ...settingsWindow.querySelectorAll("[data-watermark-content-option]")
];
const settingsCancelButton = requiredElement("[data-settings-cancel]", settingsWindow);
const settingsDoneButton = requiredElement("[data-settings-done]", settingsWindow);
const chartTemplateTrigger = settingsWindow.querySelector(
  '[data-template-trigger="chart"]'
);
const snapWindowsControl = settingsWindow.querySelector(
  "[data-workspace-snap-windows]"
);
const goToDateElement = root.querySelector("[data-go-to-date-layer]");
const viewportActionButtons = [...root.querySelectorAll("[data-viewport-action]")];
const lineTools = requiredElement("[data-line-tools]");
const lineToolCurrent = requiredElement("[data-line-tool-current]", lineTools);
const lineToolsToggle = requiredElement("[data-line-tools-toggle]", lineTools);
const lineToolsMenu = requiredElement("[data-line-tools-menu]", lineTools);
const indicatorManagerElement = requiredElement("[data-indicator-manager]");
const indicatorManagerDragHandle = requiredElement(
  "[data-indicator-manager-drag-handle]",
  indicatorManagerElement
);
const indicatorManagerButton = requiredElement("[data-open-indicator-manager]");
const indicatorTemplateTrigger = indicatorManagerElement.querySelector(
  '[data-template-trigger="indicator"]'
);
const indicatorCount = requiredElement("[data-indicator-count]", indicatorManagerButton);
const chartDropdowns = enhanceChartDropdowns(settingsWindow);
const chartColorPickers = enhanceChartColorPickers(settingsWindow);
let toolbarClock = null;
let productStorageScope = null;
let workspacePreferencesStorageKey = null;
let workspacePreferences = { snapWindows: true };
if (snapWindowsControl) {
  snapWindowsControl.checked = workspacePreferences.snapWindows;
}
setSurfaceWindowSnapping(workspaceSurface, workspacePreferences.snapWindows);

let defaultSymbol = root.dataset.defaultSymbol || "AAPL";
let defaultSymbolId = root.dataset.defaultSymbolId || null;
let defaultTimeframe = normalizeTimeframe(root.dataset.defaultTimeframe || "15m");
let defaultTheme = document.documentElement.dataset.theme === "light" ? "light" : "dark";
const historyCount = clamp(Number.parseInt(root.dataset.historyCount || "3000", 10), 100, 5000);
const MAX_GO_TO_DATE_ATTEMPTS = 3;
const parentOrigin = root.dataset.parentOrigin || window.location.origin;
const dependencyHealthEndpoint =
  root.dataset.dependencyHealthEndpoint || "/api/v1/workspace/dependency-health";
let workspaceEndpoint = root.dataset.workspaceEndpoint || null;
let templateEndpoint = root.dataset.templateEndpoint || null;
let workspacePersistenceEnabled = Boolean(workspaceEndpoint);
const dataSource = new HistoryDataSource(null, { browserHistory: true });
const fundamentalEventSource = new FundamentalEventDataSource(
  root.dataset.fundamentalEventsEndpoint
);
const workerClient = new DataWorkerClient();
const chartInstances = [];
const workspaceObjectRegistry = new WorkspaceObjectRegistry();
const historyRequests = new HistoryRequestCache({ source: dataSource, process: bars => workerClient.process(bars) });
const fundamentalEventCache = new Map();
const symbolCatalogById = new Map();
const symbolCatalogByCode = new Map();
const realtimeProvider = root.dataset.realtimeProvider || "quotemedia";
const mockRealtimeHarness = realtimeProvider === "mock" ? createMockAdapterHarness() : null;
const realtimeConnectionLease = realtimeProvider === "disabled"
  ? null
  : new BrowserStreamLease();
const realtimeGrantClient = realtimeProvider === "disabled"
  ? null
  : new ConnectionGrantClient(
      null,
      document.querySelector('meta[name="csrf-token"]')?.content || null
    );
const realtimeManager = realtimeProvider === "disabled"
  ? null
  : new RealtimeConnectionManager({
      grantClient: realtimeGrantClient,
      connectionLease: realtimeConnectionLease,
      adapterFactory: mockRealtimeHarness?.factory || createQuoteMediaAdapter,
      reconcile: reconcileRealtimeWorkspace
    });
const platformChartSettings = Object.freeze(Object.fromEntries(
  [
    ...settingsControls.map(
      (control) => [control.dataset.chartSetting, readSettingsControl(control)]
    ),
    ...colorSettingsControls.map(
      (control) => [colorOpacitySetting(control), readColorOpacity(control)]
    )
  ]
));
let defaultChartSettings = platformChartSettings;
let defaultChartSettingsByTheme = Object.freeze({
  light: platformChartSettings,
  dark: platformChartSettings
});
const chartSettingDefinitions = Object.freeze(createChartSettingDefinitions());

let effectiveCustomization = null;
let effectiveThemePalette = null;
let configuredDefaultIndicators = createDefaultIndicatorInstances();
let configuredDefaultIndicatorsByTheme = Object.freeze({
  light: configuredDefaultIndicators,
  dark: configuredDefaultIndicators
});
let configuredInitialChartSeeds = [];
let configuredInitialLayout = "single";
let configuredActiveChartIndex = 0;

let activeChart = null;
let initialized = false;
let optionalWorkspaceHydrated = false;
let chartSequence = 0;
let stackSequence = 0;
let settingsTarget = null;
let settingsOpeningSnapshot = null;
let settingsPositioned = false;
let selectedDrawingToolId = DRAWING_TOOLS[0].id;
let workspaceInstrumentId = null;
let workspaceSymbol = defaultSymbol;
let goToDateRunToken = 0;
let dependencyHealthController = null;
let dependencyHealthTimer = null;
let restoredWorkspace = null;
let workspaceRevision = 0;
let workspaceSaveTimer = null;
let workspaceRestoring = false;
let workspaceDirty = false;
let maximumChartWindows = DEFAULT_MAX_CHART_WINDOWS;
let upcomingEarnings = null;
let upcomingSplits = null;
let maximumIndicatorsPerChart = Number.POSITIVE_INFINITY;
let maximumWatchlistItems = Number.POSITIVE_INFINITY;
let maximumActiveAlerts = Number.POSITIVE_INFINITY;
let maximumWorkspaces = Number.POSITIVE_INFINITY;
let availableIndicatorIds = null;
let availableDrawingToolIds = null;
let indicatorState = {
  volume: false,
  sma20: true,
  ema50: true
};
const drawingCommandHistory = new DrawingCommandHistory({
  onAnnounce: (message) => announce(message)
});

const lineToolButtons = populateDrawingToolMenu(lineToolsMenu, (toolId) => {
  if (activeChart) selectDrawingTool(activeChart, toolId);
});
lineToolCurrent.addEventListener("click", () => {
  if (!activeChart) return;
  const toolId = activeChart.workspace.getDrawingState().activeToolId
    || selectedDrawingToolId;
  selectDrawingTool(activeChart, toolId);
});
lineToolsToggle.addEventListener("click", () => {
  const willOpen = lineToolsMenu.hidden;
  lineToolsMenu.hidden = !willOpen;
  lineToolsToggle.setAttribute("aria-expanded", String(willOpen));
  if (willOpen) {
    syncDrawingToolUi(activeChart);
    fitMenuToBottom(lineToolsMenu, window.innerHeight, 8);
    lineToolButtons.find((button) => button.getAttribute("aria-checked") === "true")
      ?.focus();
  }
});
lineToolsMenu.addEventListener("keydown", (event) => {
  if (event.key === "Escape" || event.key === "ArrowLeft") {
    event.preventDefault();
    closeLineToolsMenu();
    lineToolsToggle.focus();
    return;
  }
  if (
    (event.key === "Enter" || event.key === " ")
    && event.target instanceof HTMLButtonElement
  ) {
    event.preventDefault();
    event.target.click();
    return;
  }
  moveMenuFocus(event, lineToolButtons);
});

const symbolSearch = new SymbolSearch({
  element: symbolSearchElement,
  repository: new SymbolSearchRepository("/api/v1/symbols/search"),
  sessionStore: new SymbolSessionStore(),
  getCurrentInstrumentId: () => workspaceInstrumentId,
  getOpenInstrumentIds: () => chartInstances
    .filter((instance) => !instance.destroyed && instance.instrumentId)
    .map((instance) => instance.instrumentId),
  onBeforeOpen: prepareForSymbolSearch,
  onClose: () => symbolSearchTrigger.setAttribute("aria-expanded", "false"),
  onSelect: (instrument) => {
    registerInstrument(instrument);
    return applyInstrument(instrument, true, activeChart);
  },
  onAnnounce: announce
});
const watchlistManager = new WatchlistManager({
  surface: workspaceSurface,
  template: watchlistWindowTemplate,
  openButton: newWatchlistButton,
  symbolSearch,
  realtimeManager,
  onSelectInstrument: (instrument) => applyInstrument(instrument, true, activeChart),
  onFocusPanel: clearChartWindowFocus,
  nextStackOrder: nextWorkspaceStackOrder,
  onAnnounce: announce
});
realtimeManager?.on(handleRealtimeManagerEvent);
if (mockRealtimeHarness) {
  globalThis.__flexRealtimeTest = Object.freeze({
    emit: value => mockRealtimeHarness.emit(value),
    disconnect: () => mockRealtimeHarness.disconnect(),
    setOutcome: (providerSymbol, status) => mockRealtimeHarness.setOutcome(providerSymbol, status),
    providerSnapshot: () => mockRealtimeHarness.snapshot(),
    managerSnapshot: () => realtimeManager.snapshot(),
    workspaceSnapshot: () => chartInstances.map(instance => ({
      symbol: instance.symbol,
      timeframe: instance.timeframe,
      length: instance.data?.length || 0,
      lastTimestamp: instance.data?.length
        ? Number(instance.data.timestamps[instance.data.length - 1])
        : null,
      authoritativeClose: instance.data?.length
        ? Number(instance.data.closes[instance.data.length - 1])
        : null,
      authoritativeVolume: instance.data?.length
        ? Number(instance.data.volumes[instance.data.length - 1])
        : null,
      displayedClose: instance.workspace?.state?.data?.length
        ? Number(instance.workspace.state.data.closes[instance.workspace.state.data.length - 1])
        : null,
      displayedVolume: instance.workspace?.state?.data?.length
        ? Number(instance.workspace.state.data.volumes[instance.workspace.state.data.length - 1])
        : null,
      displayedLength: instance.workspace?.state?.data?.length || 0,
      status: instance.realtimeStatus?.textContent || null,
      releaseCount: instance.realtimeReleases?.length || 0,
      providerStatuses: Object.fromEntries(instance.realtimeProviderStatuses || [])
    }))
  });
}
const alertWorkspace = new AlertWorkspace({
  root,
  surface: workspaceSurface,
  openButton: alertManagerButton,
  getActiveChart: () => activeChart,
  getCharts: () => chartInstances.filter((instance) => !instance.destroyed),
  focusChart: (instance) => setActiveChart(instance),
  onFocusWindow: clearWorkspaceWindowFocusForAlerts,
  onWindowStateChange: () => scheduleWorkspaceSave(),
  nextStackOrder: nextWorkspaceStackOrder,
  announce
});

const indicatorManager = new IndicatorManager({
  element: indicatorManagerElement,
  onApply: applyManagedIndicators,
  getAdditionError: (definitionId, currentInstances) => {
    const currentIds = new Set(currentInstances.map((indicator) => String(indicator.id)));
    let candidateId = "__capability_candidate__";
    while (currentIds.has(candidateId)) candidateId += "_";
    return indicatorCreationError(
      currentInstances,
      [...currentInstances, { id: candidateId, definitionId }],
      indicatorCapabilityPolicy()
    );
  },
  onAddAlert: (instance, indicator, definition) => {
    if (!presentationFeatureEnabled("alerts")) return false;
    indicatorManager.close({ restoreFocus: false });
    return alertWorkspace.openForIndicator(instance, indicator, definition);
  },
  onDeleteIndicator: (instance, indicator) =>
    alertWorkspace.handleIndicatorDeleted(instance, indicator),
  onAnnounce: announce
});
workspaceObjectRegistry.registerProvider({
  id: "charts",
  getRootNodes: chartObjectTreeNodes
});
workspaceObjectRegistry.registerProvider({
  id: "watchlists",
  getRootNodes: () => presentationFeatureEnabled("watchlists")
    ? watchlistObjectTreeNodes()
    : [],
  subscribe: (listener) => watchlistManager.subscribeObjectTree(listener)
});
workspaceObjectRegistry.registerProvider({
  id: "alerts",
  getRootNodes: () => presentationFeatureEnabled("alerts")
    ? alertManagerObjectTreeNodes()
    : [],
  subscribe: (listener) => alertWorkspace.subscribeObjectTree(listener)
});
const objectTree = new ObjectTreeWindow({
  surface: workspaceSurface,
  launcher: objectTreeButton,
  registry: workspaceObjectRegistry,
  storageKey: `flexcharting.object-tree.v1.pending`,
  restoreOnConstruct: !workspaceEndpoint,
  onStateChange: () => scheduleWorkspaceSave(),
  announce
});
upcomingEarnings = initializeOptionalFeature(
  "Earnings",
  () => new UpcomingEarningsWindow({
    surface: workspaceSurface,
    launcher: upcomingEarningsButton,
    endpoint: root.dataset.upcomingEarningsEndpoint || "/api/v1/upcoming-earnings",
    storageKey: "flexcharting.upcoming-earnings.v1.pending",
    restoreOnConstruct: !workspaceEndpoint,
    onOpenChart: openUpcomingCompanyChart,
    onStateChange: () => scheduleWorkspaceSave(),
    onFocusWindow: () => {
      clearChartWindowFocus();
      upcomingSplits?.element.classList.remove("is-focused");
      watchlistManager.clearFocus();
      settingsWindow.classList.remove("is-focused");
    },
    nextStackOrder: nextWorkspaceStackOrder,
    announce
  })
);
upcomingSplits = initializeOptionalFeature(
  "Splits",
  () => new UpcomingSplitsWindow({
    surface: workspaceSurface,
    launcher: upcomingSplitsButton,
    endpoint: root.dataset.upcomingSplitsEndpoint || "/api/v1/upcoming-splits",
    storageKey: "flexcharting.upcoming-splits.v1.pending",
    restoreOnConstruct: !workspaceEndpoint,
    onOpenChart: openUpcomingCompanyChart,
    onStateChange: () => scheduleWorkspaceSave(),
    onFocusWindow: () => {
      clearChartWindowFocus();
      upcomingEarnings?.element.classList.remove("is-focused");
      watchlistManager.clearFocus();
      settingsWindow.classList.remove("is-focused");
    },
    nextStackOrder: nextWorkspaceStackOrder,
    announce
  })
);
const goToDateDialog = initializeOptionalFeature(
  "Go To Date",
  () => new GoToDateDialog({
    element: goToDateElement,
    onGo: navigateChartToDate,
    onCancel: cancelGoToDateNavigation
  })
);
const indicatorManagerFloatingWindow = new FloatingWindowController({
  surface: workspaceSurface,
  windowElement: indicatorManagerElement,
  dragHandle: indicatorManagerDragHandle,
  resizeHandles: [],
  onInteractionEnd: () => scheduleWorkspaceSave()
});
applyIndicatorManagerDefaultGeometry();

const messenger = createIframeMessenger(parentOrigin, {
  "host.ready": () => {
    if (initialized) sendReady();
  },
  "chart.setSymbol": ({ symbol, symbolId }) =>
    applySymbolIdentity(symbol, symbolId, false),
  "chart.setTimeframe": ({ timeframe }) => applyTimeframe(timeframe, false),
  "chart.setTheme": ({ theme }) => applyTheme(theme, false),
  "chart.setIndicators": (payload) => applyIndicators(payload),
  "chart.resetView": () => activeChart?.workspace.resetView(),
  "chart.goToDate": () => openGoToDate(activeChart),
  "chart.resize": () => chartInstances.forEach((instance) => instance.workspace.resize())
});

const settingsFloatingWindow = new FloatingWindowController({
  surface: workspaceSurface,
  windowElement: settingsWindow,
  dragHandle: settingsDragHandle,
  resizeHandles: [],
  onInteractionEnd: () => scheduleWorkspaceSave()
});
settingsWindow.hidden = true;

const indicatorTemplateRepository = new TemplateRepository({
  endpoint: templateEndpoint,
  type: "Indicator"
});
const chartTemplateRepository = new TemplateRepository({
  endpoint: templateEndpoint,
  type: "Chart"
});

const indicatorTemplateMenu = indicatorTemplateTrigger
  ? new TemplateMenuController({
    trigger: indicatorTemplateTrigger,
    container: indicatorManagerElement,
    repository: indicatorTemplateRepository,
    typeLabel: "Indicator",
    capturePayload: captureIndicatorTemplate,
    applyTemplate: applyIndicatorTemplateRecord,
    restoreDefaults: restoreIndicatorDefaults,
    applicationWarning: indicatorTemplateApplicationWarning,
    restoreMessage:
      "This replaces every indicator on this chart with the official default indicators. "
      + "Saved templates, the symbol, timeframe, drawings, and alerts will not change.",
    announce,
    menuLayout: "direct-list"
  })
  : null;

const chartTemplateMenu = chartTemplateTrigger
  ? new TemplateMenuController({
    trigger: chartTemplateTrigger,
    container: settingsWindow,
    repository: chartTemplateRepository,
    typeLabel: "Chart",
    capturePayload: captureChartTemplate,
    applyTemplate: applyChartTemplateRecord,
    restoreDefaults: restoreChartDefaults,
    restoreMessage:
      "This restores every setting represented in Chart Settings to the application defaults. "
      + "The symbol, timeframe, indicators, drawings, alerts, viewport, and saved templates "
      + "will not change.",
    announce,
    menuLayout: "direct-list"
  })
  : null;
indicatorManagerElement.addEventListener(
  "indicator-manager-close",
  () => indicatorTemplateMenu?.dismiss()
);

async function initialize() {
  try {
    await loadAuthoritativeChartBootstrap();
    await loadServerWorkspace();
    upcomingEarnings?.restoreState(restoredWorkspace?.snapshot?.preferences?.upcomingEarnings);
    upcomingSplits?.restoreState(restoredWorkspace?.snapshot?.preferences?.upcomingSplits);
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : "Chart access could not be initialized.";
    renderStatus.textContent = message;
    root.dataset.initialized = "false";
    for (const state of initialChartWindow.querySelectorAll("[data-state]")) {
      state.hidden = state.getAttribute("data-state") !== "error";
      const messageNode = state.querySelector("[data-state-message]");
      if (messageNode) messageNode.textContent = message;
    }
    messenger.error("initialization-failed", message, false);
    return;
  }
  timeframeSelect.value = defaultTimeframe;
  try {
    const firstRestoredWindowId = restoredWorkspace?.snapshot?.schemaVersion === WORKSPACE_SCHEMA_VERSION
      && Array.isArray(restoredWorkspace.snapshot.windows)
      ? restoredWorkspace.snapshot.windows[0]?.id || null
      : null;
    const restoredWindowCount = restoredWorkspace?.snapshot?.schemaVersion === WORKSPACE_SCHEMA_VERSION
      && Array.isArray(restoredWorkspace.snapshot.windows)
      ? Math.min(restoredWorkspace.snapshot.windows.length, maximumChartWindows)
      : 0;
    const initialWindowCount = restoredWindowCount || configuredInitialChartSeeds.length;
    if (initialWindowCount > 0) {
      createChartInstance(
        initialChartWindow,
        firstRestoredWindowId,
        restoredWindowCount ? null : configuredInitialChartSeeds[0]
      );
      while (chartInstances.length < initialWindowCount) {
        const index = chartInstances.length;
        const savedId = restoredWindowCount
          ? restoredWorkspace.snapshot.windows[index]?.id || null
          : null;
        createChartInstance(
          null,
          savedId,
          restoredWindowCount ? null : configuredInitialChartSeeds[index]
        );
      }
      if (!restoredWindowCount) applyConfiguredInitialLayout();
    }
  } catch (error) {
    const message = error instanceof WebGlUnavailableError
      ? error.message
      : "WebGL2 initialization failed.";
    showAllStates("webgl", message);
    messenger.error("webgl-unavailable", message, false);
    return;
  }

  applyTheme(defaultTheme, false);
  applyIndicators(indicatorState);
  showAllStates("loading", "Loading market data.");

  try {
    await loadSymbols();
    const defaultInstrument = defaultSymbolId
      ? symbolCatalogById.get(defaultSymbolId)
      : instrumentForCode(defaultSymbol);
    if (!defaultInstrument) {
      throw new Error(`The configured symbol ${defaultSymbol} is not supported.`);
    }
    for (let index = 0; index < chartInstances.length; index += 1) {
      const instance = chartInstances[index];
      const seed = configuredInitialChartSeeds[index];
      const instrument = seed && !restoredWorkspace
        ? await resolveConfiguredSeedInstrument(seed) || defaultInstrument
        : defaultInstrument;
      assignInstrument(instance, instrument);
    }
    setWorkspaceInstrument(defaultInstrument);
    await restoreServerWorkspace();
    if (!restoredWorkspace && chartInstances.length > 0) {
      setActiveChart(
        chartInstances[Math.min(configuredActiveChartIndex, chartInstances.length - 1)],
        { raise: false, persist: false }
      );
      const activeInstrument = instrumentForInstance(activeChart);
      if (activeInstrument) setWorkspaceInstrument(activeInstrument);
    }
    const hasChartWindows = chartInstances.length > 0;
    if (!hasChartWindows) {
      initialChartWindow.hidden = true;
      renderStatus.textContent = "No chart windows are available for this product";
      symbolToolbarCode.textContent = "\u2014";
      symbolToolbarExchange.textContent = "No chart";
      updateWindowControls();
    }
    symbolSearchTrigger.disabled = !hasChartWindows;
    timeframeSelect.disabled = !hasChartWindows;
    indicatorManagerButton.disabled = !hasChartWindows;
    timeframeSelect.value = activeChart?.timeframe || defaultTimeframe;
    updateSymbolSummaries();
    if (hasChartWindows) syncToolbarSymbol(activeChart);
    updateInstanceTimeframe(activeChart);
    await loadAllCharts(true);
    initialized = true;
    root.dataset.initialized = "true";
    sendReady();
    void hydrateOptionalFeatures([
      async () => {
        try { if (presentationFeatureEnabled("watchlists")) await watchlistManager.initialize(); }
        catch (error) {
          newWatchlistButton.disabled = true;
          announce(error?.message || "Watchlists could not be loaded.");
        }
      },
      async () => {
        try { await alertWorkspace.initialize(); }
        catch (error) { announce(error?.message || "Alerts could not be loaded."); }
      }
    ], () => {
      optionalWorkspaceHydrated = true;
      scheduleWorkspaceSave();
    });
  } catch (error) {
    if (error?.name === "AbortError") return;
    handleInitializationError(error);
  }
}

function createChartInstance(sourceElement = null, requestedId = null, customizationSeed = null) {
  if (chartInstances.length >= maximumChartWindows) return null;

  const sourceChart = activeChart;
  const isInitial = sourceElement === initialChartWindow && chartInstances.length === 0;
  const selectedWorkspaceInstrument = instrumentForWorkspace();
  const initialInstrumentId = customizationSeed?.symbolId
    || selectedWorkspaceInstrument?.instrumentId
    || sourceChart?.instrumentId
    || null;
  const initialSymbol = customizationSeed?.symbol
    || selectedWorkspaceInstrument?.code
    || workspaceSymbol
    || sourceChart?.symbol
    || symbolSelect.value
    || defaultSymbol;
  const canReuseSourceData = sourceChart?.instrumentId === initialInstrumentId;
  const element = isInitial
    ? initialChartWindow
    : initialChartWindow.cloneNode(true);
  if (!(element instanceof HTMLElement)) {
    throw new TypeError("The chart window template could not be cloned.");
  }

  const reservedIdentity = reserveChartWindowId({
    requestedId,
    existingIds: chartInstances.map((instance) => instance.id),
    sequence: chartSequence
  });
  const id = reservedIdentity.id;
  chartSequence = reservedIdentity.sequence;
  element.dataset.chartInstance = id;
  element.classList.remove("is-active", "is-interacting");
  element.style.removeProperty("left");
  element.style.removeProperty("top");
  element.style.removeProperty("width");
  element.style.removeProperty("height");
  element.style.removeProperty("z-index");

  const dragHandle = requiredElement("[data-window-drag-handle]", element);
  const resizeHandles = [...element.querySelectorAll("[data-window-resize]")];
  const stage = requiredElement("[data-chart-stage]", element);
  const webglCanvas = requiredElement("[data-webgl-chart]", element);
  const overlayCanvas = requiredElement("[data-chart-overlay]", element);
  const symbolLockIndicator = requiredElement("[data-symbol-lock-indicator]", element);
  const symbolTitle = requiredElement("[data-symbol-title]", element);
  const symbolDescription = requiredElement("[data-symbol-description]", element);
  const symbolExchange = requiredElement("[data-symbol-exchange]", element);
  const dataWindow = requiredElement("[data-data-window]", element);
  const ohlc = requiredElement("[data-ohlc]", element);
  const priceChange = requiredElement("[data-price-change]", element);
  const marketStatus = requiredElement("[data-market-status]", element);
  const delayedStatus = requiredElement("[data-delayed-status]", element);
  const realtimeStatus = requiredElement("[data-realtime-status]", element);
  const volumeValue = requiredElement("[data-volume-value]", element);
  const indicatorLegend = requiredElement("[data-indicator-legend]", element);
  const interval = requiredElement("[data-window-interval]", element);
  const timeframeToggle = requiredElement("[data-window-timeframe-toggle]", element);
  const timeframeLabel = requiredElement("[data-window-timeframe-label]", element);
  const timeframeMenu = requiredElement("[data-window-timeframe-menu]", element);
  const timeframeButtons = [...element.querySelectorAll("[data-window-timeframe-value]")];
  const contextMenu = requiredElement("[data-chart-context-menu]", element);
  const contextMenuButtons = [...element.querySelectorAll("[data-chart-context-action]")];
  const goToDateShortcutHint = element.querySelector(
    '[data-chart-context-action="go-to-date"] kbd'
  );
  if (goToDateShortcutHint instanceof HTMLElement) {
    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    goToDateShortcutHint.textContent = /mac/i.test(platform) ? "⌘G" : "Ctrl+G";
  }
  const drawingToolsContextToggle = requiredElement(
    "[data-drawing-tools-context-toggle]",
    contextMenu
  );
  const drawingToolsContextMenu = requiredElement(
    "[data-drawing-tools-context-menu]",
    contextMenu
  );
  const lockSymbolButton = requiredElement('[data-chart-context-action="lock-symbol"]', element);
  const liveRegion = requiredElement("[data-chart-live]", element);
  const retryButton = requiredElement("[data-retry]", element);
  const minimizeButton = requiredElement("[data-window-minimize]", element);
  const maximizeButton = requiredElement("[data-window-maximize]", element);
  const restoreButton = requiredElement("[data-window-restore]", element);
  const closeButton = requiredElement("[data-window-close]", element);
  const description = element.querySelector(".chart-sr-only[id]");

  if (description instanceof HTMLElement) {
    description.id = `${id}-description`;
    overlayCanvas.setAttribute("aria-describedby", description.id);
  }
  const fundamentalEventTooltip = new FundamentalEventTooltip(
    stage,
    overlayCanvas,
    `${id}-fundamental-event-tooltip`
  );
  if (!isInitial) {
    webglCanvas.removeAttribute("width");
    webglCanvas.removeAttribute("height");
    overlayCanvas.removeAttribute("width");
    overlayCanvas.removeAttribute("height");
    workspaceSurface.append(element);
  }

  const instance = {
    id,
    element,
    dragHandle,
    resizeHandles,
    stage,
    webglCanvas,
    overlayCanvas,
    description,
    symbolLockIndicator,
    symbolTitle,
    symbolDescription,
    symbolExchange,
    dataWindow,
    ohlc,
    priceChange,
    marketStatus,
    delayedStatus,
    realtimeStatus,
    volumeValue,
    indicatorLegend,
    fundamentalEventTooltip,
    interval,
    timeframeToggle,
    timeframeLabel,
    timeframeMenu,
    timeframeButtons,
    contextMenu,
    contextMenuButtons,
    drawingToolsContextToggle,
    drawingToolsContextMenu,
    drawingToolsContextButtons: [],
    drawingToolsDeleteAllButton: null,
    drawingContextMenu: null,
    drawingContextTargetId: null,
    drawingStylePreview: null,
    lockSymbolButton,
    liveRegion,
    retryButton,
    minimizeButton,
    maximizeButton,
    restoreButton,
    closeButton,
    workspace: null,
    floatingWindow: null,
    lastPerformance: null,
    plotWidth: 0,
    visibleBarCount: 0,
    stackOrder: 0,
    symbol: initialSymbol,
    timeframe: customizationSeed?.timeframe
      || sourceChart?.timeframe
      || normalizeTimeframe(timeframeSelect.value || defaultTimeframe),
    settings: customizationSeed
      ? {
          ...defaultChartSettings,
          chartType: customizationSeed.chartType || defaultChartSettings.chartType,
          sessionType: customizationSeed.session || defaultChartSettings.sessionType
        }
      : sourceChart?.settings
      ? { ...sourceChart.settings }
      : { ...defaultChartSettings },
    indicatorInstances: permittedIndicators(
      customizationSeed?.indicators
        ? cloneIndicatorInstances(customizationSeed.indicators)
        : sourceChart?.indicatorInstances
        ? cloneIndicatorInstances(sourceChart.indicatorInstances)
        : cloneIndicatorInstances(configuredDefaultIndicators)
    ),
    data: canReuseSourceData ? sourceChart?.data || null : null,
    dataIdentity: canReuseSourceData ? sourceChart?.dataIdentity || null : null,
    historyWindow: null,
    loadedDateRanges: canReuseSourceData
      ? (sourceChart?.loadedDateRanges || []).map((range) => ({ ...range }))
      : [],
    fetchDuration: sourceChart?.fetchDuration || 0,
    workerDuration: sourceChart?.workerDuration || 0,
    hoverQuoteIndex: null,
    pinnedQuoteIndex: null,
    paneIndicatorReadouts: new Map(),
    indicatorCalculationToken: 0,
    loadToken: 0,
    loadController: null,
    fundamentalController: null,
    loadPromise: null,
    goToDateController: null,
    goToDatePromise: null,
    goToDateToken: 0,
    navigationToastTimer: null,
    realtimeReleases: [],
    realtimeSubscriptionIdentity: null,
    realtimeProviderStatuses: new Map(),
    realtimeProviderMetadata: new Map(),
    realtimeMessageTypes: new Set(),
    realtimeOverlayCounts: { applied: 0, ignored: 0, reconcile: 0 },
    realtimeAppliedAccumulatedVolume: null,
    realtimeQuote: null,
    transientData: null,
    realtimeFrame: null,
    realtimeReconcilePromise: null,
    symbolId: customizationSeed?.symbolId
      || selectedWorkspaceInstrument?.symbolId
      || sourceChart?.symbolId
      || null,
    instrumentId: initialInstrumentId,
    fundamentalEvents: canReuseSourceData
      ? sourceChart?.fundamentalEvents || []
      : [],
    fundamentalFeedState: canReuseSourceData
      ? sourceChart?.fundamentalFeedState || null
      : null,
    symbolLocked: false,
    restoredViewport: null,
    linkGroupId: sourceChart?.linkGroupId || null,
    drawings: [],
    destroyed: false
  };

  instance.drawingToolsContextButtons = populateDrawingToolMenu(
    drawingToolsContextMenu,
    (toolId) => {
      selectDrawingTool(instance, toolId);
      closeContextMenu(instance, true);
    },
    () => {
      deleteAllDrawings(instance);
      closeContextMenu(instance, true);
    }
  );
  instance.drawingToolsDeleteAllButton = instance.drawingToolsContextButtons.find(
    (button) => button.dataset.drawingToolsAction === "delete-all"
  ) || null;

  try {
    instance.floatingWindow = new FloatingWindowController({
      surface: workspaceSurface,
      windowElement: element,
      dragHandle,
      resizeHandles,
      onChange: () => instance.workspace?.resize(),
      onInteractionEnd: () => scheduleWorkspaceSave()
    });
    instance.workspace = createWorkspace(instance);
    instance.drawingContextMenu = new DrawingContextMenu({
      stage,
      onAction: (action, value) => executeDrawingContextAction(instance, action, value),
      onStylePreview: (setting, value) => previewDrawingStyle(instance, setting, value),
      onStyleCommit: (setting, value) => commitDrawingStyle(instance, setting, value),
      onClose: () => {
        finalizeDrawingStylePreview(instance);
        instance.drawingContextTargetId = null;
        instance.element.classList.remove("has-drawing-context-menu");
      }
    });
  } catch (error) {
    fundamentalEventTooltip.dispose();
    instance.floatingWindow?.dispose();
    if (!isInitial) element.remove();
    throw error;
  }

  element.addEventListener("pointerdown", (event) => {
    settingsWindow.classList.remove("is-focused");
    setActiveChart(instance);
    if (event.target === overlayCanvas || event.target === webglCanvas || event.target === stage) {
      overlayCanvas.focus({ preventScroll: true });
    }
  }, { capture: true });
  for (const button of [minimizeButton, maximizeButton, restoreButton, closeButton]) {
    button.addEventListener("pointerdown", (event) => event.stopPropagation());
  }
  minimizeButton.addEventListener("click", () => setWindowState(instance, "minimized"));
  maximizeButton.addEventListener("click", () => setWindowState(instance, "maximized"));
  restoreButton.addEventListener("click", () => setWindowState(instance, "floating"));
  closeButton.addEventListener("click", () => destroyChartInstance(instance));
  interval.addEventListener("pointerdown", (event) => event.stopPropagation());
  instance.customInterval = new CustomIntervalMenu({ interval, menu: timeframeMenu, toggle: timeframeToggle,
    onOpen: () => { closeOtherTimeframeMenus(); closeTimeframeMenu(instance); },
    onSubmit: value => applyTimeframe(value, true, instance) });
  timeframeToggle.addEventListener("click", () => toggleTimeframeMenu(instance));
  timeframeMenu.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeTimeframeMenu(instance, true);
  });
  for (const button of timeframeButtons) {
    button.addEventListener("click", () => {
      applyTimeframe(button.dataset.windowTimeframeValue, true, instance);
      closeTimeframeMenu(instance, true);
    });
  }
  overlayCanvas.addEventListener("click", () => {
    if (!instance.workspace.getDrawingState().activeToolId) pinDataWindowValue(instance);
  });
  stage.addEventListener("contextmenu", (event) => openContextMenu(instance, event));
  overlayCanvas.addEventListener("keydown", (event) => {
    if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
      const drawingPoint = instance.workspace.getSelectedDrawingMenuPoint();
      if (!drawingPoint) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const stageRect = instance.stage.getBoundingClientRect();
      const overlayRect = instance.overlayCanvas.getBoundingClientRect();
      openDrawingContextMenu(instance, {
        x: overlayRect.left - stageRect.left + drawingPoint.x,
        y: overlayRect.top - stageRect.top + drawingPoint.y
      }, { keyboard: true });
      return;
    }
    if (
      (event.key === "Delete" || event.key === "Backspace")
      && instance.workspace.getSelectedDrawing()
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      executeDrawingContextAction(instance, "delete");
    }
  }, { capture: true });
  contextMenu.addEventListener("keydown", (event) => handleContextMenuKeydown(instance, event));
  drawingToolsContextMenu.addEventListener(
    "keydown",
    (event) => handleDrawingToolsMenuKeydown(instance, event)
  );
  for (const button of contextMenuButtons) {
    button.addEventListener("click", () => selectContextMenuAction(instance, button));
  }
  retryButton.addEventListener("click", () => loadChartInstance(instance, true));

  chartInstances.push(instance);
  workspaceObjectRegistry.notify();
  alertWorkspace.registerChart(instance);
  const offset = cascadeOffset(chartInstances.length - 1);
  instance.floatingWindow.reset(offset);
  setActiveChart(instance);
  updateWindowControls();
  updateInstanceWindowControls(instance);
  updateSymbolSummary(instance);
  updateInstanceLockState(instance);
  updateInstanceTimeframe(instance);
  updateInstanceIndicators(instance);
  applyInstanceSettings(instance);
  instance.workspace.setIndicatorInstances(instance.indicatorInstances, { stylesOnly: true });
  instance.workspace.setFundamentalEvents(instance.fundamentalEvents);
  showInstanceState(instance, instance.data ? null : "loading", "Loading market data.");

  if (instance.data) {
    instance.workspace.setSymbolAndTimeframe(instance.symbol, instance.timeframe);
    instance.workspace.setData(instance.data, {
      fetchDuration: instance.fetchDuration,
      workerDuration: instance.workerDuration,
      resetView: true
    });
    updateQuote(instance, null, instance.data);
    attachChartRealtime(instance);
    instance.workspace.publishPerformance();
  } else if (initialized) {
    loadChartInstance(instance, true);
  }

  announce(`${chartInstances.length === 1 ? "Chart workspace ready" : "New chart window created"}.`);
  return instance;
}

function createWorkspace(instance) {
  return new ChartWorkspace({
    stage: instance.stage,
    webglCanvas: instance.webglCanvas,
    overlayCanvas: instance.overlayCanvas,
    initialState: {
      symbol: instance.symbol,
      timeframe: instance.timeframe,
      theme: currentTheme(),
      indicators: {
        volume: false,
        sma20: false,
        ema50: false
      },
      indicatorInstances: instance.indicatorInstances,
      chartId: instance.id,
      drawings: instance.drawings,
      themePalettes: effectiveCustomization?.themes || null
    },
    onHover: (index, data) => {
      instance.hoverQuoteIndex = index;
      updateQuote(instance, index, data);
    },
    onFundamentalEventHover: (marker) => {
      if (marker) instance.fundamentalEventTooltip.show(marker);
      else instance.fundamentalEventTooltip.hide();
    },
    onPerformance: (performance) => {
      instance.lastPerformance = performance;
      updateViewportCenterAttribute(instance);
      if (activeChart === instance) publishActivePerformance(instance);
    },
    onResize: (width, height, viewport) => {
      instance.plotWidth = viewport.plotWidth;
      instance.visibleBarCount = viewport.visibleBarCount;
      if (activeChart === instance) {
        root.dataset.plotWidth = String(viewport.plotWidth);
        root.dataset.visibleBarCount = String(viewport.visibleBarCount);
        messenger.resized(width, height);
      }
    },
    onLayout: (layout) => positionIndicatorPaneReadouts(instance, layout),
    onViewportChange: () => {
      scheduleWorkspaceSave();
      historyWindowFor(instance).prefetch();
    },
    onIndicatorPaneHeightsChange: () => scheduleWorkspaceSave(),
    onDrawingsChange: (drawings) => {
      instance.drawings = drawings;
      notifyIfDrawingTreeChanged(instance);
      if (
        instance.drawingContextTargetId
        && instance.drawingContextMenu
        && !instance.drawingContextMenu.element.hidden
      ) {
        instance.drawingContextMenu.updateDrawing(
          drawings.find(
            (drawing) => drawing.id === instance.drawingContextTargetId
          ) || null
        );
      }
    },
    onDrawingStateChange: (drawingState) => {
      const selectedDrawingId = drawingState.selectionId || "";
      if (selectedDrawingId !== instance.objectTreeSelectedDrawingId) {
        instance.objectTreeSelectedDrawingId = selectedDrawingId;
        workspaceObjectRegistry.notify();
      }
      if (
        instance.drawingContextTargetId
        && instance.drawingContextMenu
        && !instance.drawingContextMenu.element.hidden
      ) {
        instance.drawingContextMenu.updateDrawing(
          instance.drawings.find(
            (drawing) => drawing.id === instance.drawingContextTargetId
          ) || null
        );
      }
      if (activeChart === instance) syncDrawingToolUi(instance);
    },
    onDrawingAnnounce: (message) => {
      instance.liveRegion.textContent = message;
      announce(message);
    },
    onDrawingCommand: ({ label, before, after }) => {
      if (!instance.workspace) return;
      drawingCommandHistory.record({
        label,
        before: [{ workspace: instance.workspace, snapshot: before }],
        after: [{ workspace: instance.workspace, snapshot: after }]
      });
      alertWorkspace.handleDrawingCommand(instance, before, after);
      scheduleWorkspaceSave();
    },
    onError: (code, message, recoverable) => {
      showInstanceState(instance, "error", message);
      if (activeChart === instance) messenger.error(code, message, recoverable);
    },
    onRecover: () => {
      showInstanceState(instance, null);
      if (activeChart === instance) sendReady();
    }
  });
}

function destroyChartInstance(instance) {
  const index = chartInstances.indexOf(instance);
  if (index < 0) return;
  instance.customInterval?.destroy();
  const wasActive = activeChart === instance;
  instance.destroyed = true;
  instance.loadToken += 1;
  instance.loadController?.abort();
  instance.loadController = null;
  instance.fundamentalController?.abort();
  instance.historyWindow?.reset();
  releaseChartRealtime(instance);
  instance.goToDateController?.abort();
  instance.goToDateController = null;
  goToDateDialog?.closeForTarget(instance);
  clearTimeout(instance.navigationToastTimer);
  closeTimeframeMenu(instance);
  closeContextMenu(instance);
  instance.drawingContextMenu?.destroy();
  instance.drawingContextMenu = null;
  instance.fundamentalEventTooltip.dispose();
  instance.floatingWindow.dispose();
  instance.workspace.dispose();
  instance.element.remove();
  chartInstances.splice(index, 1);
  workspaceObjectRegistry.notify();
  drawingCommandHistory.clear();
  scheduleWorkspaceSave();

  if (wasActive) {
    activeChart = null;
    const fallback = chartInstances.at(-1);
    if (fallback) {
      setActiveChart(fallback);
    } else {
      delete root.dataset.activeChartInstance;
      updated.textContent = "";
      renderStatus.textContent = "No chart windows open";
      symbolToolbarCode.textContent = "\u2014";
      symbolToolbarExchange.textContent = "No chart";
    }
  }
  if (settingsTarget === instance) closeSettingsWindow();
  if (!indicatorManagerElement.hidden) indicatorManager.close({ restoreFocus: false });
  updateWindowControls();
  announce("Chart window closed.");
}

function setActiveChart(instance, { raise = true, persist = true } = {}) {
  if (!instance || !chartInstances.includes(instance) && instance.element !== initialChartWindow) return;
  if (activeChart && activeChart !== instance) {
    activeChart.workspace?.cancelDrawingPlacement();
  }
  watchlistManager.clearFocus();
  upcomingEarnings?.element.classList.remove("is-focused");
  upcomingSplits?.element.classList.remove("is-focused");
  activeChart = instance;
  if (raise) instance.stackOrder = nextWorkspaceStackOrder();
  for (const chart of chartInstances) {
    const isActive = chart === instance;
    chart.element.classList.toggle("is-active", isActive);
    chart.element.setAttribute("aria-current", isActive ? "true" : "false");
    chart.element.style.zIndex = String(chart.stackOrder || 1);
  }
  root.dataset.activeChartInstance = instance.id;
  root.dataset.plotWidth = String(instance.plotWidth);
  root.dataset.visibleBarCount = String(instance.visibleBarCount);
  timeframeSelect.value = instance.timeframe;
  syncToolbarSymbol(instance);
  updated.textContent = instance.data ? loadedBarsLabel(instance) : "";
  updateInstanceTimeframe(instance);
  updateInstanceIndicators(instance);
  closeOtherTimeframeMenus(instance);
  if (instance.lastPerformance) publishActivePerformance(instance);
  syncDrawingToolUi(instance);
  workspaceObjectRegistry.notify();
  if (persist) scheduleWorkspaceSave();
}

function nextWorkspaceStackOrder() {
  const knownOrders = [
    ...chartInstances.map((instance) => instance.stackOrder),
    ...watchlistManager.objectTreePanels().map((panel) => panel.stackOrder),
    alertWorkspace.snapshotManagerState().stackOrder,
    upcomingEarnings?.snapshotState().stackOrder,
    upcomingSplits?.snapshotState().stackOrder
  ].filter(Number.isFinite);
  stackSequence = Math.max(stackSequence, ...knownOrders);
  return ++stackSequence;
}

function clearChartWindowFocus() {
  for (const chart of chartInstances) {
    chart.workspace?.cancelDrawingPlacement();
    chart.element.classList.remove("is-active");
    chart.element.setAttribute("aria-current", "false");
  }
}

function clearWorkspaceWindowFocusForAlerts() {
  clearChartWindowFocus();
  upcomingEarnings?.element.classList.remove("is-focused");
  upcomingSplits?.element.classList.remove("is-focused");
  watchlistManager.clearFocus();
  settingsWindow.classList.remove("is-focused");
}

function publishActivePerformance(instance) {
  const performance = instance.lastPerformance || instance.workspace.getPerformanceSnapshot();
  if (performance.loadedBarCount > 0 && !root.dataset.firstChartReadyMs) {
    root.dataset.firstChartReadyMs = String(Math.round(globalThis.performance.now()));
  }
  root.dataset.visibleBarCount = String(performance.visibleBarCount || 0);
  renderStatus.textContent =
    `${performance.rendererType} · ${Math.round(performance.visibleBarCount)} visible / ${performance.loadedBarCount} loaded`;
  root.dataset.dateAxisTickCount = String(performance.dateAxisTickCount || 0);
  root.dataset.dateAxisLabelCount = String(performance.dateAxisLabelCount || 0);
  root.dataset.dateAxisInterval = performance.dateAxisInterval || "";
  messenger.performance(roundPerformance(performance));
}

function updateViewportCenterAttribute(instance) {
  const timestamps = instance?.data?.timestamps;
  const viewport = instance?.workspace?.viewport;
  if (!timestamps?.length || !viewport || viewport.visibleCount <= 0) {
    instance?.element?.removeAttribute("data-viewport-center-date");
    return;
  }
  const centerIndex = Math.min(
    timestamps.length - 1,
    Math.max(0, Math.round(viewport.start + viewport.visibleCount / 2 - 0.5))
  );
  const centerDate = timestampToDateOnly(
    timestamps[centerIndex],
    exchangeTimeZoneForInstance(instance)
  );
  if (centerDate) {
    instance.element.dataset.viewportCenterDate = formatDateOnly(centerDate);
  } else {
    instance.element.removeAttribute("data-viewport-center-date");
  }
}

function updateWindowControls() {
  const atLimit = chartInstances.length >= maximumChartWindows;
  newChartButton.disabled = atLimit;
  newChartButton.title = atLimit
    ? `Maximum of ${maximumChartWindows} charts reached`
    : "New chart";
  root.dataset.chartWindowCount = String(chartInstances.length);
}

function setWindowState(instance, state) {
  if (!instance || instance.destroyed) return;
  closeTimeframeMenu(instance);
  closeContextMenu(instance);
  if (state === "minimized") {
    instance.floatingWindow.minimize();
  } else if (state === "maximized") {
    instance.floatingWindow.maximize();
  } else {
    instance.floatingWindow.restore();
  }
  updateInstanceWindowControls(instance);
  if (state !== "minimized") instance.workspace.resize();
  workspaceObjectRegistry.notify();
  scheduleWorkspaceSave();
}

function updateInstanceWindowControls(instance) {
  const state = instance.floatingWindow.state;
  const isFloating = state === "floating";
  instance.minimizeButton.hidden = !isFloating;
  instance.maximizeButton.hidden = !isFloating;
  instance.restoreButton.hidden = isFloating;
  instance.minimizeButton.disabled = state === "minimized";
  instance.maximizeButton.disabled = state === "maximized";
  instance.restoreButton.disabled = isFloating;
  const suffix = isFloating ? "" : `, ${state}`;
  const lockSuffix = instance.symbolLocked ? ", symbol locked" : "";
  instance.element.setAttribute(
    "aria-label",
    `${instance.symbol} chart window${suffix}${lockSuffix}`
  );
}

function resetWindowLayout() {
  chartInstances.forEach((instance, index) => {
    instance.floatingWindow.reset(cascadeOffset(index));
    updateInstanceWindowControls(instance);
  });
  announce("Chart window layout reset.");
  scheduleWorkspaceSave();
}

function cascadeOffset(index) {
  const step = index % 4;
  return {
    offsetX: step * WINDOW_CASCADE_OFFSET,
    offsetY: step * WINDOW_CASCADE_OFFSET
  };
}

function applyConfiguredInitialLayout() {
  if (configuredInitialLayout === "single" || chartInstances.length < 2) return;
  const width = workspaceSurface.clientWidth;
  const height = workspaceSurface.clientHeight;
  if (width <= 0 || height <= 0) return;
  const gap = 8;
  const geometries = configuredInitialLayout === "two-horizontal"
    ? chartInstances.map((_, index) => ({
        x: index * ((width - gap) / 2 + gap),
        y: 0,
        width: (width - gap) / 2,
        height
      }))
    : configuredInitialLayout === "two-vertical"
      ? chartInstances.map((_, index) => ({
          x: 0,
          y: index * ((height - gap) / 2 + gap),
          width,
          height: (height - gap) / 2
        }))
      : chartInstances.map((_, index) => ({
          x: (index % 2) * ((width - gap) / 2 + gap),
          y: Math.floor(index / 2) * ((height - gap) / 2 + gap),
          width: (width - gap) / 2,
          height: (height - gap) / 2
        }));
  chartInstances.forEach((instance, index) => {
    instance.floatingWindow.applyGeometry(geometries[index]);
    instance.workspace.resize();
  });
}

function resolveConfiguredThemeMode(mode) {
  return resolveEffectiveThemeMode(mode, window.matchMedia?.bind(window));
}

async function resolveConfiguredSeedInstrument(seed) {
  const local = seed.symbolId
    ? symbolCatalogById.get(seed.symbolId)
    : instrumentForCode(seed.symbol);
  if (local) return local;
  try {
    const endpoint = seed.symbolId
      ? `/api/v1/symbols/${encodeURIComponent(seed.symbolId)}`
      : `/api/v1/symbols/search?query=${encodeURIComponent(seed.symbol)}&limit=20`;
    const response = await fetch(endpoint, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (!response.ok) return null;
    const payload = await response.json();
    const candidates = (seed.symbolId ? [payload] : payload.results || [])
      .map(normalizeInstrument)
      .filter(Boolean);
    candidates.forEach(registerInstrument);
    return seed.symbolId
      ? candidates.find((item) => item.symbolId === seed.symbolId) || null
      : candidates.find((item) => item.code === seed.symbol) || null;
  } catch {
    return null;
  }
}

async function loadSymbols() {
  const endpoint = defaultSymbolId
    ? `/api/v1/symbols/${encodeURIComponent(defaultSymbolId)}`
    : `/api/v1/symbols/search?query=${encodeURIComponent(defaultSymbol)}&limit=20`;
  const response = await fetch(endpoint, {
    headers: { Accept: "application/json" },
    credentials: "same-origin"
  });
  if (!response.ok) throw new Error("The supported symbol list could not be loaded.");
  const payload = await response.json();
  const symbols = (defaultSymbolId ? [payload] : payload.results || [])
    .map(normalizeInstrument)
    .filter(Boolean);
  symbolCatalogById.clear();
  symbolCatalogByCode.clear();
  symbolSelect.replaceChildren();
  for (const symbol of symbols) {
    symbolCatalogById.set(symbol.instrumentId, symbol);
    const candidates = symbolCatalogByCode.get(symbol.code) || [];
    candidates.push(symbol);
    symbolCatalogByCode.set(symbol.code, candidates);
    const option = new Option(`${symbol.code} · ${symbol.exchangeDisplayName}`, symbol.code);
    option.dataset.instrumentId = symbol.instrumentId;
    option.dataset.name = symbol.displayName;
    option.dataset.exchange = symbol.exchangeDisplayName;
    symbolSelect.add(option);
  }
  if (!(defaultSymbolId
    ? symbolCatalogById.get(defaultSymbolId)
    : instrumentForCode(defaultSymbol))) {
    throw new Error(`The configured symbol ${defaultSymbol} is not supported.`);
  }
}

async function loadAllCharts(resetView) {
  await Promise.all(chartInstances.map(instance => loadChartInstance(instance, resetView)));
}

function loadChartInstance(instance, resetView, options = {}) {
  const operation = loadChartInstanceCore(instance, resetView, options);
  if (!instance) return operation;
  let trackedOperation;
  trackedOperation = operation.finally(() => {
    if (instance.loadPromise === trackedOperation) instance.loadPromise = null;
  });
  instance.loadPromise = trackedOperation;
  return trackedOperation;
}

async function loadChartInstanceCore(
  instance,
  resetView,
  { rollback = null, endDate = null, bypassCache = false } = {}
) {
  if (!instance || instance.destroyed) return;
  historyWindowFor(instance).reset();
  const plan = initialHistoryPlan({ historyCount, restoredViewport: instance.restoredViewport,
    timeframe: instance.timeframe, timeZone: exchangeTimeZoneForInstance(instance), allowLatest: Boolean(realtimeManager) });
  if (endDate) plan.endDate = endDate;
  instance.goToDateToken += 1;
  if (instance.goToDateController) {
    instance.goToDateController.abort();
    if (!goToDateDialog?.loading) {
      goToDateDialog?.closeForTarget(instance, { restoreFocus: false });
    }
  }
  instance.loadController?.abort();
  instance.fundamentalController?.abort();
  const controller = new AbortController();
  instance.loadController = controller;
  instance.fundamentalRangeKey = null;
  const token = ++instance.loadToken;
  instance.hoverQuoteIndex = null;
  instance.pinnedQuoteIndex = null;
  instance.workspace.setSymbolAndTimeframe(instance.symbol, instance.timeframe);
  updateSymbolSummary(instance);
  if (activeChart === instance) syncToolbarSymbol(instance);
  updateInstanceTimeframe(instance);
  applyInstanceSettings(instance);
  showInstanceState(
    instance,
    "loading",
    `Loading ${instance.symbol} ${instance.timeframe} data.`
  );

  let loaded = false;
  let initialLiveDenied = false;
  try {
    const initial = await loadInitialHistory(({ count, endDate: requestedEnd, includeCurrent, signal }) => loadMarketData(
      instance.symbolId, instance.instrumentId, instance.symbol, instance.timeframe,
      signal, { endDate: requestedEnd, count, includeCurrent, bypassCache }
    ), plan, controller.signal);
    const result = initial.result;
    endDate = initial.endDate;
    initialLiveDenied = initial.liveDenied;
    if (instance.destroyed || controller.signal.aborted || token !== instance.loadToken) return;
    if (!result.data?.length) {
      restoreInstrumentAfterFailedLoad(instance, rollback);
      const message = result.exhausted === false && result.nextEndDate
        ? "No market data was returned in the searched date ranges. Try an earlier chart date."
        : "No market data is available for this symbol and timeframe.";
      showInstanceState(instance, "empty", message);
      if (activeChart === instance) messenger.error("empty-data", message, true);
      return;
    }

    instance.data = result.data;
    instance.dataIdentity = historyIdentity(instance);
    historyWindowFor(instance).recordInitial(result, { endDate, targetCount: plan.targetCount, includeCurrent: initial.includeCurrent });
    instance.loadedDateRanges = [];
    const loadedRange = dateRangeForTimestamps(
      instance.data.timestamps,
      exchangeTimeZoneForInstance(instance)
    );
    if (loadedRange) instance.loadedDateRanges.push(loadedRange);
    instance.fetchDuration = result.fetchDuration;
    instance.workerDuration = result.workerDuration;
    recalculateIndicators(
      instance,
      instance.indicatorInstances.map((indicator) => indicator.id)
    );
    instance.workspace.setData(instance.data, {
      fetchDuration: instance.fetchDuration,
      workerDuration: instance.workerDuration,
      resetView
    });
    if (instance.restoredViewport) {
      restoreViewportAnchor(instance, instance.restoredViewport);
      instance.restoredViewport = null;
    }
    instance.workspace.setIndicatorInstances(instance.indicatorInstances);
    updateQuote(instance, null, instance.data);
    attachChartRealtime(instance);
    showInstanceState(instance, null);
    loaded = true;
    if (activeChart === instance) {
      updated.textContent = loadedBarsLabel(instance);
      instance.workspace.publishPerformance();
    }

    refreshFundamentalEvents(instance, loadedRange, controller.signal);
  } catch (error) {
    if (error?.name === "AbortError" || instance.destroyed || token !== instance.loadToken) return;
    restoreInstrumentAfterFailedLoad(instance, rollback);
    handleLoadError(error, instance);
  } finally {
    if (instance.loadController === controller) {
      instance.loadController = null;
      if (loaded && !instance.destroyed && !controller.signal.aborted && token === instance.loadToken) {
        // Give the small first page a paint before scheduling the wider history.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!instance.destroyed && !controller.signal.aborted && token === instance.loadToken) {
            void historyWindowFor(instance).completeInitial({
              live: !initialLiveDenied && instance.realtimeReleases.length > 0
            }).catch(() => {});
          }
        }));
      }
    }
  }
}

async function loadMarketData(
  symbolId,
  instrumentId,
  symbol,
  timeframe,
  signal,
  { endDate = null, bypassCache = false, count = historyCount, includeCurrent = true } = {}
) {
  return historyRequests.load({
    symbolId,
    instrumentId,
    symbol,
    timeframe,
    count,
    endDate,
    includeCurrent,
    signal
  }, { bypassCache });
}

function historyIdentity(instance) {
  return `${dataSource.endpoint}|${instance.symbolId || instance.instrumentId || instance.symbol}|${instance.timeframe}`;
}

function historyWindowFor(instance) {
  if (instance.historyWindow) return instance.historyWindow;
  instance.historyWindow = new HistoryWindowController({
    read: () => !instance.destroyed && !instance.loadController && instance.dataIdentity === historyIdentity(instance)
      ? { identity: instance.dataIdentity, data: instance.data, viewport: instance.workspace.viewport }
      : null,
    load: ({ endDate = null, signal, bypassCache = false, count = historyCount }) => loadMarketData(
      instance.symbolId, instance.instrumentId, instance.symbol, instance.timeframe,
      signal, { endDate, bypassCache, count }
    ),
    dateOf: timestamp => formatDateOnly(timestampToDateOnly(timestamp, exchangeTimeZoneForInstance(instance))),
    apply: result => mergeChartHistory(instance, result),
    onError: error => {
      if (error?.status === 401 || error?.status === 403) handleLoadError(error, instance);
      else {
        const message = error?.customerMessage || "Additional history is temporarily unavailable. Your loaded chart is still available.";
        instance.liveRegion.textContent = message;
        setChartRealtimeStatus(instance, "Offline", message);
      }
    }
  });
  return instance.historyWindow;
}

function mergeChartHistory(instance, result) {
  const merged = mergeHistoryWindow(instance.data, result.data, instance.workspace.viewport, {
    transientData: instance.transientData,
    pinnedQuoteIndex: instance.pinnedQuoteIndex,
    hoverQuoteIndex: instance.hoverQuoteIndex
  });
  instance.data = merged.data;
  instance.transientData = merged.transientData || null;
  instance.pinnedQuoteIndex = merged.pinnedQuoteIndex ?? null;
  instance.hoverQuoteIndex = merged.hoverQuoteIndex ?? null;
  const displayed = instance.transientData || instance.data;
  instance.fetchDuration = result.fetchDuration;
  instance.workerDuration = result.workerDuration;
  const range = dateRangeForTimestamps(result.data.timestamps, exchangeTimeZoneForInstance(instance));
  if (range) addLoadedDateRange(instance, range);
  recalculateIndicators(instance, instance.indicatorInstances.map(indicator => indicator.id));
  instance.workspace.setData(displayed, {
    fetchDuration: result.fetchDuration, workerDuration: result.workerDuration, resetView: false
  });
  instance.workspace.viewport.start = merged.start;
  instance.workspace.viewport.visibleCount = merged.visibleCount;
  instance.workspace.viewport.clamp();
  instance.workspace.setIndicatorInstances(instance.transientData
    ? calculateIndicatorsForData(instance.indicatorInstances, displayed)
    : instance.indicatorInstances);
  updateQuote(instance, instance.hoverQuoteIndex, displayed);
  if (activeChart === instance) updated.textContent = loadedBarsLabel(instance);
  refreshFundamentalEvents(instance, dateRangeForTimestamps(instance.data.timestamps, exchangeTimeZoneForInstance(instance)));
}

function refreshFundamentalEvents(instance, historyRange, signal = null) {
  const range = fundamentalEventDateRange(new Date(), historyRange);
  const key = `${instance.symbolId}:${range.from}:${range.to}`;
  if (instance.fundamentalRangeKey === key) return;
  instance.fundamentalController?.abort();
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  instance.fundamentalController = controller;
  instance.fundamentalRangeKey = key;
  const token = instance.loadToken;
  void loadFundamentalEvents(instance.symbolId, controller.signal, historyRange).then(feed => {
    if (instance.destroyed || controller.signal.aborted || token !== instance.loadToken) return;
    instance.fundamentalEvents = Array.isArray(feed?.events) ? feed.events : [];
    instance.fundamentalFeedState = String(feed?.state || "Unavailable");
    instance.workspace.setFundamentalEvents(instance.fundamentalEvents);
  }).catch(() => {
    if (!controller.signal.aborted && !instance.destroyed && token === instance.loadToken) {
      instance.fundamentalFeedState = "Unavailable";
      instance.fundamentalRangeKey = null;
    }
  }).finally(() => {
    signal?.removeEventListener("abort", abort);
    if (instance.fundamentalController === controller) {
      instance.fundamentalController = null;
      if (controller.signal.aborted && instance.fundamentalRangeKey === key) instance.fundamentalRangeKey = null;
    }
  });
}

async function loadFundamentalEvents(symbolId, signal, historyRange = null) {
  if (!symbolId) {
    return fundamentalEventSource.load({ symbolId: null, signal });
  }
  const range = fundamentalEventDateRange(new Date(), historyRange);
  const key = `${symbolId}:${range.from}:${range.to}`;
  const cached = fundamentalEventCache.get(key);
  if (cached) return cached;
  const feed = await fundamentalEventSource.load({
    symbolId,
    ...range,
    signal
  });
  if (!signal?.aborted) fundamentalEventCache.set(key, feed);
  return feed;
}

function applySymbol(symbol, notify = true, instance = activeChart) {
  const instrument = instrumentForCode(symbol);
  if (!instrument) return false;
  return applyInstrument(instrument, notify, instance);
}

async function applySymbolIdentity(symbol, symbolId, notify = true, instance = activeChart) {
  if (!symbolId) return applySymbol(symbol, notify, instance);
  let instrument = symbolCatalogById.get(symbolId);
  if (!instrument) {
    try {
      const response = await fetch(`/api/v1/symbols/${encodeURIComponent(symbolId)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) return false;
      instrument = normalizeInstrument(await response.json());
      if (instrument) registerInstrument(instrument);
    } catch {
      return false;
    }
  }
  if (!instrument || instrument.code !== String(symbol || "").trim().toUpperCase()) return false;
  return applyInstrument(instrument, notify, instance);
}

async function openUpcomingCompanyChart(item) {
  if (!initialized) throw new Error("The chart workspace is still loading. Try again shortly.");
  const symbol = String(item?.symbol || "").trim().toUpperCase();
  const symbolId = String(item?.symbolId || "");
  if (!symbol || !symbolId) throw new Error("This record has no usable company identity.");
  const instrument = await resolveConfiguredSeedInstrument({ symbol, symbolId });
  if (!instrument || instrument.code !== symbol || instrument.symbolId !== symbolId) {
    throw new Error("The company could not be resolved to its current chart. Try again later.");
  }
  let instance = chartInstances.find((candidate) => !candidate.destroyed
    && candidate.instrumentId === instrument.instrumentId);
  if (!instance) {
    if (chartInstances.length >= maximumChartWindows) {
      throw new Error(maximumChartWindows === 0
        ? "Charts are unavailable for this product."
        : "Close a chart window before opening another company chart.");
    }
    instance = createChartInstance(null, null, { symbol, symbolId });
    if (!instance) throw new Error("A chart window could not be opened.");
  }
  if (instance.floatingWindow.state === "minimized") instance.floatingWindow.restore();
  setWorkspaceInstrument(instrument);
  setActiveChart(instance);
  instance.overlayCanvas.focus({ preventScroll: true });
  scheduleWorkspaceSave();
  announce(`${symbol} chart opened.`);
  return true;
}

function applyInstrument(instrument, notify = true, instance = activeChart) {
  if (!instrument?.instrumentId || !instrument?.code) return false;
  registerInstrument(instrument);
  const workspaceChanged = workspaceInstrumentId !== instrument.instrumentId;
  setWorkspaceInstrument(instrument);

  const targets = chartInstances.filter(
    (candidate) => !candidate.destroyed && !candidate.symbolLocked
  );

  for (const target of targets) {
    closeContextMenu(target);
    if (target.instrumentId === instrument.instrumentId) {
      // A restored chart can already have the canonical instrument ID while its
      // legacy snapshot lacks the stable symbol ID. Reapply the authoritative
      // instrument so alert ownership checks never observe a half-hydrated
      // identity.
      assignInstrument(target, instrument);
      updateSymbolSummary(target);
      continue;
    }
    const rollback = {
      symbolId: target.symbolId,
      instrumentId: target.instrumentId,
      symbol: target.symbol
    };
    assignInstrument(target, instrument);
    updateSymbolSummary(target);
    loadChartInstance(target, true, { rollback });
  }

  syncToolbarSymbol();
  if (notify && workspaceChanged) {
    messenger.symbolChanged(instrument.code, instrument.symbolId);
  }
  scheduleWorkspaceSave();
  return {
    accepted: true,
    focusTarget: instance && !instance.destroyed ? instance.overlayCanvas : null
  };
}

function applyTimeframe(timeframe, notify = true, instance = activeChart) {
  if (!instance) return;
  if (!(timeframeSelect instanceof HTMLSelectElement)) return;
  const normalized = normalizeChartTimeframe(timeframe);
  if (!normalized) return;
  if (![...timeframeSelect.options].some(option => option.value === normalized)) {
    timeframeSelect.add(new Option(normalized, normalized));
  }
  closeContextMenu(instance);
  if (instance.timeframe === normalized) {
    if (activeChart === instance) timeframeSelect.value = normalized;
    updateInstanceTimeframe(instance);
    return;
  }
  releaseChartRealtime(instance);
  instance.timeframe = normalized;
  instance.data = null;
  updateInstanceTimeframe(instance);
  workspaceObjectRegistry.notify();
  if (activeChart === instance) {
    timeframeSelect.value = normalized;
    if (notify) messenger.timeframeChanged(normalized);
  }
  loadChartInstance(instance, true);
  scheduleWorkspaceSave();
}

function applyTheme(theme, notify = true) {
  if (theme !== "dark" && theme !== "light") return;
  const previousDefaults = defaultChartSettings;
  if (effectiveCustomization) {
    effectiveThemePalette = applyEffectiveTheme(document, effectiveCustomization, theme);
    setDrawingThemePalette(effectiveThemePalette);
    defaultChartSettings = defaultChartSettingsByTheme[theme];
  } else {
    document.documentElement.dataset.theme = theme;
  }
  themeToggle.setAttribute("aria-pressed", String(theme === "light"));
  themeToggle.setAttribute("aria-label", theme === "light" ? "Use dark theme" : "Use light theme");
  chartInstances.forEach((instance) => {
    if (effectiveCustomization) {
      for (const key of Object.keys(paletteChartSettings(effectiveCustomization, theme))) {
        if (instance.settings[key] === previousDefaults[key]) {
          instance.settings[key] = defaultChartSettings[key];
        }
      }
      migrateConfiguredIndicatorColors(
        instance,
        configuredDefaultIndicators,
        configuredDefaultIndicatorsByTheme[theme]
      );
      applyInstanceSettings(instance);
    }
    instance.workspace.setThemePalettes(effectiveCustomization?.themes || null);
    instance.workspace.setTheme(theme);
  });
  if (notify) messenger.themeChanged(theme);
  configuredDefaultIndicators = configuredDefaultIndicatorsByTheme[theme];
  scheduleWorkspaceSave();
}

function applyIndicators(indicators) {
  indicatorState = {
    ...indicatorState,
    ...indicators,
    volume: false
  };
  chartInstances.forEach((instance) => {
    const visibilityByDefinition = {};
    if (Object.hasOwn(indicators, "sma20")) {
      visibilityByDefinition.sma = indicators.sma20 !== false;
    }
    if (Object.hasOwn(indicators, "ema50")) {
      visibilityByDefinition.ema = indicators.ema50 !== false;
    }
    if (Object.hasOwn(indicators, "volume")) {
      visibilityByDefinition.volume = indicators.volume !== false;
    }
    let instances = instance.indicatorInstances.map((indicator) => (
      Object.hasOwn(visibilityByDefinition, indicator.definitionId)
        ? { ...indicator, visible: visibilityByDefinition[indicator.definitionId] }
        : indicator
    ));
    if (indicators.volume === true && !instances.some((indicator) => indicator.definitionId === "volume")) {
      const volume = createIndicatorInstance("volume", { paneId: "pane-2" });
      const candidate = [...instances, volume];
      const restriction = indicatorCreationError(
        instance.indicatorInstances,
        candidate,
        indicatorCapabilityPolicy()
      );
      if (restriction) {
        indicatorState.volume = false;
        announce(restriction);
        messenger.error("indicator-capability", restriction, true);
      } else {
        instances = recalculateIndicators(instance, [volume.id], candidate) || candidate;
      }
    }
    instance.indicatorInstances = instances;
    instance.workspace.setIndicators({ volume: false, sma20: false, ema50: false });
    instance.workspace.setIndicatorInstances(instance.indicatorInstances, { stylesOnly: true });
    updateInstanceIndicators(instance);
  });
  workspaceObjectRegistry.notify();
  scheduleWorkspaceSave();
}

async function applyManagedIndicators(instance, instances, options = {}) {
  if (!instance || instance.destroyed) return;
  if (options.rollback !== true) {
    const restriction = indicatorCreationError(
      instance.indicatorInstances,
      instances,
      indicatorCapabilityPolicy()
    );
    if (restriction) throw new Error(restriction);
  }
  const previous = new Map(
    instance.indicatorInstances.map((indicator) => [indicator.id, indicator])
  );
  instance.indicatorInstances = instances.map((indicator) => ({
    ...indicator,
    outputs: Object.keys(indicator.outputs || {}).length > 0
      ? indicator.outputs
      : previous.get(indicator.id)?.outputs || {}
  }));
  // History can expand while the manager is open. Opening output arrays no
  // longer describe the current bars, even though its settings are restored.
  if (options.rollback === true) {
    recalculateIndicators(instance, instance.indicatorInstances.map(indicator => indicator.id));
  } else if (options.recalculateIds?.length) {
    recalculateIndicators(instance, options.recalculateIds);
  }
  instance.workspace.setIndicatorInstances(instance.indicatorInstances, {
    stylesOnly: options.styleOnly === true
  });
  updateInstanceIndicators(instance);
  if (instance.data?.length) updateQuote(instance, instance.hoverQuoteIndex, instance.data);
  workspaceObjectRegistry.notify();
  scheduleWorkspaceSave();
}

function captureIndicatorTemplate() {
  const instance = indicatorManager.context?.target;
  if (!instance || instance.destroyed) {
    throw new Error("Open Indicator Manager for a chart before saving a template.");
  }
  return createIndicatorTemplatePayload(
    instance.indicatorInstances,
    instance.workspace.getIndicatorPaneHeights()
  );
}

function indicatorTemplateApplicationWarning(payload) {
  const instance = indicatorManager.context?.target;
  if (!instance || instance.destroyed) {
    throw new Error("The target chart is no longer available.");
  }
  prepareIndicatorTemplate(payload);
  const differs = !materiallyEqual(captureIndicatorTemplate(), payload);
  return differs && instance.indicatorInstances.length > 0
    ? `This replaces all ${instance.indicatorInstances.length} current indicators; it does not merge them.`
    : "";
}

async function applyIndicatorTemplateRecord(record) {
  const instance = indicatorManager.context?.target;
  if (!instance || instance.destroyed) {
    throw new Error("The target chart is no longer available.");
  }
  const prepared = prepareIndicatorTemplate(record.payload);
  if (prepared.instances.length > maximumIndicatorsPerChart) {
    throw new Error(
      `This template contains ${prepared.instances.length} indicators, above the current limit of `
      + `${maximumIndicatorsPerChart}.`
    );
  }
  const unavailable = availableIndicatorIds
    ? prepared.instances.find((indicator) =>
        !availableIndicatorIds.has(String(indicator.definitionId).toLowerCase()))
    : null;
  if (unavailable) {
    const definition = getIndicatorDefinition(unavailable.definitionId);
    throw new Error(
      `${definition?.name || unavailable.definitionId} is unavailable for this educator product.`
    );
  }
  const calculated = calculatePreparedIndicators(instance, prepared.instances);
  const previous = captureIndicatorStateSnapshot(instance);
  const next = {
    instances: cloneIndicatorInstances(calculated),
    paneHeights: { ...prepared.paneHeights }
  };
  try {
    commitIndicatorStateSnapshot(instance, next);
  } catch (error) {
    commitIndicatorStateSnapshot(instance, previous);
    throw error;
  }
  recordIndicatorTemplateCommand(instance, previous, next, `Apply ${record.name} template`);
}

async function restoreIndicatorDefaults() {
  const instance = indicatorManager.context?.target;
  if (!instance || instance.destroyed) {
    throw new Error("The target chart is no longer available.");
  }
  const previous = captureIndicatorStateSnapshot(instance);
  const defaults = calculatePreparedIndicators(
    instance,
    permittedIndicators(cloneIndicatorInstances(configuredDefaultIndicators))
  );
  const next = {
    instances: cloneIndicatorInstances(defaults),
    paneHeights: {}
  };
  try {
    commitIndicatorStateSnapshot(instance, next);
  } catch (error) {
    commitIndicatorStateSnapshot(instance, previous);
    throw error;
  }
  recordIndicatorTemplateCommand(instance, previous, next, "Restore indicator defaults");
}

function calculatePreparedIndicators(instance, instances) {
  if (!instance.data?.length) return cloneIndicatorInstances(instances);
  const calculated = recalculateIndicators(
    instance,
    instances.map((indicator) => indicator.id),
    instances
  );
  const failed = calculated?.find((indicator) => indicator.error);
  if (failed) {
    const definition = getIndicatorDefinition(failed.definitionId);
    throw new Error(
      `${definition?.name || failed.definitionId} could not be calculated: ${failed.error}`
    );
  }
  return calculated || cloneIndicatorInstances(instances);
}

function captureIndicatorStateSnapshot(instance) {
  return {
    instances: cloneIndicatorInstances(instance.indicatorInstances),
    paneHeights: { ...instance.workspace.getIndicatorPaneHeights() }
  };
}

function commitIndicatorStateSnapshot(instance, snapshot) {
  if (!instance || instance.destroyed) return;
  instance.indicatorInstances = cloneIndicatorInstances(snapshot.instances);
  instance.workspace.setIndicatorTemplateState(
    instance.indicatorInstances,
    snapshot.paneHeights
  );
  updateInstanceIndicators(instance);
  if (instance.data?.length) updateQuote(instance, instance.hoverQuoteIndex, instance.data);
  if (indicatorManager.context?.target === instance) {
    indicatorManager.replaceInstances(instance.indicatorInstances);
  }
  workspaceObjectRegistry.notify();
  scheduleWorkspaceSave();
}

function recordIndicatorTemplateCommand(instance, before, after, label) {
  const beforeKey = JSON.stringify(createIndicatorTemplatePayload(
    before.instances,
    before.paneHeights
  ));
  const afterKey = JSON.stringify(createIndicatorTemplatePayload(
    after.instances,
    after.paneHeights
  ));
  drawingCommandHistory.recordCustom({
    label,
    beforeKey,
    afterKey,
    undo: () => commitIndicatorStateSnapshot(instance, before),
    redo: () => commitIndicatorStateSnapshot(instance, after)
  });
}

function recalculateIndicators(instance, ids, sourceInstances = null) {
  if (!instance?.data?.length) return;
  const requested = new Set(ids || []);
  const candidates = sourceInstances || instance.indicatorInstances;
  const calculated = candidates.map((indicator) => {
    if (!requested.has(indicator.id)) return indicator;
    const definition = getIndicatorDefinition(indicator.definitionId);
    if (!definition) {
      return { ...indicator, outputs: {}, error: "Definition unavailable." };
    }
    try {
      return {
        ...indicator,
        outputs: calculateIndicator(definition, indicator, instance.data),
        error: null
      };
    } catch (error) {
      return {
        ...indicator,
        outputs: {},
        error: error instanceof Error ? error.message : "Calculation failed."
      };
    }
  });
  if (sourceInstances) return calculated;
  instance.indicatorInstances = calculated;
}

function openIndicatorManager(instance, opener = indicatorManagerButton) {
  if (effectiveCustomization && !featureIsVisible(effectiveCustomization, "indicatorManager")) {
    return;
  }
  if (!instance || instance.destroyed) return;
  closeSettingsWindow();
  indicatorManagerFloatingWindow.constrainToSurface();
  return indicatorManager.open({
    target: instance,
    symbol: instance.symbol,
    instances: instance.indicatorInstances,
    opener
  });
}

function applyIndicatorManagerDefaultGeometry() {
  const bounds = {
    width: workspaceSurface.clientWidth,
    height: workspaceSurface.clientHeight
  };
  if (bounds.width <= 0 || bounds.height <= 0) return;
  const compact = bounds.width <= 620 || bounds.height <= 560;
  const maximumWidth = Math.max(0, Math.min(920, bounds.width - 36));
  const maximumHeight = Math.max(0, Math.min(760, bounds.height - 32));
  const width = compact
    ? bounds.width
    : maximumWidth;
  const height = compact
    ? bounds.height
    : clamp(bounds.height * 0.74, Math.min(560, maximumHeight), maximumHeight);
  indicatorManagerFloatingWindow.applyGeometry({
    x: Math.max(0, (bounds.width - width) / 2),
    y: Math.max(0, (bounds.height - height) / 2),
    width,
    height
  });
}

function updateInstanceIndicators(instance, targetIndex = null) {
  const showValues = instance.settings?.statusIndicators !== false;
  const displayedIndicators = instance.workspace?.state?.indicatorInstances
    || instance.indicatorInstances;
  const displayedData = instance.workspace?.state?.data || instance.data;
  const visible = displayedIndicators.filter((indicator) => indicator.visible !== false);
  const groupByPane = instance.settings?.dataGrouping === "pane";
  const usedPaneIds = new Set();
  instance.indicatorLegend.replaceChildren();
  instance.indicatorLegend.hidden = true;
  if (showValues) {
    const index = Number.isInteger(targetIndex)
      ? targetIndex
      : Math.max(0, (displayedData?.length || 1) - 1);
    for (const indicator of visible) {
      const definition = getIndicatorDefinition(indicator.definitionId);
      if (!definition) continue;
      const values = [];
      for (const output of definition.outputs) {
        const outputStyle = indicator.styles?.[output.key] || output.defaultStyle;
        if (
          output.kind === "fill"
          || outputStyle?.visible === false
          || outputStyle?.lastValueLabel === false
        ) continue;
        const value = indicator.outputs?.[output.key]?.[index];
        if (!Number.isFinite(value)) continue;
        values.push({
          label: output.label,
          value,
          color: outputStyle?.color || output.defaultStyle.color
        });
      }
      if (!indicator.error && values.length === 0) continue;
      const row = createIndicatorReadoutRow(instance, indicator, values);
      if (groupByPane && indicator.paneId !== "main") {
        const paneReadout = ensureIndicatorPaneReadout(instance, indicator.paneId);
        if (!usedPaneIds.has(indicator.paneId)) paneReadout.replaceChildren();
        paneReadout.append(row);
        usedPaneIds.add(indicator.paneId);
      } else {
        instance.indicatorLegend.append(row);
      }
    }
  }
  instance.indicatorLegend.hidden = instance.indicatorLegend.childElementCount === 0;
  for (const [paneId, readout] of instance.paneIndicatorReadouts) {
    if (groupByPane && showValues && usedPaneIds.has(paneId)) continue;
    readout.remove();
    instance.paneIndicatorReadouts.delete(paneId);
  }
  if (groupByPane) {
    positionIndicatorPaneReadouts(instance, instance.workspace?.renderInfo?.layout);
  }
  if (activeChart === instance) {
    indicatorCount.textContent = String(visible.length);
    indicatorManagerButton.setAttribute(
      "aria-label",
      `Open Indicator Manager. ${visible.length} active ${visible.length === 1 ? "indicator" : "indicators"}.`
    );
  }
}

function createIndicatorReadoutRow(instance, indicator, values) {
  const row = document.createElement("span");
  row.className = "chart-indicator-legend-row";
  row.dataset.indicatorLegendInstance = indicator.id;
  row.dataset.indicatorPane = indicator.paneId;
  if (values[0]?.color) row.style.color = values[0].color;
  const name = document.createElement("b");
  name.textContent = indicator.displayName;
  row.append(name);
  if (indicator.error) {
    const error = document.createElement("span");
    error.className = "chart-indicator-legend-error";
    error.textContent = "Error";
    error.title = indicator.error;
    row.append(error);
    return row;
  }
  for (const output of values) {
    const value = document.createElement("span");
    value.style.color = output.color;
    value.title = output.label;
    value.textContent = formatIndicatorValue(output.value, resolveDataPrecision(instance));
    row.append(value);
  }
  return row;
}

function ensureIndicatorPaneReadout(instance, paneId) {
  let readout = instance.paneIndicatorReadouts.get(paneId);
  if (readout) {
    applyDataWindowSurfaceStyle(instance, readout);
    return readout;
  }
  readout = document.createElement("div");
  readout.className = "chart-legend chart-indicator-pane-readout";
  readout.dataset.indicatorPaneReadout = paneId;
  readout.setAttribute("aria-label", `${paneId} indicator values`);
  applyDataWindowSurfaceStyle(instance, readout);
  instance.stage.append(readout);
  instance.paneIndicatorReadouts.set(paneId, readout);
  return readout;
}

function positionIndicatorPaneReadouts(instance, layout) {
  if (instance.settings?.dataGrouping !== "pane") return;
  const paneById = new Map((layout?.indicatorPanes || []).map((pane) => [pane.id, pane]));
  for (const [paneId, readout] of instance.paneIndicatorReadouts) {
    const pane = paneById.get(paneId);
    readout.hidden = !pane;
    if (pane) readout.style.top = `${Math.round(pane.top + 6)}px`;
  }
}

function updateInstanceTimeframe(instance) {
  if (!instance) return;
  if (normalizeChartTimeframe(instance.timeframe) && ![...timeframeSelect.options].some(option => option.value === instance.timeframe)) {
    timeframeSelect.add(new Option(instance.timeframe, instance.timeframe));
  }
  if (activeChart === instance) timeframeSelect.value = instance.timeframe;
  instance.timeframeLabel.textContent = instance.timeframe;
  instance.timeframeToggle.setAttribute(
    "aria-label",
    `Change chart interval. Current interval ${timeframeDescription(instance.timeframe)}`
  );
  for (const button of instance.timeframeButtons) {
    button.setAttribute(
      "aria-checked",
      String(button.dataset.windowTimeframeValue === instance.timeframe)
    );
  }
}

function toggleTimeframeMenu(instance) {
  const opening = instance.timeframeMenu.hidden;
  instance.customInterval?.close();
  closeOtherTimeframeMenus(instance);
  instance.timeframeMenu.hidden = !opening;
  instance.timeframeToggle.setAttribute("aria-expanded", String(opening));
  if (opening) {
    const selected = instance.timeframeButtons.find(
      (button) => button.dataset.windowTimeframeValue === instance.timeframe
    );
    selected?.focus();
  }
}

function closeTimeframeMenu(instance, restoreFocus = false) {
  if (!instance) return;
  instance.customInterval?.close();
  instance.timeframeMenu.hidden = true;
  instance.timeframeToggle.setAttribute("aria-expanded", "false");
  if (restoreFocus) instance.timeframeToggle.focus();
}

function closeOtherTimeframeMenus(except = null) {
  for (const instance of chartInstances) {
    if (instance !== except) closeTimeframeMenu(instance);
  }
}

function openContextMenu(instance, event) {
  if (!instance || instance.destroyed || instance.floatingWindow.state === "minimized") return;
  if (
    presentationFeatureEnabled("alerts")
    && alertWorkspace.openAlertContextMenu(instance, event)
  ) return;
  event.preventDefault();
  const canceledDrawingPlacement = instance.workspace.cancelDrawingForContextMenu();
  syncDrawingToolUi(instance);
  if (canceledDrawingPlacement) {
    closeTimeframeMenu(instance);
    closeContextMenu(instance);
    setActiveChart(instance);
    return;
  }
  closeTimeframeMenu(instance);
  closeOtherContextMenus(instance);
  setActiveChart(instance);
  updateInstanceLockState(instance);
  instance.drawingContextMenu?.close({ restoreFocus: false, notify: true });
  instance.drawingContextTargetId = null;

  const stageRect = instance.stage.getBoundingClientRect();
  const overlayRect = instance.overlayCanvas.getBoundingClientRect();
  const menuPoint = {
    x: event.clientX - stageRect.left,
    y: event.clientY - stageRect.top
  };
  const drawingPoint = {
    x: event.clientX - overlayRect.left,
    y: event.clientY - overlayRect.top
  };
  const info = instance.workspace.renderInfo;
  instance.contextAlertPrice = (
    info?.layout &&
    drawingPoint.y >= info.layout.priceTop &&
    drawingPoint.y <= info.layout.priceBottom
  )
    ? info.yToPrice(drawingPoint.y)
    : null;
  const addAlertLabel = instance.contextMenu.querySelector("[data-add-alert-label]");
  if (addAlertLabel) {
    const price = Number(instance.contextAlertPrice);
    addAlertLabel.textContent = Number.isFinite(price)
      ? `Add Alert at ${price.toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 4
        })}`
      : "Add Alert\u2026";
  }
  const drawingTarget = instance.workspace.drawingAtPoint(drawingPoint, {
    tolerance: 8,
    select: true
  });
  if (drawingTarget) {
    openDrawingContextMenu(instance, menuPoint, {
      drawing: drawingTarget.drawing
    });
    return;
  }

  instance.contextMenu.hidden = false;
  instance.contextMenu.style.left = "0px";
  instance.contextMenu.style.top = "0px";
  const inset = 6;
  const left = clamp(
    event.clientX - stageRect.left,
    inset,
    Math.max(inset, stageRect.width - instance.contextMenu.offsetWidth - inset)
  );
  const top = clamp(
    event.clientY - stageRect.top,
    inset,
    Math.max(inset, stageRect.height - instance.contextMenu.offsetHeight - inset)
  );
  instance.contextMenu.style.left = `${Math.round(left)}px`;
  instance.contextMenu.style.top = `${Math.round(top)}px`;
  instance.contextMenuButtons[0]?.focus();
}

function openDrawingContextMenu(
  instance,
  point,
  { keyboard = false, drawing: requestedDrawing = null } = {}
) {
  if (!instance || instance.destroyed || !instance.drawingContextMenu) return;
  const drawing = requestedDrawing || (
    keyboard ? instance.workspace.getSelectedDrawing() : null
  );
  if (!drawing) return;
  closeOtherContextMenus(instance);
  instance.contextMenu.hidden = true;
  instance.drawingContextTargetId = drawing.id;
  const tool = getDrawingTool(drawing.type);
  const destinationCharts = chartInstances
    .map((chart, index) => ({ chart, index }))
    .filter(({ chart }) => chart !== instance && !chart.destroyed)
    .map(({ chart, index }) => ({
      id: chart.id,
      label: `${chart.symbol} · ${chart.timeframe} · Chart ${index + 1}`
    }));
  instance.drawingContextMenu.open({
    drawing,
    capabilities: tool?.contextMenuCapabilities,
    point,
    destinationCharts,
    returnFocus: instance.overlayCanvas
  });
  instance.element.classList.add("has-drawing-context-menu");
}

function closeContextMenu(instance, restoreFocus = false) {
  if (!instance) return;
  instance.drawingToolsContextMenu.hidden = true;
  instance.drawingToolsContextToggle.setAttribute("aria-expanded", "false");
  instance.contextMenu.hidden = true;
  instance.drawingContextMenu?.close({ restoreFocus, notify: true });
  instance.drawingContextTargetId = null;
  if (restoreFocus) instance.overlayCanvas.focus();
}

function closeOtherContextMenus(except = null) {
  for (const instance of chartInstances) {
    if (instance !== except) closeContextMenu(instance);
  }
}

function executeDrawingContextAction(instance, action, value = null) {
  if (!instance || instance.destroyed) return false;
  finalizeDrawingStylePreview(instance);
  const drawingId = instance.drawingContextTargetId
    || instance.workspace.getSelectedDrawing()?.id;
  const drawing = instance.workspace.getSelectedDrawing();
  if (!drawingId || !drawing || drawing.id !== drawingId) return false;

  if (action === "alert") {
    if (!presentationFeatureEnabled("alerts")) return false;
    alertWorkspace.openForDrawing(instance, drawing);
    closeContextMenu(instance);
    return true;
  }

  const styleSetting = action.startsWith("style-")
    ? action.slice("style-".length)
    : null;
  if (styleSetting) {
    return drawingCommandHistory.execute({
      label: `Change drawing ${drawingSettingLabel(styleSetting)}`,
      targets: [instance.workspace],
      mutate: () => instance.workspace.updateDrawingStyle(drawingId, {
        [styleSetting]: value
      })
    });
  }

  const drawingSetting = action.startsWith("setting-")
    ? action.slice("setting-".length)
    : null;
  if (drawingSetting) {
    return drawingCommandHistory.execute({
      label: `Change drawing ${drawingSettingLabel(drawingSetting)}`,
      targets: [instance.workspace],
      mutate: () => instance.workspace.updateDrawingSettings(drawingId, {
        [drawingSetting]: value
      })
    });
  }
  if (action === "drawing-visible") {
    return drawingCommandHistory.execute({
      label: value ? "Show drawing" : "Hide drawing",
      targets: [instance.workspace],
      mutate: () => instance.workspace.setDrawingVisible(drawingId, Boolean(value))
    });
  }

  if (action === "lock") {
    return drawingCommandHistory.execute({
      label: drawing.locked ? "Unlock drawing" : "Lock drawing",
      targets: [instance.workspace],
      mutate: () => instance.workspace.setDrawingLocked(drawingId, !drawing.locked)
    });
  }
  if (action === "clone") {
    const restriction = drawingCreationError(drawing, { availableDrawingToolIds });
    if (restriction) {
      announce(restriction);
      return false;
    }
    return drawingCommandHistory.execute({
      label: "Clone drawing",
      targets: [instance.workspace],
      mutate: () => instance.workspace.cloneDrawing(drawingId)
    });
  }
  if (action === "delete") {
    const remove = () => drawingCommandHistory.execute({
        label: "Delete drawing",
        targets: [instance.workspace],
        mutate: () => instance.workspace.deleteDrawing(drawingId)
      });
    if (alertWorkspace.requestDrawingDelete(instance, drawing, remove)) {
      closeContextMenu(instance);
      return true;
    }
    return remove();
  }
  if (action === "copy-chart" || action === "copy-all") {
    const restriction = drawingCreationError(drawing, { availableDrawingToolIds });
    if (restriction) {
      announce(restriction);
      return false;
    }
    const destinations = chartInstances.filter((chart) => (
      chart !== instance
      && !chart.destroyed
      && (action === "copy-all" || chart.id === value)
    ));
    if (destinations.length === 0) {
      announce("Open another chart to copy this drawing.");
      return false;
    }
    const changed = drawingCommandHistory.execute({
      label: destinations.length === 1 ? "Copy drawing" : "Copy drawing to charts",
      targets: destinations.map((chart) => chart.workspace),
      mutate: () => {
        for (const destination of destinations) {
          destination.workspace.insertDrawingCopy(drawing);
        }
      }
    });
    if (changed) {
      announce(
        destinations.length === 1
          ? `Drawing copied to ${destinations[0].symbol}.`
          : `Drawing copied to ${destinations.length} charts.`
      );
    }
    return changed;
  }
  return false;
}

function drawingSettingLabel(setting) {
  return String(setting).replace(/([A-Z])/g, " $1").toLowerCase();
}

function deleteAllDrawings(instance) {
  if (!instance || instance.destroyed) return false;
  const drawingCount = instance.workspace.serializeDrawings()?.drawings?.length || 0;
  if (drawingCount === 0) return false;
  const changed = drawingCommandHistory.execute({
    label: "Delete all drawings",
    targets: [instance.workspace],
    mutate: () => instance.workspace.restoreDrawings([])
  });
  if (changed) {
    announce(`Deleted ${drawingCount} drawing${drawingCount === 1 ? "" : "s"}.`);
  }
  return changed;
}

function previewDrawingStyle(instance, setting, value) {
  const drawingId = instance?.drawingContextTargetId;
  if (!drawingId) return;
  if (!instance.drawingStylePreview) {
    instance.drawingStylePreview = {
      drawingId,
      before: captureDrawingTargets([instance.workspace])
    };
  }
  instance.workspace.updateDrawingStyle(drawingId, { [setting]: value });
}

function commitDrawingStyle(instance, setting, value) {
  const drawingId = instance?.drawingContextTargetId;
  if (!drawingId) return;
  previewDrawingStyle(instance, setting, value);
  finalizeDrawingStylePreview(instance, `Change drawing ${setting}`);
}

function finalizeDrawingStylePreview(instance, label = "Change drawing appearance") {
  const preview = instance?.drawingStylePreview;
  if (!preview) return false;
  const after = captureDrawingTargets([instance.workspace]);
  instance.drawingStylePreview = null;
  return drawingCommandHistory.record({
    label,
    before: preview.before,
    after
  });
}

function handleContextMenuKeydown(instance, event) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeContextMenu(instance, true);
    return;
  }
  if (
    event.key === "ArrowRight"
    && document.activeElement === instance.drawingToolsContextToggle
  ) {
    event.preventDefault();
    toggleDrawingToolsContextMenu(instance);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const buttons = instance.contextMenuButtons;
  if (buttons.length === 0) return;
  const current = buttons.indexOf(document.activeElement);
  const next = event.key === "Home"
    ? 0
    : event.key === "End"
      ? buttons.length - 1
      : event.key === "ArrowDown"
        ? (current + 1 + buttons.length) % buttons.length
        : (current - 1 + buttons.length) % buttons.length;
  buttons[next].focus();
}

function selectContextMenuAction(instance, button) {
  const action = button.dataset.chartContextAction;
  if (action === "drawings") {
    toggleDrawingToolsContextMenu(instance);
    return;
  }
  const label = button.textContent.trim();
  instance.element.dispatchEvent(new CustomEvent("chartcontextaction", {
    bubbles: true,
    detail: {
      action,
      symbol: instance.symbol,
      timeframe: instance.timeframe
    }
  }));
  closeContextMenu(instance, action !== "go-to-date");
  if (action === "go-to-date") {
    openGoToDate(instance, instance.overlayCanvas);
    return;
  }
  if (action === "add-alert") {
    if (!presentationFeatureEnabled("alerts")) return;
    alertWorkspace.openAtChartPoint(instance, instance.contextAlertPrice);
    instance.contextAlertPrice = null;
    return;
  }
  if (action === "settings") {
    openSettingsWindow(instance);
    return;
  }
  if (action === "indicators") {
    openIndicatorManager(instance, instance.overlayCanvas);
    return;
  }
  if (action === "lock-symbol") {
    instance.symbolLocked = !instance.symbolLocked;
    updateInstanceLockState(instance);
    syncToolbarSymbol(instance);
    announce(`${instance.symbol} symbol ${instance.symbolLocked ? "locked" : "unlocked"}.`);
    scheduleWorkspaceSave();
    return;
  }
  announce(`${label} selected.`);
}

function populateDrawingToolMenu(container, onSelect, onDeleteAll) {
  container.replaceChildren();
  let previousSection = null;
  const tools = availableDrawingToolIds
    ? DRAWING_TOOLS.filter((tool) => availableDrawingToolIds.has(tool.id))
    : DRAWING_TOOLS;
  const buttons = tools.map((tool) => {
    if (tool.section !== previousSection) {
      const section = document.createElement("div");
      section.className = "chart-drawing-tool-group";
      section.setAttribute("role", "presentation");
      section.textContent = tool.section;
      container.append(section);
      previousSection = tool.section;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.role = "menuitemradio";
    button.dataset.drawingToolId = tool.id;
    button.setAttribute("aria-checked", "false");
    button.title = tool.label;
    const icon = createDrawingToolIcon(tool.id);
    const label = document.createElement("span");
    label.textContent = tool.label;
    const check = document.createElement("span");
    check.className = "chart-line-tool-check";
    check.setAttribute("aria-hidden", "true");
    check.textContent = "\u2713";
    button.append(icon, label, check);
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      onSelect(tool.id);
    });
    container.append(button);
    return button;
  });
  const divider = document.createElement("div");
  divider.className = "chart-context-divider";
  divider.setAttribute("role", "separator");
  const deleteAllButton = document.createElement("button");
  deleteAllButton.type = "button";
  deleteAllButton.role = "menuitem";
  deleteAllButton.className = "is-danger";
  deleteAllButton.dataset.drawingToolsAction = "delete-all";
  deleteAllButton.append(createChartIcon("trash"), "Delete All");
  deleteAllButton.addEventListener("click", (event) => {
    event.stopPropagation();
    onDeleteAll();
  });
  container.append(divider, deleteAllButton);
  return [...buttons, deleteAllButton];
}

function selectDrawingTool(instance, toolId) {
  const tool = getDrawingTool(toolId);
  if (!tool || !instance || instance.destroyed) return;
  if (availableDrawingToolIds && !availableDrawingToolIds.has(tool.id)) {
    announce("This drawing tool is unavailable for the active product.");
    return;
  }
  selectedDrawingToolId = tool.id;
  setActiveChart(instance);
  instance.workspace.armDrawingTool(tool.id);
  closeLineToolsMenu();
  syncDrawingToolUi(instance);
  instance.overlayCanvas.focus({ preventScroll: true });
}

function syncDrawingToolUi(instance = activeChart) {
  const state = instance?.workspace?.getDrawingState();
  const tool = getDrawingTool(state?.activeToolId || selectedDrawingToolId)
    || DRAWING_TOOLS[0];
  lineToolCurrent.replaceChildren(createDrawingToolIcon(tool.id));
  lineToolCurrent.title = tool.label;
  lineToolCurrent.setAttribute("aria-label", `Use ${tool.label} drawing tool`);
  lineTools.classList.toggle("is-active", Boolean(state?.activeToolId));
  for (const button of lineToolsMenu.querySelectorAll("[data-drawing-tool-id]")) {
    button.setAttribute("aria-checked", String(button.dataset.drawingToolId === tool.id));
  }
  if (instance) {
    for (const button of instance.drawingToolsContextButtons) {
      if (!button.dataset.drawingToolId) continue;
      button.setAttribute(
        "aria-checked",
        String(button.dataset.drawingToolId === state?.activeToolId)
      );
    }
  }
}

function toggleDrawingToolsContextMenu(instance) {
  const willOpen = instance.drawingToolsContextMenu.hidden;
  instance.drawingToolsContextMenu.hidden = !willOpen;
  instance.drawingToolsContextToggle.setAttribute("aria-expanded", String(willOpen));
  if (!willOpen) return;
  if (instance.drawingToolsDeleteAllButton) {
    instance.drawingToolsDeleteAllButton.disabled = (
      (instance.workspace.serializeDrawings()?.drawings?.length || 0) === 0
    );
  }
  const stageRect = instance.stage.getBoundingClientRect();
  const contextRect = instance.contextMenu.getBoundingClientRect();
  const submenuWidth = 194;
  instance.drawingToolsContextMenu.classList.toggle(
    "is-left",
    contextRect.right + submenuWidth + 8 > stageRect.right
  );
  fitMenuToBottom(
    instance.drawingToolsContextMenu,
    stageRect.bottom,
    6
  );
  syncDrawingToolUi(instance);
  instance.drawingToolsContextButtons[0]?.focus();
}

function fitMenuToBottom(menu, bottomBoundary, inset = 6) {
  if (!(menu instanceof HTMLElement) || menu.hidden) return;
  menu.style.removeProperty("max-height");
  const top = menu.getBoundingClientRect().top;
  const availableHeight = Math.floor(Number(bottomBoundary) - top - inset);
  menu.style.maxHeight = `${Math.max(48, availableHeight)}px`;
}

function handleDrawingToolsMenuKeydown(instance, event) {
  if (event.key === "Escape" || event.key === "ArrowLeft") {
    event.preventDefault();
    instance.drawingToolsContextMenu.hidden = true;
    instance.drawingToolsContextToggle.setAttribute("aria-expanded", "false");
    instance.drawingToolsContextToggle.focus();
    return;
  }
  if (
    (event.key === "Enter" || event.key === " ")
    && event.target instanceof HTMLButtonElement
  ) {
    event.preventDefault();
    event.target.click();
    return;
  }
  moveMenuFocus(event, instance.drawingToolsContextButtons);
}

function moveMenuFocus(event, buttons) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const availableButtons = buttons.filter((button) => !button.disabled);
  if (!availableButtons.length) return;
  const current = availableButtons.indexOf(document.activeElement);
  const next = event.key === "Home"
    ? 0
    : event.key === "End"
      ? availableButtons.length - 1
      : event.key === "ArrowDown"
        ? (current + 1 + availableButtons.length) % availableButtons.length
        : (current - 1 + availableButtons.length) % availableButtons.length;
  availableButtons[next].focus();
}

function closeLineToolsMenu() {
  lineToolsMenu.hidden = true;
  lineToolsToggle.setAttribute("aria-expanded", "false");
}

function updateInstanceLockState(instance) {
  if (!instance?.lockSymbolButton) return;
  const locked = instance.symbolLocked === true;
  instance.element.dataset.symbolLocked = String(locked);
  instance.symbolLockIndicator.hidden = !locked;
  instance.lockSymbolButton.setAttribute("aria-pressed", String(locked));
  instance.lockSymbolButton.classList.toggle("is-active", locked);
  const label = instance.lockSymbolButton.querySelector("span");
  if (label) label.textContent = locked ? "Unlock symbol" : "Lock symbol";
  updateInstanceWindowControls(instance);
}

function prepareForSymbolSearch() {
  closeSettingsWindow({ restoreFocus: false });
  indicatorManager.close({ restoreFocus: false });
  closeOtherTimeframeMenus();
  closeOtherContextMenus();
  symbolSearchTrigger.setAttribute("aria-expanded", "true");
}

function openSettingsWindow(instance) {
  if (effectiveCustomization && !featureIsVisible(effectiveCustomization, "chartSettings")) {
    return false;
  }
  if (!instance || instance.destroyed) return false;
  indicatorManager.close({ restoreFocus: false });
  settingsTarget = instance;
  settingsOpeningSnapshot = {
    target: instance,
    settings: structuredClone(instance.settings),
    snapWindows: surfaceWindowSnappingEnabled(workspaceSurface)
  };
  settingsSymbol.textContent = instance.symbol;
  if (snapWindowsControl) {
    snapWindowsControl.checked = surfaceWindowSnappingEnabled(workspaceSurface);
  }
  syncSettingsControls(instance);
  settingsWindow.hidden = false;
  settingsWindow.classList.add("is-focused");
  if (!settingsPositioned) {
    settingsFloatingWindow.applyGeometry(settingsWindowGeometry());
    settingsPositioned = true;
  } else {
    settingsFloatingWindow.constrainToSurface();
  }
  selectSettingsTab(settingsTabs.find((tab) => tab.getAttribute("aria-selected") === "true")
    || settingsTabs[0], false);
  settingsTabs.find((tab) => tab.getAttribute("aria-selected") === "true")?.focus();
  announce("Settings window opened.");
  return true;
}

function closeSettingsWindow() {
  if (settingsWindow.hidden) return;
  chartTemplateMenu?.dismiss();
  for (const dropdown of chartDropdowns) dropdown.close();
  for (const colorPicker of chartColorPickers) colorPicker.close();
  settingsWindow.hidden = true;
  settingsWindow.classList.remove("is-focused");
  settingsTarget = null;
  settingsOpeningSnapshot = null;
  activeChart?.overlayCanvas.focus();
  announce("Settings window closed.");
}

function cancelSettingsWindow() {
  const instance = settingsTarget;
  const snapshot = settingsOpeningSnapshot;
  if (instance && !instance.destroyed && snapshot?.target === instance) {
    instance.settings = {
      ...defaultChartSettings,
      ...structuredClone(snapshot.settings)
    };
    updateSnapWindowsPreference(snapshot.snapWindows);
    applyInstanceSettings(instance);
    scheduleWorkspaceSave();
  }
  closeSettingsWindow();
  announce("Settings changes canceled.");
}

function settingsWindowGeometry() {
  const bounds = {
    width: workspaceSurface.clientWidth,
    height: workspaceSurface.clientHeight
  };
  const inset = Math.min(12, Math.max(0, Math.min(bounds.width, bounds.height) / 2));
  const width = Math.min(840, Math.max(220, bounds.width - inset * 2));
  const height = Math.min(580, Math.max(260, bounds.height - inset * 2));
  return {
    x: Math.max(0, (bounds.width - width) / 2),
    y: Math.max(0, (bounds.height - height) / 2),
    width,
    height
  };
}

function fitSettingsWindowToContent() {
  if (settingsWindow.hidden) return;
  const bounds = {
    width: workspaceSurface.clientWidth,
    height: workspaceSurface.clientHeight
  };
  if (bounds.width <= 0 || bounds.height <= 0) return;

  const snapshot = settingsFloatingWindow.snapshot();
  const inset = Math.min(12, Math.max(0, Math.min(bounds.width, bounds.height) / 2));
  const previousHeight = settingsWindow.style.height;
  settingsWindow.classList.remove("is-content-constrained");
  settingsWindow.style.height = "auto";
  const desiredHeight = Math.ceil(settingsWindow.getBoundingClientRect().height);
  settingsWindow.style.height = previousHeight;

  const maximumHeight = Math.max(1, bounds.height - inset * 2);
  const height = clamp(desiredHeight, Math.min(260, maximumHeight), maximumHeight);
  const width = clamp(snapshot.width, Math.min(220, bounds.width), bounds.width);
  const constrained = desiredHeight > maximumHeight;
  settingsWindow.classList.toggle("is-content-constrained", constrained);
  settingsFloatingWindow.applyGeometry({
    x: clamp(snapshot.x, 0, Math.max(0, bounds.width - width)),
    y: clamp(snapshot.y, 0, Math.max(0, bounds.height - height)),
    width,
    height
  });
}

function readSettingsControl(control) {
  if (control instanceof HTMLInputElement && control.type === "checkbox") {
    return control.checked;
  }
  return control.value;
}

function createChartSettingDefinitions() {
  const definitions = {};
  for (const control of settingsControls) {
    const name = control.dataset.chartSetting;
    if (control instanceof HTMLInputElement && control.type === "checkbox") {
      definitions[name] = { type: "boolean" };
    } else if (control instanceof HTMLInputElement && control.type === "color") {
      definitions[name] = { type: "color" };
      definitions[colorOpacitySetting(control)] = {
        type: "number",
        minimum: 0,
        maximum: 100
      };
    } else {
      definitions[name] = {
        type: "string",
        options: control instanceof HTMLSelectElement
          ? [...control.options].map((option) => option.value)
          : null
      };
    }
  }
  return definitions;
}

function captureChartTemplate() {
  if (!settingsTarget || settingsTarget.destroyed) {
    throw new Error("Open Chart Settings for a chart before saving a template.");
  }
  return createChartTemplatePayload(settingsTarget.settings, chartSettingDefinitions);
}

async function applyChartTemplateRecord(record) {
  const instance = settingsTarget;
  if (!instance || instance.destroyed) {
    throw new Error("The target chart is no longer available.");
  }
  const prepared = prepareChartTemplate(
    record.payload,
    chartSettingDefinitions,
    defaultChartSettings
  );
  const previous = { settings: structuredClone(instance.settings) };
  const next = { settings: structuredClone(prepared.settings) };
  try {
    commitChartSettingsSnapshot(instance, next);
  } catch (error) {
    commitChartSettingsSnapshot(instance, previous);
    throw error;
  }
  recordChartTemplateCommand(instance, previous, next, `Apply ${record.name} template`);
}

async function restoreChartDefaults() {
  const instance = settingsTarget;
  if (!instance || instance.destroyed) {
    throw new Error("The target chart is no longer available.");
  }
  const previous = { settings: structuredClone(instance.settings) };
  const next = { settings: structuredClone(defaultChartSettings) };
  try {
    commitChartSettingsSnapshot(instance, next);
  } catch (error) {
    commitChartSettingsSnapshot(instance, previous);
    throw error;
  }
  recordChartTemplateCommand(instance, previous, next, "Restore chart defaults");
}

function commitChartSettingsSnapshot(instance, snapshot) {
  if (!instance || instance.destroyed) return;
  instance.settings = {
    ...defaultChartSettings,
    ...structuredClone(snapshot.settings)
  };
  if (settingsTarget === instance) syncSettingsControls(instance);
  applyInstanceSettings(instance);
  scheduleWorkspaceSave();
}

function recordChartTemplateCommand(instance, before, after, label) {
  const beforeKey = JSON.stringify(createChartTemplatePayload(
    before.settings,
    chartSettingDefinitions
  ));
  const afterKey = JSON.stringify(createChartTemplatePayload(
    after.settings,
    chartSettingDefinitions
  ));
  drawingCommandHistory.recordCustom({
    label,
    beforeKey,
    afterKey,
    undo: () => commitChartSettingsSnapshot(instance, before),
    redo: () => commitChartSettingsSnapshot(instance, after)
  });
}

function colorOpacitySetting(control) {
  return control.dataset.chartOpacitySetting
    || `${control.dataset.chartSetting}Opacity`;
}

function readColorOpacity(control) {
  const opacity = Number.parseFloat(control.dataset.chartOpacity);
  return clamp(Number.isFinite(opacity) ? opacity : 100, 0, 100);
}

function writeSettingsControl(control, value) {
  if (control instanceof HTMLInputElement && control.type === "checkbox") {
    control.checked = value === true;
    return;
  }
  control.value = String(value ?? "");
  syncChartDropdown(control);
  syncChartColorPicker(control);
  if (control === watermarkOpacityControl) syncWatermarkOpacityValue();
}

function syncSettingsControls(instance) {
  for (const control of settingsControls) {
    if (colorSettingsControls.includes(control)) {
      const opacitySetting = colorOpacitySetting(control);
      control.dataset.chartOpacity = String(
        instance.settings[opacitySetting] ?? defaultChartSettings[opacitySetting]
      );
    }
    writeSettingsControl(
      control,
      instance.settings[control.dataset.chartSetting] ?? defaultChartSettings[control.dataset.chartSetting]
    );
  }
  syncBackgroundSurfaceControls();
  syncMarketHoursControls();
}

function updateSettingFromControl(control) {
  if (!settingsTarget || control.disabled) return;
  if (watermarkContentControls.includes(control)) {
    syncWatermarkContentSelection(control);
  } else {
    settingsTarget.settings[control.dataset.chartSetting] = readSettingsControl(control);
  }
  if (control === backgroundStyleControl) syncBackgroundSurfaceControls({ fit: true });
  if (control === marketHoursControl) syncMarketHoursControls({ fit: true });
  if (control === watermarkOpacityControl) syncWatermarkOpacityValue();
  applyInstanceSettings(settingsTarget);
  scheduleWorkspaceSave();
}

function syncWatermarkContentSelection(changedControl) {
  const noneControl = watermarkContentControls.find(
    (control) => control.dataset.chartSetting === "watermarkNone"
  );
  const contentControls = watermarkContentControls.filter((control) => control !== noneControl);

  if (changedControl === noneControl && noneControl.checked) {
    contentControls.forEach((control) => {
      control.checked = false;
    });
  } else if (changedControl !== noneControl && changedControl.checked) {
    noneControl.checked = false;
  }

  if (!contentControls.some((control) => control.checked)) {
    noneControl.checked = true;
  }

  for (const control of watermarkContentControls) {
    settingsTarget.settings[control.dataset.chartSetting] = control.checked;
  }
}

function syncWatermarkOpacityValue() {
  watermarkOpacityValue.textContent = `${watermarkOpacityControl.value}%`;
}

function syncBackgroundSurfaceControls({ fit = false } = {}) {
  const gradientSelected = backgroundStyleControl.value === "gradient";
  backgroundGradientControl.hidden = !gradientSelected;
  if (!gradientSelected) {
    chartColorPickers
      .find((picker) => picker.input.dataset.chartSetting === "backgroundColorEnd")
      ?.close();
  }
  if (fit) fitSettingsWindowToContent();
}

function syncMarketHoursControls({ fit = false } = {}) {
  const mode = ["regular", "extended", "24-hours"].includes(marketHoursControl.value)
    ? marketHoursControl.value
    : "regular";
  const visibleSessions = mode === "24-hours"
    ? new Set(["premarket", "postmarket", "night"])
    : mode === "extended"
      ? new Set(["premarket", "postmarket"])
      : new Set();

  marketSessionColors.hidden = visibleSessions.size === 0;
  for (const row of marketSessionColorRows) {
    const visible = visibleSessions.has(row.dataset.marketSessionColor);
    row.hidden = !visible;
    if (!visible) {
      const setting = row.querySelector("[data-chart-setting]")?.dataset.chartSetting;
      chartColorPickers.find((picker) => picker.input.dataset.chartSetting === setting)?.close();
    }
  }
  if (fit) fitSettingsWindowToContent();
}

function applyInstanceSettings(instance) {
  if (!instance?.workspace) return;
  const settings = instance.settings;
  const chartType = normalizeChartType(settings.chartType);
  if (!settingsTarget || settingsTarget === instance) syncChartStyleSections(chartType);
  instance.element.dataset.chartType = chartType;
  Object.assign(instance.element.dataset, {
    scaleMode: String(settings.scaleMode || "linear"),
    showPriceScale: String(settings.showPriceScale !== false),
    showTimeScale: String(settings.showTimeScale !== false),
    pricePrecision: String(settings.pricePrecision || "auto"),
    topMargin: String(settings.topMargin || 0),
    bottomMargin: String(settings.bottomMargin || 0),
    rightSpacing: String(settings.rightSpacing || 0),
    dateTimeFormat: String(settings.dateTimeFormat || "auto"),
    lastPriceLabel: String(settings.lastPriceLabel !== false),
    lastPriceLine: String(settings.lastPriceLine !== false),
    previousCloseLine: String(settings.previousCloseLine === true),
    highLowLabels: String(settings.highLowLabels === true),
    statusSymbol: String(settings.statusSymbol !== false),
    statusDescription: String(settings.statusDescription !== false),
    statusInterval: String(settings.statusInterval !== false),
    statusExchange: String(settings.statusExchange !== false),
    dataPriceChange: String(settings.dataPriceChange !== false),
    dataMarketStatus: String(settings.dataMarketStatus !== false),
    dataDelayed: String(settings.dataDelayed !== false),
    dataOhlc: String(settings.dataOhlc !== false),
    dataVolume: String(settings.dataVolume !== false),
    dataGrouping: String(settings.dataGrouping || "series"),
    dataFormat: String(settings.dataFormat || "compact"),
    dataPrecision: String(settings.dataPrecision || "inherit"),
    dataBackgroundOpacity: String(settings.dataBackgroundOpacity ?? "82"),
    dataValueSource: String(settings.dataValueSource || "crosshair")
  });
  instance.overlayCanvas.setAttribute("aria-label", chartTypeAriaLabel(chartType));
  if (instance.description instanceof HTMLElement) {
    instance.description.textContent = chartTypeDescription(chartType);
  }
  instance.symbolTitle.hidden = settings.statusSymbol === false;
  instance.symbolDescription.hidden = settings.statusDescription === false;
  instance.symbolExchange.hidden = settings.statusExchange === false;
  instance.interval.hidden = settings.statusInterval === false;
  instance.priceChange.hidden = settings.dataPriceChange === false;
  instance.marketStatus.hidden = settings.dataMarketStatus === false;
  instance.delayedStatus.hidden = settings.dataDelayed === false;
  instance.ohlc.hidden = settings.dataOhlc === false;
  instance.volumeValue.hidden = settings.dataVolume === false;
  applyDataWindowBehavior(instance);
  if (instance.data?.length) {
    updateQuote(instance, instance.hoverQuoteIndex, instance.data);
  }
  updateInstanceIndicators(instance);
  instance.workspace.setThemePalettes(effectiveCustomization?.themes || null);
  instance.workspace.setBehavior({
    crosshairMode: effectiveCustomization?.chartDefaults?.crosshairMode === "off"
      ? "hidden"
      : "normal"
  });
  instance.workspace.setAppearance({
    chartType,
    backgroundStyle: settings.backgroundStyle,
    backgroundColor: settings.backgroundColor,
    backgroundColorOpacity: settings.backgroundColorOpacity,
    backgroundColorEnd: settings.backgroundColorEnd,
    backgroundColorEndOpacity: settings.backgroundColorEndOpacity,
    bodyUpColor: settings.bodyUpColor,
    bodyUpColorOpacity: settings.bodyUpColorOpacity,
    bodyDownColor: settings.bodyDownColor,
    bodyDownColorOpacity: settings.bodyDownColorOpacity,
    borderUpColor: settings.borderUpColor,
    borderUpColorOpacity: settings.borderUpColorOpacity,
    borderDownColor: settings.borderDownColor,
    borderDownColorOpacity: settings.borderDownColorOpacity,
    wickUpColor: settings.wickUpColor,
    wickUpColorOpacity: settings.wickUpColorOpacity,
    wickDownColor: settings.wickDownColor,
    wickDownColorOpacity: settings.wickDownColorOpacity,
    showBodies: settings.showBodies,
    showBorders: settings.showBorders,
    showWicks: settings.showWicks,
    barUpColor: settings.barUpColor,
    barUpColorOpacity: settings.barUpColorOpacity,
    barDownColor: settings.barDownColor,
    barDownColorOpacity: settings.barDownColorOpacity,
    lineColor: settings.lineColor,
    lineColorOpacity: settings.lineColorOpacity,
    areaLineColor: settings.areaLineColor,
    areaLineColorOpacity: settings.areaLineColorOpacity,
    areaFillColor: settings.areaFillColor,
    areaFillColorOpacity: settings.areaFillColorOpacity,
    verticalGridColor: settings.verticalGridColor,
    horizontalGridColor: settings.horizontalGridColor,
    gridLineWidth: settings.gridLineWidth,
    gridLineOpacity: settings.gridLineOpacity,
    gridLineStyle: settings.gridLineStyle,
    horizontalGrid: settings.horizontalGrid,
    verticalGrid: settings.verticalGrid,
    crosshairColor: settings.crosshairColor,
    crosshairColorOpacity: settings.crosshairColorOpacity,
    crosshairStyle: settings.crosshairStyle,
    watermarkNone: settings.watermarkNone,
    watermarkTicker: settings.watermarkTicker,
    watermarkInterval: settings.watermarkInterval,
    watermarkDescription: settings.watermarkDescription,
    watermarkOpacity: Number.parseFloat(settings.watermarkOpacity),
    watermarkTickerText: instance.symbol,
    watermarkIntervalText: instance.timeframe,
    watermarkDescriptionText: instance.symbolDescription.textContent,
    fontFamily: settings.fontFamily,
    scaleMode: settings.scaleMode,
    showPriceScale: settings.showPriceScale,
    showTimeScale: settings.showTimeScale,
    pricePrecision: resolveInstrumentPricePrecision(instance),
    topMargin: settings.topMargin,
    bottomMargin: settings.bottomMargin,
    rightSpacing: settings.rightSpacing,
    dateTimeFormat: settings.dateTimeFormat,
    timeZone: exchangeTimeZoneForInstance(instance),
    lastPriceLabel: settings.lastPriceLabel,
    lastPriceLine: settings.lastPriceLine,
    previousCloseLine: settings.previousCloseLine,
    highLowLabels: settings.highLowLabels
  });
}

function applyDataWindowBehavior(instance) {
  const settings = instance.settings;
  const grouping = ["series", "field", "pane"].includes(settings.dataGrouping)
    ? settings.dataGrouping
    : "series";
  const format = settings.dataFormat === "detailed" ? "detailed" : "compact";
  const precision = ["2", "4", "8"].includes(String(settings.dataPrecision))
    ? String(settings.dataPrecision)
    : "inherit";
  const valueSource = ["crosshair", "pinned", "latest"].includes(settings.dataValueSource)
    ? settings.dataValueSource
    : "crosshair";
  const requestedOpacity = Number.parseFloat(settings.dataBackgroundOpacity);
  const backgroundOpacity = clamp(
    Number.isFinite(requestedOpacity) ? requestedOpacity : 82,
    0,
    100
  );
  Object.assign(instance.dataWindow.dataset, {
    grouping,
    format,
    precision,
    backgroundOpacity: String(backgroundOpacity),
    valueSource
  });
  applyDataWindowSurfaceStyle(instance, instance.dataWindow, backgroundOpacity);
  for (const readout of instance.paneIndicatorReadouts.values()) {
    applyDataWindowSurfaceStyle(instance, readout, backgroundOpacity);
  }
  [instance.ohlc, instance.volumeValue, instance.priceChange].forEach((element, index) => {
    element.style.order = String((index + 1) * 10);
  });
  instance.marketStatus.style.order = "40";
  instance.delayedStatus.style.order = "50";
  if (valueSource === "pinned" && instance.data?.length && instance.pinnedQuoteIndex === null) {
    instance.pinnedQuoteIndex = instance.hoverQuoteIndex ?? instance.data.length - 1;
  }
}

function applyDataWindowSurfaceStyle(instance, element, opacity = null) {
  const requestedOpacity = Number.parseFloat(
    opacity ?? instance.settings.dataBackgroundOpacity
  );
  const backgroundOpacity = clamp(
    Number.isFinite(requestedOpacity) ? requestedOpacity : 82,
    0,
    100
  );
  const background = String(instance.settings.dataBackground || "").trim();
  if (background) {
    element.style.setProperty("--chart-data-window-background", background);
  } else {
    element.style.removeProperty("--chart-data-window-background");
  }
  element.style.setProperty(
    "--chart-data-window-opacity",
    `${backgroundOpacity}%`
  );
}

function pinDataWindowValue(instance) {
  if (instance.settings.dataValueSource !== "pinned" || !instance.data?.length) return;
  instance.pinnedQuoteIndex = instance.hoverQuoteIndex ?? instance.data.length - 1;
  updateQuote(instance, instance.pinnedQuoteIndex, instance.data);
  announce(`Data window pinned to bar ${instance.pinnedQuoteIndex + 1}.`);
}

function syncChartStyleSections(chartType) {
  for (const section of chartStyleSections) {
    section.hidden = section.dataset.chartStyleSection !== chartType;
  }
}

function normalizeChartType(value) {
  return ["candles", "bars", "line", "area"].includes(value) ? value : "candles";
}

function chartTypeAriaLabel(chartType) {
  const names = {
    candles: "Interactive candlestick chart",
    bars: "Interactive OHLC bar chart",
    line: "Interactive line chart",
    area: "Interactive area chart"
  };
  return names[chartType];
}

function chartTypeDescription(chartType) {
  const names = {
    candles: "Candlestick price chart",
    bars: "OHLC bar chart",
    line: "Line price chart",
    area: "Area price chart"
  };
  return `${names[chartType]} with optional volume, SMA 20, and EMA 50. `
    + "Use the mouse wheel or plus and minus keys to zoom. Drag or use the left and right "
    + "arrow keys to pan one bar; hold Control with an arrow key to pan 20 percent of the "
    + "visible bars. Press Home to move the latest bar to the right edge without changing zoom.";
}

function resetActiveSettingsTab() {
  if (!settingsTarget) return;
  const activeTab = settingsTabs.find((tab) => tab.getAttribute("aria-selected") === "true");
  if (!activeTab) return;
  if (activeTab.dataset.settingsTab === "workspace") {
    updateSnapWindowsPreference(true);
    announce("Workspace settings reset.");
    return;
  }
  const panel = settingsPanels.find(
    (candidate) => candidate.dataset.settingsPanel === activeTab.dataset.settingsTab
  );
  if (!panel) return;
  for (const control of panel.querySelectorAll("[data-chart-setting]")) {
    const name = control.dataset.chartSetting;
    settingsTarget.settings[name] = defaultChartSettings[name];
    if (control instanceof HTMLInputElement && control.type === "color") {
      const opacitySetting = colorOpacitySetting(control);
      settingsTarget.settings[opacitySetting] = defaultChartSettings[opacitySetting];
      control.dataset.chartOpacity = String(defaultChartSettings[opacitySetting]);
    }
    writeSettingsControl(control, defaultChartSettings[name]);
  }
  syncBackgroundSurfaceControls();
  syncMarketHoursControls();
  fitSettingsWindowToContent();
  applyInstanceSettings(settingsTarget);
  announce(`${activeTab.textContent.trim()} settings reset.`);
}

function selectSettingsTab(tab, focus = true) {
  if (!(tab instanceof HTMLElement)) return;
  const name = tab.dataset.settingsTab;
  for (const candidate of settingsTabs) {
    const selected = candidate === tab;
    candidate.setAttribute("aria-selected", String(selected));
    candidate.tabIndex = selected ? 0 : -1;
  }
  for (const panel of settingsPanels) {
    panel.hidden = panel.dataset.settingsPanel !== name;
  }
  fitSettingsWindowToContent();
  if (focus) tab.focus();
}

function handleSettingsTabKeydown(event) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const current = settingsTabs.indexOf(document.activeElement);
  const next = event.key === "Home"
    ? 0
    : event.key === "End"
      ? settingsTabs.length - 1
      : event.key === "ArrowDown"
        ? (current + 1 + settingsTabs.length) % settingsTabs.length
        : (current - 1 + settingsTabs.length) % settingsTabs.length;
  selectSettingsTab(settingsTabs[next]);
}

function timeframeDescription(timeframe) {
  return {
    "1m": "1 minute",
    "5m": "5 minutes",
    "15m": "15 minutes",
    "1h": "1 hour",
    "1D": "1 day"
  }[timeframe] || String(timeframe).replace(/^(\d+)(m|h|D|W|M)$/, (_, amount, unit) =>
    `${amount} ${{ m: 'minute', h: 'hour', D: 'day', W: 'week', M: 'month' }[unit]}${Number(amount) === 1 ? '' : 's'}`);
}

function runViewportAction(action) {
  if (!activeChart) return;
  if (action === "focus") {
    activeChart.overlayCanvas.focus();
    announce("Chart crosshair focused.");
  }
  if (action === "zoom-in") activeChart.workspace.zoomAtCenter(0.8);
  if (action === "zoom-out") activeChart.workspace.zoomAtCenter(1.25);
  if (action === "pan-left") activeChart.workspace.panBars(-12);
  if (action === "pan-right") activeChart.workspace.panBars(12);
}

function openGoToDate(instance = activeChart, returnFocus = null) {
  if (effectiveCustomization && !featureIsVisible(effectiveCustomization, "goToDate")) {
    return;
  }
  if (!goToDateDialog) {
    announce("Go To Date is unavailable.");
    return false;
  }
  if (!instance || instance.destroyed) return false;
  setActiveChart(instance);
  closeLineToolsMenu();
  closeOtherTimeframeMenus();
  closeOtherContextMenus();
  closeSettingsWindow();
  if (!indicatorManagerElement.hidden) indicatorManager.close({ restoreFocus: false });
  const timeZone = exchangeTimeZoneForInstance(instance);
  const today = todayDateOnly(timeZone);
  const initialDate = initialDateForViewport(
    instance.data?.timestamps,
    instance.workspace.viewport,
    timeZone,
    today
  );
  goToDateDialog.show({
    initialDate,
    today,
    target: instance,
    returnFocus: returnFocus || instance.overlayCanvas,
    timeZone,
    chartCount: chartInstances.filter((candidate) => !candidate.destroyed).length
  });
  return true;
}

function cancelGoToDateNavigation({ target, scope } = {}) {
  goToDateRunToken += 1;
  const targets = scope === "all"
    ? chartInstances
    : [target];
  for (const instance of targets) {
    if (!instance) continue;
    instance.goToDateToken += 1;
    instance.goToDateController?.abort();
  }
}

async function navigateChartToDate({ date, target, timeZone, scope = "this" }) {
  const selectedDate = parseDateOnly(date);
  if (!selectedDate) throw new Error("Enter a valid date in YYYY-MM-DD format.");
  if (!target || target.destroyed || !chartInstances.includes(target)) {
    throw new Error("The target chart is no longer available.");
  }

  const targets = scope === "all"
    ? chartInstances.filter((instance) => !instance.destroyed)
    : [target];
  const runToken = ++goToDateRunToken;
  setActiveChart(target);
  let prepared = null;

  for (let attempt = 0; attempt < MAX_GO_TO_DATE_ATTEMPTS; attempt += 1) {
    assertGoToDateRun(runToken);
    const operations = targets.map((instance) => ({
      instance,
      token: ++instance.goToDateToken
    }));
    const settled = await Promise.allSettled(operations.map(({ instance, token }) =>
      prepareChartDateNavigation(
        instance,
        selectedDate,
        instance === target ? timeZone : exchangeTimeZoneForInstance(instance),
        token
      )
    ));
    assertGoToDateRun(runToken);

    const failedIndex = settled.findIndex(
      (result) => result.status === "rejected" && result.reason?.name !== "AbortError"
    );
    if (failedIndex >= 0) {
      throwGoToDateFailure(scope, targets[failedIndex], settled[failedIndex].reason);
    }

    const interrupted = settled.some((result) => result.status === "rejected")
      || operations.some(({ instance, token }) => instance.goToDateToken !== token);
    if (interrupted) {
      if (attempt + 1 < MAX_GO_TO_DATE_ATTEMPTS) continue;
      throw new DOMException("Navigation was interrupted.", "AbortError");
    }

    try {
      prepared = settled
        .map((result) => result.value)
        .map(refreshChartDateNavigation);
      break;
    } catch (error) {
      if (error?.name === "AbortError" && attempt + 1 < MAX_GO_TO_DATE_ATTEMPTS) continue;
      throw error;
    }
  }

  if (!prepared) throw new DOMException("Navigation was interrupted.", "AbortError");
  for (const navigation of prepared) applyChartDateNavigation(navigation);
  setActiveChart(target);
  target.workspace.publishPerformance();

  if (scope === "all" && prepared.length > 1) {
    const adjustedCount = prepared.filter(({ resolution }) => !resolution.exact).length;
    announce(
      `${prepared.length} charts centered on ${shortDateLabel(selectedDate)}.`
      + (adjustedCount ? ` ${adjustedCount} used the nearest available session.` : "")
    );
    return prepared.map(({ resolution }) => resolution);
  }

  const [{ instance, resolution }] = prepared;
  if (resolution.resolvedDate !== resolution.requestedDate) {
    const resolvedDate = parseDateOnly(resolution.resolvedDate);
    announce(
      `No data for ${shortDateLabel(selectedDate)}. Showing ${shortDateLabel(resolvedDate)}.`
    );
  } else {
    announce(`${instance.symbol} centered on ${shortDateLabel(selectedDate)}.`);
  }
  return resolution;
}

async function prepareChartDateNavigation(instance, selectedDate, timeZone, token) {
  assertGoToDateTarget(instance, token);
  if (instance.loadPromise) {
    await instance.loadPromise;
    assertGoToDateTarget(instance, token);
  }
  const today = todayDateOnly(timeZone);
  let resolution = resolveBarForDate(
    instance.data?.timestamps,
    selectedDate,
    instance.timeframe,
    timeZone
  );
  const historicalDate = compareDateOnly(selectedDate, today) <= 0;
  if (
    historicalDate
    && (!instance.data?.length || !isDateInRanges(selectedDate, instance.loadedDateRanges))
  ) {
    await loadHistoryAroundDate(instance, selectedDate, timeZone);
    assertGoToDateTarget(instance, token);
    resolution = resolveBarForDate(
      instance.data?.timestamps,
      selectedDate,
      instance.timeframe,
      timeZone
    );
  }

  validateChartDateResolution(instance, selectedDate, timeZone, resolution);
  return { instance, resolution, selectedDate, timeZone, token };
}

function refreshChartDateNavigation({ instance, selectedDate, timeZone, token }) {
  assertGoToDateTarget(instance, token);
  const resolution = resolveBarForDate(
    instance.data?.timestamps,
    selectedDate,
    instance.timeframe,
    timeZone
  );
  validateChartDateResolution(instance, selectedDate, timeZone, resolution);
  return { instance, resolution, selectedDate, timeZone, token };
}

function validateChartDateResolution(instance, selectedDate, timeZone, resolution) {
  if (!resolution) {
    throw new Error(`No market data is available near ${shortDateLabel(selectedDate)}.`);
  }
  const today = todayDateOnly(timeZone);
  const historicalDate = compareDateOnly(selectedDate, today) <= 0;
  const resolvedDate = parseDateOnly(resolution.resolvedDate);
  const maximumGap = maximumExpectedDateGap(instance.timeframe);
  if (
    !resolution.exact
    && historicalDate
    && resolvedDate
    && Math.abs(dateOnlyDistance(resolvedDate, selectedDate)) > maximumGap
  ) {
    throw new Error(`No market data is available near ${shortDateLabel(selectedDate)}.`);
  }
}

function applyChartDateNavigation({ instance, resolution, selectedDate, token }) {
  assertGoToDateTarget(instance, token);
  if (!instance.workspace.centerOnBar(resolution.index)) {
    throw new Error("The chart viewport could not be repositioned.");
  }
  instance.workspace.publishPerformance();
  if (resolution.resolvedDate !== resolution.requestedDate) {
    const resolvedDate = parseDateOnly(resolution.resolvedDate);
    const message = `No data for ${shortDateLabel(selectedDate)}. Showing ${shortDateLabel(resolvedDate)}.`;
    showNavigationToast(instance, message);
    instance.liveRegion.textContent = message;
  } else {
    instance.liveRegion.textContent = `${instance.symbol} centered on ${shortDateLabel(selectedDate)}.`;
  }
}

function assertGoToDateTarget(instance, token) {
  if (!instance || instance.destroyed || !chartInstances.includes(instance)) {
    throw new Error("The target chart is no longer available.");
  }
  if (instance.goToDateToken !== token) {
    throw new DOMException("Navigation was aborted.", "AbortError");
  }
}

function assertGoToDateRun(token) {
  if (goToDateRunToken !== token) {
    throw new DOMException("Navigation was aborted.", "AbortError");
  }
}

function throwGoToDateFailure(scope, instance, reason) {
  const message = reason?.message || "That date could not be loaded.";
  throw new Error(
    scope === "all"
      ? `Could not navigate all charts. ${instance.symbol}: ${message}`
      : message
  );
}

async function loadHistoryAroundDate(instance, selectedDate, timeZone) {
  if (instance.goToDatePromise) return instance.goToDatePromise;
  const controller = new AbortController();
  instance.goToDateController = controller;
  const requestedValue = formatDateOnly(selectedDate);
  const endDate = formatDateOnly(addDateOnlyDays(selectedDate, 14));
  instance.liveRegion.textContent = `Loading ${instance.symbol} history near ${requestedValue}.`;

  instance.goToDatePromise = (async () => {
    try {
      const result = await loadMarketData(
        instance.symbolId,
        instance.instrumentId,
        instance.symbol,
        instance.timeframe,
        controller.signal,
        { endDate }
      );
      if (instance.destroyed || controller.signal.aborted) {
        throw new DOMException("Navigation was aborted.", "AbortError");
      }
      if (!result.data?.length) {
        throw new Error(`No market data is available near ${shortDateLabel(selectedDate)}.`);
      }

      const fetchedRange = dateRangeForTimestamps(result.data.timestamps, timeZone);
      if (!fetchedRange) {
        throw new Error(`No market data is available near ${shortDateLabel(selectedDate)}.`);
      }
      const fetchedStart = parseDateOnly(fetchedRange.start);
      if (
        fetchedStart
        && compareDateOnly(selectedDate, fetchedStart) < 0
        && dateOnlyDistance(fetchedStart, selectedDate) > maximumExpectedDateGap(instance.timeframe)
      ) {
        throw new Error(`No market data is available near ${shortDateLabel(selectedDate)}.`);
      }

      mergeChartHistory(instance, result);
      return instance.data;
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      if (error?.name === "MarketDataRequestError" && !error.customerMessage && error.status !== 401 && error.status !== 403) {
        throw new Error(
          error.status === 404
            ? `No market data is available near ${shortDateLabel(selectedDate)}.`
            : "Historical data could not be loaded. Try again."
        );
      }
      throw error;
    } finally {
      if (!instance.destroyed && !controller.signal.aborted && !instance.loadController && instance.data?.length) {
        showInstanceState(instance, null);
      }
      if (instance.goToDateController === controller) instance.goToDateController = null;
      instance.goToDatePromise = null;
    }
  })();
  return instance.goToDatePromise;
}

function captureViewportAnchor(instance) {
  const timestamps = instance?.data?.timestamps;
  const viewport = instance?.workspace?.viewport;
  if (!timestamps?.length || !viewport || viewport.visibleCount <= 0) return null;
  const centerIndex = Math.min(
    timestamps.length - 1,
    Math.max(0, Math.round(viewport.start + viewport.visibleCount / 2 - 0.5))
  );
  return {
    timestamp: timestamps[centerIndex],
    visibleCount: viewport.visibleCount
  };
}

function restoreViewportAnchor(instance, anchor) {
  if (!anchor || !instance?.data?.timestamps?.length) return false;
  const index = instance.data.timestamps.indexOf(anchor.timestamp);
  if (index < 0) return false;
  instance.workspace.viewport.visibleCount = Math.min(
    anchor.visibleCount,
    instance.data.timestamps.length
  );
  return instance.workspace.centerOnBar(index);
}

function addLoadedDateRange(instance, range) {
  const ranges = [...(instance.loadedDateRanges || []), range]
    .filter((candidate) => candidate?.start && candidate?.end)
    .sort((left, right) => left.start.localeCompare(right.start));
  const merged = [];
  for (const candidate of ranges) {
    const previous = merged.at(-1);
    if (!previous || candidate.start > previous.end) {
      merged.push({ ...candidate });
    } else if (candidate.end > previous.end) {
      previous.end = candidate.end;
    }
  }
  instance.loadedDateRanges = merged;
}

function maximumExpectedDateGap(timeframe) {
  const value = String(timeframe || "").trim();
  if (/^\d+Y$/.test(value)) return 370;
  if (/^\d+M$/.test(value)) return 45;
  return 14;
}

function exchangeTimeZoneForInstance(instance) {
  const instrument = instrumentForInstance(instance);
  const supplied = String(instrument?.timeZoneId || instrument?.timeZone || "").trim();
  if (supplied) return supplied;
  const exchange = String(
    instrument?.exchangeCode || instrument?.exchangeDisplayName || ""
  ).trim().toUpperCase();
  if (instrument?.securityType === "Crypto" || instrument?.securityType === "Forex") return "UTC";
  if (/CME|CBOT|XCBT|NYMEX|COMEX/.test(exchange)) return "America/Chicago";
  if (/LSE|XLON/.test(exchange)) return "Europe/London";
  if (/NSE|BSE|XNSE|XBOM/.test(exchange)) return "Asia/Kolkata";
  if (/JPX|TSE|XTKS/.test(exchange)) return "Asia/Tokyo";
  if (/ASX|XASX/.test(exchange)) return "Australia/Sydney";
  return "America/New_York";
}

function shortDateLabel(date) {
  return formatDateOnlyLabel(date).replace(/^[^,]+,\s*/, "");
}

function showNavigationToast(instance, message) {
  clearTimeout(instance.navigationToastTimer);
  let toast = instance.stage.querySelector("[data-chart-navigation-toast]");
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement("div");
    toast.className = "chart-navigation-toast";
    toast.dataset.chartNavigationToast = "";
    toast.setAttribute("role", "status");
    instance.stage.append(toast);
  }
  toast.textContent = message;
  toast.hidden = false;
  instance.navigationToastTimer = setTimeout(() => {
    toast.hidden = true;
  }, 4_500);
}

async function toggleFullscreen() {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await root.requestFullscreen();
    }
  } catch {
    announce("Fullscreen is not available in this embed.");
  }
}

function updateFullscreenButton() {
  const active = document.fullscreenElement === root;
  fullscreenToggle.setAttribute("aria-pressed", String(active));
  fullscreenToggle.setAttribute("aria-label", active ? "Exit fullscreen" : "Enter fullscreen");
  fullscreenToggle.setAttribute("title", active ? "Exit fullscreen" : "Fullscreen");
}

function updateSymbolSummaries() {
  chartInstances.forEach(updateSymbolSummary);
}

function updateSymbolSummary(instance) {
  const instrument = instrumentForInstance(instance);
  const symbol = instrument?.code || instance.symbol || defaultSymbol;
  instance.symbolTitle.textContent = symbol;
  instance.symbolDescription.textContent = instrument?.displayName || "Market data";
  instance.symbolExchange.textContent =
    instrument?.exchangeDisplayName || instrument?.exchangeCode || "Market data";
  updateInstanceWindowControls(instance);
  workspaceObjectRegistry.notify();
}

function assignInstrument(instance, instrument) {
  const nextProviderSymbol = String(instrument.providerSymbol || instrument.code || "")
    .trim()
    .toUpperCase();
  const nextRealtimeIdentity = `${instrument.instrumentId}|${nextProviderSymbol}|${instance.timeframe}`;
  if (
    instance.realtimeSubscriptionIdentity
    && instance.realtimeSubscriptionIdentity !== nextRealtimeIdentity
  ) {
    releaseChartRealtime(instance);
  }
  instance.symbolId = instrument.symbolId;
  instance.instrumentId = instrument.instrumentId;
  instance.symbol = instrument.code;
  alertWorkspace?.syncChart(instance);
  workspaceObjectRegistry.notify();
}

function registerInstrument(instrument) {
  if (!instrument?.instrumentId || !instrument?.code) return;
  symbolCatalogById.set(instrument.instrumentId, instrument);
  const candidates = symbolCatalogByCode.get(instrument.code) || [];
  if (!candidates.some((candidate) => candidate.instrumentId === instrument.instrumentId)) {
    candidates.unshift(instrument);
    symbolCatalogByCode.set(instrument.code, candidates);
  }
}

function instrumentForInstance(instance) {
  return symbolCatalogById.get(instance?.instrumentId)
    || instrumentForCode(instance?.symbol)
    || null;
}

function instrumentForCode(symbol) {
  const normalized = String(symbol || "").trim().toUpperCase();
  return symbolCatalogByCode.get(normalized)?.[0] || null;
}

function instrumentForWorkspace() {
  return symbolCatalogById.get(workspaceInstrumentId)
    || instrumentForCode(workspaceSymbol)
    || null;
}

function setWorkspaceInstrument(instrument) {
  if (!instrument?.instrumentId || !instrument?.code) return;
  workspaceInstrumentId = instrument.instrumentId;
  workspaceSymbol = instrument.code;
  syncToolbarSymbol();
}

function restoreInstrumentAfterFailedLoad(instance, rollback) {
  if (!rollback?.symbol) return;
  instance.symbolId = rollback.symbolId || null;
  instance.instrumentId = rollback.instrumentId;
  instance.symbol = rollback.symbol;
  instance.workspace.setSymbolAndTimeframe(instance.symbol, instance.timeframe);
  updateSymbolSummary(instance);
  if (activeChart === instance) syncToolbarSymbol(instance);
}

function syncToolbarSymbol() {
  const instrument = instrumentForWorkspace();
  const symbol = instrument?.code || workspaceSymbol || defaultSymbol;
  symbolSelect.value = symbol;
  symbolToolbarCode.textContent = symbol;
  symbolToolbarExchange.textContent =
    instrument?.exchangeDisplayName || instrument?.exchangeCode || "Market data";
  symbolSearchTrigger.setAttribute(
    "aria-label",
    `Search and change the workspace symbol, currently ${symbol}, ${instrument?.displayName || "Market data"}`
  );
  symbolSearchTrigger.title = `Search workspace symbols · ${symbol}`;
}

function attachChartRealtime(instance) {
  if (!instance?.data?.length || instance.destroyed) return;
  if (!realtimeManager) {
    setChartRealtimeStatus(instance, "Offline", "Live market data is not configured.");
    return;
  }
  const instrument = instrumentForInstance(instance);
  const internalSymbolId = instance.instrumentId || instance.symbolId;
  const providerSymbol = String(instrument?.providerSymbol || instance.symbol || "")
    .trim()
    .toUpperCase();
  if (!internalSymbolId || !providerSymbol) {
    setChartRealtimeStatus(instance, "Offline", "Live symbol mapping is unavailable.");
    return;
  }
  const identity = `${internalSymbolId}|${providerSymbol}|${instance.timeframe}`;
  if (instance.realtimeSubscriptionIdentity === identity && instance.realtimeReleases.length > 0) {
    return;
  }
  releaseChartRealtime(instance);
  instance.realtimeSubscriptionIdentity = identity;
  const type = "PRICEDATA";
  const handle = realtimeManager.subscribe({
    internalSymbolId,
    providerSymbol,
    type,
    conflation: null,
    skipHeavyInitialLoad: true,
    exchange: instrument?.exchangeCode,
    expectedAvailability: instrument?.dataAvailability
  }, event => handleChartRealtimeEvent(instance, type, event));
  instance.realtimeReleases.push(handle.release);
  setChartRealtimeStatus(instance, "Offline", "Connecting to live market data.");
}

function releaseChartRealtime(instance) {
  if (!instance) return;
  for (const release of instance.realtimeReleases || []) release();
  instance.realtimeReleases = [];
  instance.realtimeSubscriptionIdentity = null;
  instance.realtimeProviderStatuses?.clear();
  instance.realtimeProviderMetadata?.clear();
  instance.realtimeMessageTypes?.clear();
  instance.realtimeQuote = null;
  instance.realtimeAppliedAccumulatedVolume = null;
  instance.transientData = null;
  instance.pendingLivePriceData = null;
  if (instance.realtimeFrame !== null) cancelAnimationFrame(instance.realtimeFrame);
  instance.realtimeFrame = null;
}

function handleChartRealtimeEvent(instance, type, event) {
  if (instance.destroyed) return;
  if (event.kind === "subscription-status") {
    instance.realtimeProviderStatuses.set(type, event.status);
    instance.realtimeProviderMetadata.set(type, event.metadata);
    refreshChartRealtimeStatus(instance, event.metadata);
    return;
  }
  instance.realtimeMessageTypes.add(event.type);
  const metrics = realtimeManager.snapshot().metrics;
  instance.realtimeStatus.dataset.realtimeMessageCount = String(metrics.messages);
  instance.realtimeStatus.dataset.realtimeMessageTypes =
    [...instance.realtimeMessageTypes].sort().join(",");
  instance.realtimeStatus.dataset.realtimeLastMessageType = event.type;
  if (event.type === "PRICEDATA") {
    instance.realtimeQuote = Object.freeze({
      ...(instance.realtimeQuote || {}),
      last: event.price ?? instance.realtimeQuote?.last ?? null,
      change: event.change ?? instance.realtimeQuote?.change ?? null,
      percentChange: event.percentChange ?? instance.realtimeQuote?.percentChange ?? null,
      bid: event.bid ?? instance.realtimeQuote?.bid ?? null,
      ask: event.ask ?? instance.realtimeQuote?.ask ?? null,
      receivedAt: event.receiptTimestampMs
    });
  }
  if (event.type !== "PRICEDATA") return;
  instance.pendingLivePriceData = event;
  if (instance.realtimeFrame !== null) return;
  instance.realtimeFrame = requestAnimationFrame(() => {
    instance.realtimeFrame = null;
    applyPendingChartPriceData(instance);
  });
}

function applyPendingChartPriceData(instance) {
  const message = instance.pendingLivePriceData;
  instance.pendingLivePriceData = null;
  if (!message || instance.destroyed || !instance.data?.length) return;
  if (!instance.transientData) instance.transientData = cloneChartData(instance.data);
  const accumulatedVolume = message.volume === null || message.volume === undefined
    ? null
    : Number(message.volume);
  const previousAccumulatedVolume = instance.realtimeAppliedAccumulatedVolume === null ||
      instance.realtimeAppliedAccumulatedVolume === undefined
    ? null
    : Number(instance.realtimeAppliedAccumulatedVolume);
  const volumeDelta = Number.isFinite(accumulatedVolume) &&
      Number.isFinite(previousAccumulatedVolume) &&
      accumulatedVolume >= previousAccumulatedVolume
    ? accumulatedVolume - previousAccumulatedVolume
    : message.size;
  const result = applyPriceDataToCurrentBar(instance.transientData, {
    ...message,
    size: volumeDelta
  }, {
    timeframe: instance.timeframe,
    timeZone: exchangeTimeZoneForInstance(instance),
    sessionMode: instance.settings.sessionType || "regular"
  });
  if (result !== CurrentBarUpdateResult.IGNORED && Number.isFinite(accumulatedVolume)) {
    instance.realtimeAppliedAccumulatedVolume = accumulatedVolume;
  }
  instance.realtimeOverlayCounts[result] =
    (instance.realtimeOverlayCounts[result] || 0) + 1;
  instance.realtimeStatus.dataset.realtimeOverlayResult = result;
  instance.realtimeStatus.dataset.realtimeOverlayAppliedCount =
    String(instance.realtimeOverlayCounts.applied);
  instance.realtimeStatus.dataset.realtimeOverlayIgnoredCount =
    String(instance.realtimeOverlayCounts.ignored);
  instance.realtimeStatus.dataset.realtimeOverlayReconcileCount =
    String(instance.realtimeOverlayCounts.reconcile);
  instance.realtimeStatus.dataset.realtimeLastPriceTimestamp =
    message.eventTimestampUtc || message.receiptTimestampUtc || "";
  const lastBarTimestamp = Number(instance.transientData.timestamps.at(-1));
  instance.realtimeStatus.dataset.realtimeCurrentBarTimestamp =
    Number.isFinite(lastBarTimestamp) ? new Date(lastBarTimestamp).toISOString() : "";
  if (result === CurrentBarUpdateResult.RECONCILE) {
    instance.transientData = null;
    void reconcileChartHistory(instance, "completed-bar").catch(() => {});
    return;
  }
  if (result !== CurrentBarUpdateResult.APPLIED) return;
  instance.workspace.replaceTransientCurrentBar(instance.transientData);
  const transientIndicators = calculateIndicatorsForData(
    instance.indicatorInstances,
    instance.transientData
  );
  instance.workspace.setIndicatorInstances(transientIndicators, { stylesOnly: true });
  updateQuote(instance, null, instance.transientData);
  refreshChartRealtimeStatus(instance);
}

function calculateIndicatorsForData(instances, data) {
  return instances.map(indicator => {
    const definition = getIndicatorDefinition(indicator.definitionId);
    if (!definition) return indicator;
    try {
      return { ...indicator, outputs: calculateIndicator(definition, indicator, data), error: null };
    } catch {
      return indicator;
    }
  });
}

function handleRealtimeManagerEvent(event) {
  if (event.kind === "gap-suspected") {
    for (const instance of chartInstances) {
      const providerSymbol = instrumentForInstance(instance)?.providerSymbol || instance.symbol;
      if (String(providerSymbol).toUpperCase() === String(event.message?.providerSymbol).toUpperCase()) {
        void reconcileChartHistory(instance, "gap-suspected").catch(() => {});
      }
    }
    return;
  }
  if (event.kind === "provider-event" || event.kind === "diagnostic") {
    const metrics = realtimeManager.snapshot().metrics;
    for (const instance of chartInstances) {
      instance.realtimeStatus.dataset.realtimeUnknownMessageCount = String(metrics.unknown);
      if (event.kind === "provider-event") {
        instance.realtimeStatus.dataset.realtimeLastProviderEvent = event.event?.kind || "unknown";
        const code = event.event?.detail?.code;
        if (Number.isFinite(code)) instance.realtimeStatus.dataset.realtimeLastProviderCode = String(code);
        else delete instance.realtimeStatus.dataset.realtimeLastProviderCode;
        if (event.event?.kind === "close") {
          if (Number.isFinite(code)) instance.realtimeStatus.dataset.realtimeLastCloseCode = String(code);
          else delete instance.realtimeStatus.dataset.realtimeLastCloseCode;
        }
      }
    }
    return;
  }
  if (event.kind !== "connection-state") return;
  const desiredSubscriptions = event.desiredSubscriptions || [];
  root.dataset.realtimeDesiredSubscriptionCount = String(desiredSubscriptions.length);
  root.dataset.realtimeActiveSubscriptionCount = String(
    event.activeSubscriptions?.length || 0
  );
  root.dataset.realtimeMaximumReferenceCount = String(Math.max(
    0,
    ...desiredSubscriptions.map(item => Number(item.referenceCount) || 0)
  ));
  root.dataset.realtimeSubscriptionSummary = desiredSubscriptions
    .map(item => [
      item.request?.providerSymbol || "unknown",
      item.request?.type || "unknown",
      item.referenceCount || 0
    ].join(":"))
    .sort()
    .join(",");
  for (const instance of chartInstances) refreshChartRealtimeStatus(instance, null, event);
  watchlistManager.setRealtimeConnectionState?.(event);
}

function refreshChartRealtimeStatus(instance, metadata = null, managerEvent = null) {
  const managerSnapshot = realtimeManager?.snapshot();
  const connectionState = managerEvent?.state
    || managerSnapshot?.state
    || RealtimeConnectionState.CLOSED;
  const connectionReason = managerEvent?.reason || managerSnapshot?.reason || null;
  if (connectionState === RealtimeConnectionState.RECONNECTING) {
    setChartRealtimeStatus(
      instance,
      "Reconnecting",
      "Live data is reconnecting and will be reconciled with history.",
      connectionReason
    );
    return;
  }
  if ([RealtimeConnectionState.DEGRADED, RealtimeConnectionState.DISCONNECTED,
    RealtimeConnectionState.UNAUTHORIZED, RealtimeConnectionState.CLOSED].includes(connectionState)) {
    const detail = connectionState === RealtimeConnectionState.UNAUTHORIZED
      ? "Live market-data access is unavailable for this account."
      : connectionState === RealtimeConnectionState.DEGRADED
        ? connectionReason === "live-session-active-in-another-tab"
          ? "Live market data is active in another tab. Close that tab and refresh this workspace."
          : connectionReason === "connection-limit"
          ? "The provider connection limit was reached. Close another active workspace and retry."
          : "Live market data is offline after repeated connection attempts. Historical data remains available."
        : "Live market data is offline. Historical data remains available.";
    setChartRealtimeStatus(instance, "Offline", detail, connectionReason);
    return;
  }
  const statuses = [...instance.realtimeProviderStatuses.values()];
  if (statuses.includes("unentitled")) {
    setChartRealtimeStatus(instance, "Not entitled", "This market-data product is not included in the account.");
    return;
  }
  if (statuses.includes("invalid-symbol")) {
    setChartRealtimeStatus(instance, "Offline", "The live provider does not recognize this symbol mapping.");
    return;
  }
  if (statuses.includes("rejected")) {
    setChartRealtimeStatus(instance, "Offline", metadata?.reason === "connection-limit"
      ? "The provider connection limit was reached. Close another active workspace and retry."
      : "The live provider rejected this subscription.");
    return;
  }
  if (connectionState === RealtimeConnectionState.CONNECTED && statuses.length === 1 &&
      statuses.every(status => status === "subscribed")) {
    const providerAvailability = [...instance.realtimeProviderMetadata.values()]
      .map(value => availabilityFromProviderEntitlement(value?.entitlement))
      .filter(Boolean);
    const delayed = providerAvailability.includes("delayed");
    const live = providerAvailability.includes("live");
    if (!delayed && !live) {
      setChartRealtimeStatus(
        instance,
        "Offline",
        "The provider confirmed the subscription but did not classify its data availability."
      );
      return;
    }
    setChartRealtimeStatus(
      instance,
      delayed ? "Delayed" : "Live",
      delayed
        ? "Provider-confirmed delayed market data."
        : "Provider-confirmed live price-data subscription."
    );
    return;
  }
  setChartRealtimeStatus(instance, "Offline", "Live market data is connecting.");
}

function setChartRealtimeStatus(instance, label, detail, diagnosticReason = null) {
  if (!instance?.realtimeStatus) return;
  const state = label.toLowerCase().replaceAll(" ", "-");
  instance.realtimeStatus.textContent = label === "Live" ? "" : label;
  instance.realtimeStatus.dataset.statusState = state;
  if (diagnosticReason) instance.realtimeStatus.dataset.statusReason = diagnosticReason;
  else delete instance.realtimeStatus.dataset.statusReason;
  instance.realtimeStatus.title = detail;
  instance.realtimeStatus.setAttribute("aria-label", `${label}. ${detail}`);
  setStatusIcon(
    instance.delayedStatus,
    label === "Live" ? "live" : label === "Delayed" ? "delayed" : "unknown",
    label === "Live" ? "Real-time data" : label === "Delayed" ? "Delayed data" : `${label}. ${detail}`
  );
}

async function reconcileRealtimeWorkspace() {
  await Promise.all(chartInstances
    .filter(instance => !instance.destroyed && instance.realtimeReleases.length > 0)
    .map(instance => reconcileChartHistory(instance, "reconnect")));
  await watchlistManager.reconcileRealtime?.();
}

function reconcileChartHistory(instance, reason) {
  if (!instance || instance.destroyed) return Promise.resolve();
  const identity = historyIdentity(instance);
  return historyWindowFor(instance).refresh({ force: reason === "reconnect" })
    .then(() => {
      if (!instance.destroyed && identity === historyIdentity(instance)) refreshChartRealtimeStatus(instance);
    })
    .catch(error => {
      if (error?.name !== "AbortError") {
        setChartRealtimeStatus(instance, "Offline", "Historical reconciliation failed. Retry the chart load.");
        throw error;
      }
    });
}

function updateQuote(instance, index, data) {
  if (!data?.length) return;
  const target = resolveQuoteIndex(instance, index, data.length);
  const detailed = instance.settings.dataFormat === "detailed";
  const precision = resolveDataPrecision(instance);
  const close = Number(data.closes[target]);
  const previousClose = target > 0 ? Number(data.closes[target - 1]) : close;
  const values = detailed
    ? [
        ["Open", data.opens[target]],
        ["High", data.highs[target]],
        ["Low", data.lows[target]],
        ["Close", close]
      ]
    : [
        ["O", data.opens[target]],
        ["H", data.highs[target]],
        ["L", data.lows[target]],
        ["C", close]
      ];
  instance.ohlc.replaceChildren(...values.map(([label, value]) => {
    const span = document.createElement("span");
    const prefix = document.createElement("b");
    prefix.textContent = `${label} `;
    span.append(prefix, document.createTextNode(formatDataNumber(value, precision)));
    return span;
  }));
  updatePriceChange(instance, close, previousClose, precision, detailed);
  updateMarketStatusIcon(instance.marketStatus, data.timestamps[target]);
  updateDelayStatusIcon(
    instance.delayedStatus,
    data.timestamps[target],
    instrumentForInstance(instance)?.dataAvailability
  );
  if (instance.realtimeReleases?.length) refreshChartRealtimeStatus(instance);
  instance.volumeValue.textContent = formatVolumeValue(data.volumes[target], detailed);
  updateInstanceIndicators(instance, target);
  instance.dataWindow.dataset.quoteIndex = String(target);
}

function resolveQuoteIndex(instance, hoverIndex, dataLength) {
  const latest = dataLength - 1;
  const source = instance.settings.dataValueSource || "crosshair";
  const requested = source === "latest"
    ? latest
    : source === "pinned"
      ? instance.pinnedQuoteIndex ?? hoverIndex ?? latest
      : hoverIndex ?? latest;
  return Math.max(0, Math.min(latest, Number.isInteger(requested) ? requested : latest));
}

function resolveDataPrecision(instance) {
  const settings = instance.settings;
  if (["2", "4", "8"].includes(String(settings.dataPrecision))) {
    return Number(settings.dataPrecision);
  }
  const pricePrecision = Number.parseInt(resolveInstrumentPricePrecision(instance), 10);
  if (Number.isInteger(pricePrecision) && pricePrecision >= 0) {
    return pricePrecision;
  }
  return 2;
}

function resolveInstrumentPricePrecision(instance) {
  const requested = String(instance.settings.pricePrecision || "auto");
  if (requested !== "auto") return requested;
  const tickSize = Number(instrumentForInstance(instance)?.optionContract?.tickSize);
  if (!Number.isFinite(tickSize) || tickSize <= 0) return "auto";
  return String(Math.min(8, Math.max(0, Math.ceil(-Math.log10(tickSize)))));
}

function formatDataNumber(value, precision) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(precision) : "—";
}

function updatePriceChange(instance, close, previousClose, precision, detailed) {
  const change = close - previousClose;
  const percent = previousClose === 0 ? 0 : (change / previousClose) * 100;
  const sign = change > 0 ? "+" : "";
  const label = detailed ? "Change " : "";
  instance.priceChange.textContent =
    `${label}${sign}${formatDataNumber(change, precision)} (${sign}${percent.toFixed(2)}%)`;
  instance.priceChange.classList.toggle("is-positive", change > 0);
  instance.priceChange.classList.toggle("is-negative", change < 0);
}

function updateMarketStatusIcon(element, timestamp) {
  const state = regularMarketSessionState(timestamp);
  const label = state === "open" ? "Market open" : "Market closed";
  setStatusIcon(element, state, label);
}

function updateDelayStatusIcon(element, timestamp, dataAvailability = "") {
  const state = String(dataAvailability).toLocaleLowerCase() === "delayed"
    ? "delayed"
    : delayedDataState(timestamp);
  const label = state === "live" ? "Real-time data" : "Delayed data";
  setStatusIcon(element, state, label);
}

function loadedBarsLabel(instance) {
  const instrument = instrumentForInstance(instance);
  const source = instrument?.securityType === "Option"
    ? "development option bars"
    : "historical bars";
  return `${instance.data.length.toLocaleString()} ${source}`;
}

function setStatusIcon(element, state, label) {
  element.dataset.statusState = state;
  element.setAttribute("aria-label", label);
  element.setAttribute("title", label);
}

function regularMarketSessionState(timestamp) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(new Date(timestamp)).map(({ type, value }) => [type, value])
  );
  const minutes = Number(parts.hour) * 60 + Number(parts.minute);
  const weekday = !["Sat", "Sun"].includes(parts.weekday);
  return weekday && minutes >= 570 && minutes < 960 ? "open" : "closed";
}

function delayedDataState(timestamp) {
  const delay = Date.now() - Number(timestamp);
  return Number.isFinite(delay) && delay <= 20 * 60 * 1000 ? "live" : "delayed";
}

function formatVolumeValue(value, detailed) {
  const volume = Number(value);
  const label = detailed ? "Volume" : "V";
  if (!Number.isFinite(volume) || volume <= 0) return `${label} —`;
  return `${label} ${new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(volume)}`;
}

function formatIndicatorValue(value, precision = 2) {
  return Number.isFinite(value) ? Number(value).toFixed(precision) : "—";
}

function showAllStates(name, message = "") {
  chartInstances.forEach((instance) => showInstanceState(instance, name, message));
}

function showInstanceState(instance, name, message = "") {
  instance.element.querySelectorAll("[data-state]").forEach((element) => {
    element.hidden = element.getAttribute("data-state") !== name;
    const messageNode = element.querySelector("[data-state-message]");
    if (messageNode && message) messageNode.textContent = message;
  });
  if (message) instance.liveRegion.textContent = message;
}

function announce(message) {
  if (activeChart) activeChart.liveRegion.textContent = message;
}

function handleInitializationError(error) {
  const message = error instanceof Error ? error.message : "The chart application could not initialize.";
  showAllStates("error", message);
  messenger.error("initialization-failed", message, true);
}

function startDependencyHealthMonitoring() {
  refreshDependencyHealth();
  dependencyHealthTimer = window.setInterval(() => {
    if (document.visibilityState === "visible") refreshDependencyHealth();
  }, 30_000);
}

async function refreshDependencyHealth() {
  dependencyHealthController?.abort();
  const controller = new AbortController();
  dependencyHealthController = controller;
  try {
    const response = await fetch(dependencyHealthEndpoint, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal
    });
    if (!response.ok) throw new Error(`Dependency health returned ${response.status}.`);
    const health = await response.json();
    setDependencyStatus("symbol", health.symbolServiceUp === true);
    setDependencyStatus("fundamental", health.fundamentalServiceUp === true);
  } catch (error) {
    if (error?.name === "AbortError") return;
    setDependencyStatus("symbol", false);
    setDependencyStatus("fundamental", false);
  } finally {
    if (dependencyHealthController === controller) dependencyHealthController = null;
  }
}

function setDependencyStatus(service, isUp) {
  const element = dependencyStatusElements.get(service);
  if (!(element instanceof HTMLElement)) return;
  const serviceName = service === "symbol" ? "Symbol service" : "Fundamental service";
  const status = isUp ? "up" : "down";
  const label = isUp ? "Up" : "Down";
  element.dataset.status = status;
  element.setAttribute("aria-label", `${serviceName} is ${status}`);
  element.title = `${serviceName} is ${status}`;
  const labelElement = element.querySelector("[data-dependency-label]");
  if (labelElement) labelElement.textContent = label;
}

function handleLoadError(error, instance) {
  const status = error?.status;
  const code = status === 401 || status === 403
    ? "unauthorized-configuration"
    : status === 404
      ? "unsupported-market-data"
      : error?.message?.includes("worker")
        ? "worker-failed"
        : "data-request-failed";
  const message = error?.customerMessage || (code === "unauthorized-configuration"
    ? "This embed session is not authorized to load market data."
    : code === "unsupported-market-data"
      ? "The selected symbol or timeframe is not supported."
      : code === "worker-failed"
        ? "Market data could not be prepared. Retry the chart."
        : "Market data could not be loaded. Check the connection and retry.");
  showInstanceState(instance, "error", message);
  if (activeChart === instance) messenger.error(code, message, true);
}

function sendReady() {
  const performance = activeChart
    ? activeChart.lastPerformance || activeChart.workspace.getPerformanceSnapshot()
    : null;
  messenger.ready(createChartReadyPayload({
    activeChart,
    defaultSymbol,
    defaultSymbolId,
    defaultTimeframe,
    theme: currentTheme(),
    rendererType: performance?.rendererType || null,
    maximumChartWindows
  }));
}

function currentTheme() {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

function roundPerformance(performance) {
  return {
    averageFps: round(performance.averageFps),
    averageFrameTime: round(performance.averageFrameTime),
    maximumFrameTime: round(performance.maximumFrameTime),
    visibleBarCount: Math.round(performance.visibleBarCount),
    loadedBarCount: Math.round(performance.loadedBarCount),
    canvasWidth: Math.round(performance.canvasWidth),
    canvasHeight: Math.round(performance.canvasHeight),
    rendererType: performance.rendererType,
    dataFetchDuration: round(performance.dataFetchDuration),
    workerDuration: round(performance.workerDuration),
    dateAxisTickCount: Math.round(performance.dateAxisTickCount || 0),
    dateAxisLabelCount: Math.round(performance.dateAxisLabelCount || 0),
    dateAxisInterval: performance.dateAxisInterval || null
  };
}

function requiredElement(selector, scope = root) {
  const element = scope.querySelector(selector);
  if (!(element instanceof HTMLElement)) throw new Error(`Required chart element ${selector} was not found.`);
  return element;
}

async function loadServerWorkspace() {
  if (!workspaceEndpoint || !workspacePersistenceEnabled) {
    root.dataset.workspacePersistence = "disabled-by-capability";
    return;
  }
  try {
    const response = await fetch(workspaceEndpoint, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (response.status === 204) return;
    if (!response.ok) {
      throw await workspacePolicyError(
        response,
        "Saved workspace could not be loaded."
      );
    }
    restoredWorkspace = await response.json();
    workspaceRevision = Number(restoredWorkspace?.revision) || 0;
  } catch (error) {
    root.dataset.workspacePersistence = "load-failed";
    announce(error?.message || "Saved workspace could not be loaded.");
  }
}

async function loadAuthoritativeChartBootstrap() {
  try {
    const bootstrap = await loadChartBootstrap({
      fetch
    });
    if (!bootstrap) {
      throw new Error("A versioned chart bootstrap is required for this chart launch.");
    }
    effectiveCustomization = bootstrap.customization;
    const configuredDefaults = effectiveCustomization.chartDefaults;
    defaultSymbol = configuredDefaults.symbol;
    defaultSymbolId = configuredDefaults.symbolId || null;
    defaultTimeframe = normalizeTimeframe(configuredDefaults.timeframe);
    defaultTheme = resolveConfiguredThemeMode(configuredDefaults.themeMode);
    defaultChartSettingsByTheme = Object.freeze(Object.fromEntries(
      ["light", "dark"].map((mode) => [mode, Object.freeze({
        ...platformChartSettings,
        ...paletteChartSettings(effectiveCustomization, mode)
      })])
    ));
    defaultChartSettings = defaultChartSettingsByTheme[defaultTheme];
    configuredDefaultIndicatorsByTheme = Object.freeze(Object.fromEntries(
      ["light", "dark"].map((mode) => [
        mode,
        configuredIndicatorsForMode(effectiveCustomization, mode)
      ])
    ));
    configuredDefaultIndicators = configuredDefaultIndicatorsByTheme[defaultTheme];
    workspaceSymbol = defaultSymbol;
    workspaceEndpoint = bootstrap.endpoints.workspace || null;
    templateEndpoint = bootstrap.endpoints.templates || null;
    const marketDataEndpoints = productMarketDataEndpoints(
      bootstrap.endpoints.productApi
    );
    dataSource.setEndpoint(marketDataEndpoints.history);
    symbolSearch.setOptionEndpoint(marketDataEndpoints.options);
    realtimeGrantClient?.setEndpoint(marketDataEndpoints.realtimeGrant);

    const policy = resolveChartCapabilityPolicy(
      bootstrap.capabilities,
      DEFAULT_MAX_CHART_WINDOWS
    );
    maximumChartWindows = policy.maximumChartWindows;
    maximumIndicatorsPerChart = policy.maximumIndicatorsPerChart;
    maximumWatchlistItems = policy.maximumWatchlistItems;
    maximumActiveAlerts = policy.maximumActiveAlerts;
    maximumWorkspaces = policy.maximumWorkspaces;
    availableIndicatorIds = policy.availableIndicatorIds;
    availableDrawingToolIds = policy.availableDrawingToolIds;
    configuredDefaultIndicators = permittedIndicators(configuredDefaultIndicators);
    configuredInitialChartSeeds = initialChartSeeds(
      effectiveCustomization,
      maximumChartWindows,
      defaultTheme
    ).map((seed) => ({
      ...seed,
      indicators: permittedIndicators(seed.indicators)
    }));
    configuredInitialLayout = effectiveCustomization.initialWorkspace.layoutPreset;
    configuredActiveChartIndex = effectiveCustomization.initialWorkspace.activeChartIndex;
    workspacePersistenceEnabled = Boolean(workspaceEndpoint) && maximumWorkspaces > 0;

    effectiveThemePalette = applyEffectiveTheme(
      document,
      effectiveCustomization,
      defaultTheme
    );
    applyBranding(document, effectiveCustomization.branding);
    applyCustomizationPresentation(document, effectiveCustomization);
    // Keep calendar launchers after Object Tree when customization reorders commands.
    objectTreeButton.after(upcomingEarningsButton, upcomingSplitsButton);

    productStorageScope = chartProductStorageScope(bootstrap);
    workspacePreferencesStorageKey = scopedStorageKey(
      WORKSPACE_PREFERENCES_STORAGE_PREFIX,
      productStorageScope
    );
    workspacePreferences = readWorkspacePreferences();
    if (snapWindowsControl) snapWindowsControl.checked = workspacePreferences.snapWindows;
    setSurfaceWindowSnapping(workspaceSurface, workspacePreferences.snapWindows);
    objectTree.setStorageKey(scopedStorageKey(
      "flexcharting.object-tree.v1",
      productStorageScope
    ));
    upcomingEarnings?.setStorageKey(scopedStorageKey(
      "flexcharting.upcoming-earnings.v1",
      productStorageScope
    ));
    upcomingSplits?.setStorageKey(scopedStorageKey(
      "flexcharting.upcoming-splits.v1",
      productStorageScope
    ));
    indicatorTemplateRepository.endpoint = templateEndpoint;
    indicatorTemplateRepository.setStorageScope(productStorageScope);
    chartTemplateRepository.endpoint = templateEndpoint;
    chartTemplateRepository.setStorageScope(productStorageScope);
    watchlistManager.configureProductPolicy({
      endpoint: `${bootstrap.endpoints.productApi}/watchlists`,
      maximumItemsPerWatchlist: maximumWatchlistItems
    });
    alertWorkspace.setProductPolicy({
      canCreateAlerts: policy.canCreateAlerts,
      maximumActiveAlerts
    });
    alertWorkspace.configureProductContext({
      productApiEndpoint: bootstrap.endpoints.productApi,
      alertHubEndpoint: bootstrap.endpoints.alertHub,
      chartResourcesEndpoint: bootstrap.endpoints.chartResources,
      storageScope: productStorageScope
    });
    if (realtimeConnectionLease) {
      realtimeConnectionLease.name = `flexcharting-quotemedia-${productStorageScope}`;
    }
    if (featureIsVisible(effectiveCustomization, "clock")) {
      toolbarClock ||= initializeOptionalFeature(
        "Toolbar clock",
        () => new ToolbarClock({
          element: toolbarClockElement,
          store: new ClockPreferenceStore(
            undefined,
            scopedStorageKey("flexcharting.toolbar-clock.v1", productStorageScope)
          )
        })
      );
    }
    root.dataset.chartResourcesEndpoint = bootstrap.endpoints.chartResources;
    indicatorState.sma20 = configuredDefaultIndicators.some(
      (indicator) => indicator.definitionId === "sma"
    );
    indicatorState.ema50 = configuredDefaultIndicators.some(
      (indicator) => indicator.definitionId === "ema"
    );
    indicatorState.volume = configuredDefaultIndicators.some(
      (indicator) => indicator.definitionId === "volume"
    );
    if (availableIndicatorIds) {
      indicatorState.sma20 = indicatorState.sma20 && availableIndicatorIds.has("sma");
      indicatorState.ema50 = indicatorState.ema50 && availableIndicatorIds.has("ema");
      indicatorState.volume = indicatorState.volume && availableIndicatorIds.has("volume");
    }
    if (availableDrawingToolIds) {
      for (const button of lineToolButtons) {
        if (!button.dataset.drawingToolId) continue;
        const allowed = availableDrawingToolIds.has(button.dataset.drawingToolId);
        button.disabled = !allowed;
        button.hidden = !allowed;
      }
    }
    root.dataset.chartId = bootstrap.chart.resourceId;
    root.dataset.bootstrapSchemaVersion = String(bootstrap.schemaVersion);
    root.dataset.bootstrapContext = bootstrap.context;
    root.dataset.activeTenant = bootstrap.tenant.slug;
    root.dataset.effectiveOpenCharts = String(maximumChartWindows);
    root.dataset.effectiveIndicatorsPerChart = String(maximumIndicatorsPerChart);
    root.dataset.effectiveWatchlistItems = String(maximumWatchlistItems);
    root.dataset.effectiveActiveAlerts = String(maximumActiveAlerts);
    root.dataset.effectiveWorkspaces = String(maximumWorkspaces);
    root.dataset.activeProduct = bootstrap.product.slug;
    root.dataset.customizationSchemaVersion = String(effectiveCustomization.schemaVersion);
    root.dataset.customizationHash = effectiveCustomization.effectiveHash;
    root.dataset.customizationEducatorRevision = String(
      effectiveCustomization.educatorRevision || ""
    );
    root.dataset.customizationProductRevision = String(
      effectiveCustomization.productRevision || ""
    );
    if (!workspacePersistenceEnabled) {
      root.dataset.workspacePersistence = "disabled-by-capability";
    }
  } catch (error) {
    root.dataset.workspacePersistence = "bootstrap-failed";
    throw error;
  }
}

function permittedIndicators(indicators) {
  return permittedIndicatorInstances(indicators, indicatorCapabilityPolicy());
}

function presentationFeatureEnabled(key) {
  return !effectiveCustomization || featureIsVisible(effectiveCustomization, key);
}

function configuredIndicatorsForMode(customization, mode) {
  let indicators = customizationIndicatorInstances(
    customization.defaultIndicators,
    customization.themes[mode].indicatorPalette
  );
  const hasVolume = indicators.some((indicator) => indicator.definitionId === "volume");
  if (customization.chartDefaults.volumeVisible && !hasVolume) {
    indicators = [
      ...indicators,
      createIndicatorInstance("volume", {
        id: "configured-volume",
        paneId: "pane-2",
        styles: {
          volume: { color: customization.themes[mode].volumeUp.slice(0, 7) }
        }
      })
    ];
  } else if (!customization.chartDefaults.volumeVisible && hasVolume) {
    indicators = indicators.filter((indicator) => indicator.definitionId !== "volume");
  }
  return indicators;
}

function migrateConfiguredIndicatorColors(instance, previousDefaults, nextDefaults) {
  if (!instance?.indicatorInstances?.length) return;
  instance.indicatorInstances = instance.indicatorInstances.map((indicator) => {
    const previous = previousDefaults.find(
      (candidate) => candidate.definitionId === indicator.definitionId
    );
    const next = nextDefaults.find(
      (candidate) => candidate.definitionId === indicator.definitionId
    );
    if (!previous || !next) return indicator;
    const styles = Object.fromEntries(Object.entries(indicator.styles || {}).map(([key, style]) => {
      const previousColor = previous.styles?.[key]?.color;
      const nextColor = next.styles?.[key]?.color;
      return [key, previousColor && nextColor && style?.color === previousColor
        ? { ...style, color: nextColor }
        : style];
    }));
    return { ...indicator, styles };
  });
  instance.workspace.setIndicatorInstances(instance.indicatorInstances, { stylesOnly: true });
}

function indicatorCapabilityPolicy() {
  return {
    maximumIndicatorsPerChart,
    availableIndicatorIds
  };
}

async function restoreServerWorkspace() {
  if (!workspacePersistenceEnabled) return;
  const snapshot = restoredWorkspace?.snapshot;
  if (!snapshot || snapshot.schemaVersion !== WORKSPACE_SCHEMA_VERSION) return;
  const windows = restorableChartWindows(snapshot.windows, maximumChartWindows);
  if (maximumChartWindows === 0) {
    root.dataset.workspacePersistence = "not-applicable";
  }
  if (!windows.length) return;

  const effectiveWindowLimit = maximumChartWindows;
  maximumChartWindows = Math.max(effectiveWindowLimit, windows.length);
  workspaceRestoring = true;
  try {
    while (chartInstances.length < windows.length) {
      const saved = windows[chartInstances.length];
      createChartInstance(
        chartInstances.length === 0 ? initialChartWindow : null,
        saved.id
      );
    }
    for (let index = 0; index < windows.length; index += 1) {
      const saved = windows[index];
      const instance = chartInstances[index];
      const instrument = await resolveSavedInstrument(saved);
      if (instrument) assignInstrument(instance, instrument);
      instance.timeframe = normalizeTimeframe(saved.timeframe, defaultTimeframe);
      instance.symbolLocked = saved.symbolLocked === true;
      instance.restoredViewport = saved.viewport || null;
      if (Number.isFinite(saved.stackOrder) && saved.stackOrder > 0) {
        instance.stackOrder = saved.stackOrder;
        stackSequence = Math.max(stackSequence, saved.stackOrder);
        instance.element.style.zIndex = String(saved.stackOrder);
      }
      if (saved.settings && typeof saved.settings === "object") {
        instance.settings = { ...defaultChartSettings, ...saved.settings };
      }
      if (Array.isArray(saved.indicators)) {
        instance.indicatorInstances = permittedIndicators(saved.indicators);
      }
      if (saved.drawings && typeof saved.drawings === "object") {
        instance.workspace.restoreDrawingState(saved.drawings);
      }
      instance.workspace.setSymbolAndTimeframe(instance.symbol, instance.timeframe);
      instance.workspace.setIndicatorInstances(instance.indicatorInstances, { stylesOnly: true });
      instance.workspace.setIndicatorPaneHeights(saved.indicatorPaneHeights);
      if (validSavedGeometry(saved.geometry)) {
        instance.floatingWindow.applyGeometry(saved.geometry);
      }
      if (saved.windowState === "minimized") instance.floatingWindow.minimize();
      if (saved.windowState === "maximized") instance.floatingWindow.maximize();
      // Saved desktop geometry may arrive after the surface resize observer ran.
      // Fit it now so chart controls remain reachable in a smaller iframe.
      instance.floatingWindow.constrainToSurface();
      updateInstanceTimeframe(instance);
      updateInstanceLockState(instance);
      updateSymbolSummary(instance);
      updateInstanceIndicators(instance);
      applyInstanceSettings(instance);
    }

    const activeIndex = windows.findIndex(
      (saved) => saved.id === snapshot.lastActiveWindowId
    );
    setActiveChart(
      chartInstances[activeIndex >= 0 ? activeIndex : 0],
      { raise: false, persist: false }
    );
    const activeInstrument = instrumentForCode(activeChart.symbol);
    if (activeInstrument) setWorkspaceInstrument(activeInstrument);
    if (snapshot.preferences?.theme) applyTheme(snapshot.preferences.theme, false);
    if (typeof snapshot.preferences?.snapWindows === "boolean") {
      updateSnapWindowsPreference(snapshot.preferences.snapWindows);
    }
    if (presentationFeatureEnabled("objectTree")) {
      objectTree.restoreState(snapshot.preferences?.objectTree);
    }
    if (presentationFeatureEnabled("alerts")) {
      alertWorkspace.restoreManagerState(snapshot.preferences?.alertManager);
    }
    if (validSavedGeometry(snapshot.preferences?.indicatorManagerGeometry)) {
      indicatorManagerFloatingWindow.applyGeometry(
        snapshot.preferences.indicatorManagerGeometry
      );
    }
    if (validSavedGeometry(snapshot.preferences?.settingsWindowGeometry)) {
      settingsFloatingWindow.applyGeometry(snapshot.preferences.settingsWindowGeometry);
      settingsPositioned = true;
    }
    root.dataset.workspacePersistence = "restored";
  } finally {
    maximumChartWindows = effectiveWindowLimit;
    updateWindowControls();
    workspaceRestoring = false;
  }
}

async function resolveSavedInstrument(saved) {
  const local = saved.instrumentId
    ? symbolCatalogById.get(saved.instrumentId)
    : instrumentForCode(saved.symbol);
  if (local) return local;
  const identifier = saved.symbolId || saved.instrumentId;
  if (!identifier) return null;
  try {
    const response = await fetch(`/api/v1/symbols/${encodeURIComponent(identifier)}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (!response.ok) return null;
    const instrument = normalizeInstrument(await response.json());
    if (instrument) registerInstrument(instrument);
    return instrument;
  } catch {
    return null;
  }
}

function scheduleWorkspaceSave() {
  if (!workspaceEndpoint || !workspacePersistenceEnabled || !initialized || workspaceRestoring || !optionalWorkspaceHydrated) {
    return;
  }
  if (!hasPersistableChartWindows(chartInstances)) {
    clearTimeout(workspaceSaveTimer);
    workspaceSaveTimer = null;
    workspaceDirty = false;
    root.dataset.workspacePersistence = "not-applicable";
    return;
  }
  workspaceDirty = true;
  root.dataset.workspacePersistence = "pending";
  clearTimeout(workspaceSaveTimer);
  workspaceSaveTimer = setTimeout(() => {
    workspaceSaveTimer = null;
    void saveServerWorkspace();
  }, WORKSPACE_SAVE_DEBOUNCE_MS);
}

async function saveServerWorkspace({ keepalive = false } = {}) {
  if (
    !workspaceEndpoint
    || !workspacePersistenceEnabled
    || !workspaceDirty
    || workspaceRestoring
    || !optionalWorkspaceHydrated
  ) return;
  if (!hasPersistableChartWindows(chartInstances)) {
    workspaceDirty = false;
    root.dataset.workspacePersistence = "not-applicable";
    return;
  }
  workspaceDirty = false;
  root.dataset.workspacePersistence = "saving";
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (!csrfToken) {
    workspaceDirty = true;
    return;
  }
  try {
    const request = {
      expectedRevision: workspaceRevision,
      schemaVersion: WORKSPACE_SCHEMA_VERSION,
      snapshot: captureServerWorkspace()
    };
    const response = await fetch(workspaceEndpoint, {
      method: "PUT",
      credentials: "same-origin",
      keepalive,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken
      },
      body: JSON.stringify(request)
    });
    if (response.status === 409) {
      const conflict = await response.json();
      workspaceRevision = Number(conflict?.currentRevision) || workspaceRevision;
      workspaceDirty = true;
      root.dataset.workspacePersistence = "conflict";
      announce("This workspace changed in another session. Your newer local changes were not overwritten.");
      return;
    }
    if (!response.ok) {
      throw await workspacePolicyError(
        response,
        "Workspace changes could not be saved."
      );
    }
    const saved = await response.json();
    workspaceRevision = Number(saved.revision) || workspaceRevision;
    root.dataset.workspacePersistence = "saved";
  } catch (error) {
    workspaceDirty = true;
    root.dataset.workspacePersistence = "save-failed";
    if (!keepalive) announce(error?.message || "Workspace changes could not be saved.");
  }
}

function captureServerWorkspace() {
  const windows = chartInstances
    .filter((instance) => !instance.destroyed)
    .map((instance) => ({
      id: instance.id,
      symbol: instance.symbol,
      symbolId: instance.symbolId || null,
      instrumentId: instance.instrumentId || null,
      timeframe: instance.timeframe,
      geometry: instance.floatingWindow.snapshot(),
      windowState: instance.floatingWindow.state,
      stackOrder: instance.stackOrder,
      symbolLocked: instance.symbolLocked === true,
      indicators: serializeIndicatorInstancesForWorkspace(instance.indicatorInstances),
      drawings: instance.workspace.serializeDrawings(),
      settings: instance.settings,
      viewport: captureViewportAnchor(instance),
      indicatorPaneHeights: instance.workspace.getIndicatorPaneHeights()
    }));
  return {
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    windows,
    watchlists: { authority: "watchlist-api" },
    preferences: {
      snapWindows: surfaceWindowSnappingEnabled(workspaceSurface),
      theme: currentTheme(),
      objectTree: objectTree.snapshotState(),
      upcomingEarnings: upcomingEarnings?.snapshotState() || null,
      upcomingSplits: upcomingSplits?.snapshotState() || null,
      alertManager: alertWorkspace.snapshotManagerState(),
      indicatorManagerGeometry: validSavedGeometry(
        indicatorManagerFloatingWindow.snapshot()
      )
        ? indicatorManagerFloatingWindow.snapshot()
        : null,
      settingsWindowGeometry: settingsPositioned
        && validSavedGeometry(settingsFloatingWindow.snapshot())
          ? settingsFloatingWindow.snapshot()
          : null
    },
    lastActiveWindowId: activeChart?.id || windows[0]?.id || null
  };
}

function validSavedGeometry(geometry) {
  return geometry
    && Number.isFinite(geometry.x)
    && Number.isFinite(geometry.y)
    && Number.isFinite(geometry.width)
    && Number.isFinite(geometry.height)
    && geometry.width >= 160
    && geometry.height >= 120;
}

function readWorkspacePreferences() {
  if (!workspacePreferencesStorageKey) return { snapWindows: true };
  try {
    const saved = JSON.parse(
      globalThis.localStorage?.getItem(workspacePreferencesStorageKey) || "{}"
    );
    return {
      snapWindows: saved.snapWindows !== false
    };
  } catch {
    return { snapWindows: true };
  }
}

function updateSnapWindowsPreference(enabled) {
  const snapWindows = enabled !== false;
  if (snapWindowsControl) snapWindowsControl.checked = snapWindows;
  setSurfaceWindowSnapping(workspaceSurface, snapWindows);
  workspacePreferences.snapWindows = snapWindows;
  try {
    if (workspacePreferencesStorageKey) {
      globalThis.localStorage?.setItem(
        workspacePreferencesStorageKey,
        JSON.stringify(workspacePreferences)
      );
    }
  } catch {
    // Snapping still works for this session when browser storage is unavailable.
  }
  scheduleWorkspaceSave();
}

function normalizeTimeframe(timeframe, fallback = "15m") {
  return normalizeChartTimeframe(timeframe)
    || normalizeChartTimeframe(fallback)
    || "15m";
}

function chartProductStorageScope(bootstrap) {
  return [
    `v${bootstrap.schemaVersion}`,
    bootstrap.tenant.id,
    bootstrap.product.id,
    bootstrap.chart.resourceId
  ].join(":").toLowerCase();
}

async function workspacePolicyError(response, fallback) {
  let problem = null;
  try {
    problem = await response.json();
  } catch {
    // Non-JSON transport failures use the stable fallback below.
  }
  const code = String(problem?.code || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll("_", "-")
    .toLowerCase();
  const messages = {
    "capability-disabled": "This product does not include saved workspaces.",
    "quota-exceeded": "You have reached the saved workspace limit for this product.",
    "resource-product-mismatch": "This workspace belongs to another product.",
    "enrollment-expired": "Your product access has expired.",
    "product-inactive": "This product is not currently available.",
    "product-access-denied": "You no longer have access to workspaces for this product.",
    "access-invalid": "You no longer have access to workspaces for this product."
  };
  const error = new Error(messages[code] || fallback);
  error.code = code || null;
  error.status = response.status;
  return error;
}

function scopedStorageKey(prefix, scope) {
  return `${prefix}.${encodeURIComponent(scope)}`;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum));
}

function round(value) {
  return Math.round((Number.isFinite(value) ? value : 0) * 10) / 10;
}

function chartObjectTreeNodes() {
  return chartInstances
    .filter((instance) => !instance.destroyed)
    .map((instance, index) => {
      const paneIds = ["main"];
      for (const indicator of instance.indicatorInstances) {
        const paneId = indicator.paneId || "main";
        if (!paneIds.includes(paneId)) paneIds.push(paneId);
      }
      for (const drawing of instance.drawings) {
        const paneId = drawing.paneId || "main";
        if (!paneIds.includes(paneId)) paneIds.push(paneId);
      }
      const indicators = instance.indicatorInstances.map((indicator) => ({
        id: `indicator:${instance.id}:${indicator.id}`,
        kind: "indicator",
        paneId: indicator.paneId || "main",
        label: indicator.displayName || indicator.definitionId.toUpperCase(),
        description: inputSummary(indicator),
        hidden: indicator.visible === false,
        status: indicator.error ? "Error" : "",
        activate: () => activateChartObject(instance.id, "indicator", indicator.id),
        settings: () => openIndicatorSettingsFromTree(instance.id, indicator.id),
        delete: () => deleteIndicatorFromTree(instance.id, indicator.id)
      }));
      const selectedDrawingId = instance.workspace.getSelectedDrawing()?.id || null;
      const drawings = instance.drawings
        .map((drawing, originalIndex) => ({ drawing, originalIndex }))
        .sort((left, right) =>
          Number(right.drawing.zOrder || 0) - Number(left.drawing.zOrder || 0)
        )
        .map(({ drawing, originalIndex }) => ({
        id: `drawing:${instance.id}:${drawing.id}`,
        kind: "drawing",
        paneId: drawing.paneId || "main",
        label: drawing.metadata?.name
          || `${getDrawingTool(drawing.type)?.label || "Drawing"} ${originalIndex + 1}`,
        description: paneLabel(drawing.paneId, instance.symbol),
        selected: drawing.id === selectedDrawingId,
        hidden: drawing.visible === false,
        locked: drawing.locked === true,
        activate: () => activateChartObject(instance.id, "drawing", drawing.id),
        settings: () => openDrawingSettingsFromTree(instance.id, drawing.id),
        delete: drawing.locked
          ? null
          : () => deleteDrawingFromTree(instance.id, drawing.id),
        deleteDisabledReason: drawing.locked
          ? "Unlock this drawing before deleting it."
          : ""
      }));
      const panes = paneIds.map((paneId) => {
        const paneIndicators = indicators
          .filter((indicator) => indicator.paneId === paneId)
          .map(({ paneId: _paneId, ...indicator }) => indicator);
        const paneDrawings = drawings
          .filter((drawing) => drawing.paneId === paneId)
          .map(({ paneId: _paneId, ...drawing }) => drawing);
        return {
          id: `pane:${instance.id}:${paneId}`,
          kind: "pane",
          label: paneLabel(paneId, instance.symbol),
          description: paneId === "main" ? "Price pane" : "Indicator pane",
          settingsDisabledReason: "This pane is configured through its indicators.",
          activate: () => activateChartObject(instance.id, "pane", paneId),
          children: [
            objectTreeGroup(
              `${instance.id}:pane:${paneId}`,
              "indicators",
              "Indicators",
              paneIndicators
            ),
            objectTreeGroup(
              `${instance.id}:pane:${paneId}`,
              "drawings",
              "Drawings",
              paneDrawings
            )
          ]
        };
      });
      const alerts = presentationFeatureEnabled("alerts")
        ? alertWorkspace.objectTreeAlertsForChart(instance).map((alert) => ({
            id: `alert:${instance.id}:${alert.id}`,
            kind: "alert",
            label: alert.label,
            description: alert.description,
            status: alert.status,
            paused: alert.paused,
            activate: () => activateChartObject(instance.id, "alert", alert.id),
            settings: () => alertWorkspace.editAlertById(alert.id),
            delete: () => alertWorkspace.deleteAlertById(alert.id)
          }))
        : [];
      return {
        id: `chart:${instance.id}`,
        kind: "chart",
        label: `${instance.symbol} · ${instance.timeframe}`,
        description: `Chart ${index + 1}`,
        status: capitalize(instance.floatingWindow?.state || "floating"),
        stackOrder: instance.stackOrder,
        active: instance.element.classList.contains("is-active"),
        activate: () => activateChartObject(instance.id),
        settings: () => openChartSettingsFromTree(instance.id),
        delete: () => destroyChartFromTree(instance.id),
        children: [
          objectTreeGroup(instance.id, "panes", "Panes", panes),
          ...(presentationFeatureEnabled("alerts")
            ? [objectTreeGroup(instance.id, "alerts", "Alerts", alerts)]
            : [])
        ]
      };
    });
}

function objectTreeGroup(chartId, id, label, children) {
  return {
    id: `group:${chartId}:${id}`,
    kind: "group",
    label,
    description: String(children.length),
    settingsDisabledReason: "Open settings on an individual object in this group.",
    children
  };
}

function watchlistObjectTreeNodes() {
  if (!presentationFeatureEnabled("watchlists")) return [];
  return watchlistManager.objectTreePanels().map((panel) => ({
    id: `watchlist:${panel.id}`,
    kind: "watchlist",
    label: panel.label,
    description: panel.description,
    status: panel.status,
    stackOrder: panel.stackOrder,
    active: panel.active,
    activate: () => watchlistManager.focusPanelById(panel.id),
    settings: () => watchlistManager.openSettingsById(panel.id),
    delete: () => watchlistManager.deleteWatchlistByPanelId(panel.id)
  }));
}

function alertManagerObjectTreeNodes() {
  if (!presentationFeatureEnabled("alerts")) return [];
  const manager = alertWorkspace.objectTreeManager();
  if (!manager) return [];
  return [{
    id: `utility:${manager.id}`,
    kind: "utility",
    label: manager.label,
    description: manager.description,
    stackOrder: manager.stackOrder,
    active: manager.active,
    activate: () => alertWorkspace.openManager()
  }];
}

function activateChartObject(chartId, kind = "chart", objectId = null) {
  if (kind === "alert" && !presentationFeatureEnabled("alerts")) return false;
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance) return false;
  if (instance.floatingWindow.state === "minimized") {
    setWindowState(instance, "floating");
  }
  setActiveChart(instance);
  clearObjectTreeVisualSelection();
  instance.element.dataset.objectTreeSelection = kind;
  if (kind === "drawing") {
    instance.workspace.selectDrawing(objectId);
  } else {
    instance.workspace.clearDrawingSelection();
  }
  if (kind === "indicator") {
    for (const row of instance.element.querySelectorAll("[data-indicator-legend-instance]")) {
      row.classList.toggle(
        "is-object-tree-selected",
        row.dataset.indicatorLegendInstance === objectId
      );
    }
  }
  if (kind === "pane") {
    instance.element.dataset.objectTreePane = objectId;
    instance.paneIndicatorReadouts.get(objectId)?.classList.add("is-object-tree-selected");
  }
  if (kind === "alert") {
    alertWorkspace.activateAlertById(objectId);
  } else {
    instance.overlayCanvas.focus({ preventScroll: true });
  }
  workspaceObjectRegistry.notify();
  return true;
}

function clearObjectTreeVisualSelection() {
  for (const chart of chartInstances) {
    delete chart.element.dataset.objectTreeSelection;
    delete chart.element.dataset.objectTreePane;
    for (const row of chart.element.querySelectorAll(".is-object-tree-selected")) {
      row.classList.remove("is-object-tree-selected");
    }
  }
}

function openChartSettingsFromTree(chartId) {
  if (!presentationFeatureEnabled("chartSettings")) return false;
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance) return false;
  activateChartObject(chartId);
  return openSettingsWindow(instance);
}

async function openIndicatorSettingsFromTree(chartId, indicatorId) {
  if (!presentationFeatureEnabled("indicatorManager")) return false;
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance?.indicatorInstances.some((indicator) => indicator.id === indicatorId)) return false;
  activateChartObject(chartId, "indicator", indicatorId);
  await openIndicatorManager(instance, objectTree.element);
  indicatorManager.openConfiguration(indicatorId);
  return true;
}

async function deleteIndicatorFromTree(chartId, indicatorId) {
  if (!presentationFeatureEnabled("indicatorManager")) return false;
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance?.indicatorInstances.some((indicator) => indicator.id === indicatorId)) return false;
  activateChartObject(chartId, "indicator", indicatorId);
  await openIndicatorManager(instance, objectTree.element);
  await indicatorManager.deleteInstance(indicatorId);
  return true;
}

function openDrawingSettingsFromTree(chartId, drawingId) {
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  const drawing = instance?.drawings.find((candidate) => candidate.id === drawingId);
  if (!instance || !drawing) return false;
  activateChartObject(chartId, "drawing", drawingId);
  const point = instance.workspace.getSelectedDrawingMenuPoint() || {
    x: Math.max(24, instance.stage.clientWidth / 2),
    y: Math.max(24, instance.stage.clientHeight / 2)
  };
  openDrawingContextMenu(instance, point, { drawing, keyboard: true });
  return true;
}

function deleteDrawingFromTree(chartId, drawingId) {
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance?.drawings.some((drawing) => drawing.id === drawingId)) return false;
  activateChartObject(chartId, "drawing", drawingId);
  instance.drawingContextTargetId = drawingId;
  return executeDrawingContextAction(instance, "delete");
}

function destroyChartFromTree(chartId) {
  const instance = chartInstances.find((chart) => chart.id === chartId && !chart.destroyed);
  if (!instance) return false;
  destroyChartInstance(instance);
  return true;
}

function notifyIfDrawingTreeChanged(instance) {
  const signature = instance.drawings.map((drawing) => [
    drawing.id,
    drawing.type,
    drawing.paneId,
    drawing.visible !== false,
    drawing.locked === true,
    drawing.metadata?.name || ""
  ].join(":")).join("|");
  if (signature === instance.objectTreeDrawingSignature) return;
  instance.objectTreeDrawingSignature = signature;
  workspaceObjectRegistry.notify();
}

function capitalize(value) {
  const text = String(value || "");
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "";
}

newChartButton.addEventListener("click", () => {
  try {
    createChartInstance();
    scheduleWorkspaceSave();
  } catch (error) {
    const message = error instanceof WebGlUnavailableError
      ? "This browser cannot open another WebGL chart window."
      : "A new chart window could not be created.";
    announce(message);
    messenger.error("chart-window-failed", message, true);
  }
});
symbolSearchTrigger.addEventListener("click", () => {
  if (!initialized || !activeChart) return;
  symbolSearch.open({
    query: workspaceSymbol,
    trigger: symbolSearchTrigger
  });
});
symbolSelect.addEventListener("change", () => {
  applySymbol(symbolSelect.value, true);
});
timeframeSelect.addEventListener("change", () => {
  applyTimeframe(timeframeSelect.value, true, activeChart);
});
themeToggle.addEventListener("click", () => applyTheme(currentTheme() === "dark" ? "light" : "dark", true));
resetButton.addEventListener("click", () => activeChart?.workspace.moveToLatest());
windowResetButton.addEventListener("click", resetWindowLayout);
for (const button of viewportActionButtons) {
  button.addEventListener("click", () => runViewportAction(button.dataset.viewportAction));
}
indicatorManagerButton.addEventListener("click", () => openIndicatorManager(activeChart));
fullscreenToggle.addEventListener("click", toggleFullscreen);
settingsCloseButton.addEventListener("pointerdown", (event) => event.stopPropagation());
settingsCloseButton.addEventListener("click", closeSettingsWindow);
settingsDoneButton.addEventListener("click", closeSettingsWindow);
settingsCancelButton.addEventListener("click", cancelSettingsWindow);
snapWindowsControl?.addEventListener("change", () => {
  updateSnapWindowsPreference(snapWindowsControl.checked);
  announce(`Snap windows ${snapWindowsControl.checked ? "enabled" : "disabled"}.`);
});
settingsWindow.addEventListener("pointerdown", () => {
  settingsWindow.classList.add("is-focused");
  settingsWindow.focus({ preventScroll: true });
}, { capture: true });
for (const control of settingsControls) {
  control.addEventListener("input", () => updateSettingFromControl(control));
  control.addEventListener("change", () => updateSettingFromControl(control));
}
settingsWindow.addEventListener("chart-color-opacity", (event) => {
  const control = event.target;
  const opacity = Number(event.detail?.opacity);
  if (
    !settingsTarget
    || !(control instanceof HTMLInputElement)
    || control.type !== "color"
    || !Number.isFinite(opacity)
  ) return;
  const setting = colorOpacitySetting(control);
  settingsTarget.settings[setting] = opacity;
  for (const peer of colorSettingsControls) {
    if (colorOpacitySetting(peer) !== setting) continue;
    peer.dataset.chartOpacity = String(opacity);
    syncChartColorPicker(peer);
  }
  applyInstanceSettings(settingsTarget);
  scheduleWorkspaceSave();
});
for (const tab of settingsTabs) {
  tab.addEventListener("click", () => selectSettingsTab(tab));
  tab.addEventListener("keydown", handleSettingsTabKeydown);
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden" && workspaceDirty) {
    clearTimeout(workspaceSaveTimer);
    workspaceSaveTimer = null;
    void saveServerWorkspace({ keepalive: true });
  }
});
settingsWindow.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    event.preventDefault();
    closeSettingsWindow();
  }
});
document.addEventListener("fullscreenchange", updateFullscreenButton);
document.addEventListener("keydown", (event) => {
  if (
    initialized
    && activeChart
    && (!effectiveCustomization || featureIsVisible(effectiveCustomization, "goToDate"))
    && isGoToDateShortcut(event)
  ) {
    event.preventDefault();
    if (!goToDateDialog?.open) openGoToDate(activeChart, activeChart.overlayCanvas);
    return;
  }
  const editableTarget = event.target instanceof HTMLElement
    && Boolean(event.target.closest("input, textarea, [contenteditable='true']"));
  if (
    !event.defaultPrevented
    && !editableTarget
    && (event.ctrlKey || event.metaKey)
    && !event.altKey
    && event.key.toLowerCase() === "z"
  ) {
    const handled = event.shiftKey
      ? drawingCommandHistory.redo()
      : drawingCommandHistory.undo();
    if (handled) event.preventDefault();
    return;
  }
  if (
    !event.defaultPrevented
    && !editableTarget
    && (event.ctrlKey || event.metaKey)
    && !event.altKey
    && event.key.toLowerCase() === "y"
  ) {
    if (drawingCommandHistory.redo()) event.preventDefault();
    return;
  }
  if (
    !initialized
    || !symbolSearchElement.hidden
    || !activeChart
    || event.defaultPrevented
    || event.altKey
    || event.ctrlKey
    || event.metaKey
    || event.key.length !== 1
    || !/^[a-z0-9.]$/i.test(event.key)
    || document.activeElement !== activeChart.overlayCanvas
  ) return;
  event.preventDefault();
  symbolSearch.open({
    query: event.key,
    trigger: activeChart.overlayCanvas,
    selectQuery: false
  });
});
document.addEventListener("pointerdown", (event) => {
  if (!(event.target instanceof Element) || !event.target.closest("[data-line-tools]")) {
    closeLineToolsMenu();
  }
  if (!settingsWindow.hidden &&
      event.target instanceof Node &&
      !settingsWindow.contains(event.target) &&
      !(event.target instanceof Element && event.target.closest("[data-chart-color-picker-popover]"))) {
    closeSettingsWindow();
  }
  if (!(event.target instanceof Element) || !event.target.closest("[data-window-interval]")) {
    closeOtherTimeframeMenus();
  }
  if (
    !(event.target instanceof Element)
    || !event.target.closest(
      "[data-chart-context-menu], [data-drawing-context-menu], [data-chart-color-picker-popover]"
    )
  ) {
    closeOtherContextMenus();
  }
});
window.addEventListener("resize", () => {
  closeLineToolsMenu();
  closeOtherContextMenus();
  fitSettingsWindowToContent();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") refreshDependencyHealth();
});
window.addEventListener("pagehide", () => {
  objectTree.dispose();
  upcomingEarnings?.dispose();
  upcomingSplits?.dispose();
  toolbarClock?.destroy();
  symbolSearch.destroy();
  watchlistManager.destroy();
  realtimeManager?.close("pagehide");
  if (mockRealtimeHarness) delete globalThis.__flexRealtimeTest;
  messenger.dispose();
  workerClient.dispose();
  indicatorManager.destroy();
  indicatorTemplateMenu?.destroy();
  chartTemplateMenu?.destroy();
  indicatorManagerFloatingWindow.dispose();
  settingsFloatingWindow.dispose();
  for (const dropdown of chartDropdowns) dropdown.destroy();
  for (const colorPicker of chartColorPickers) colorPicker.destroy();
  for (const instance of chartInstances) {
    instance.destroyed = true;
    releaseChartRealtime(instance);
    instance.loadToken += 1;
    instance.loadController?.abort();
    instance.loadController = null;
    instance.fundamentalController?.abort();
    instance.historyWindow?.reset();
    instance.fundamentalEventTooltip.dispose();
    instance.floatingWindow.dispose();
    instance.workspace.dispose();
  }
  dependencyHealthController?.abort();
  dependencyHealthController = null;
  if (dependencyHealthTimer !== null) window.clearInterval(dependencyHealthTimer);
  dependencyHealthTimer = null;
}, { once: true });

startDependencyHealthMonitoring();
initialize();
